// XZ3D encounter creation core — pure authoring/compiler functions, no DOM.
//
// The authoring loop is intentionally Dungeon Alchemist-like: the GM supplies
// coarse room rectangles and intent; the compiler supplies deterministic local
// topology skin and dressing. Shape-blob v1 remains the Janus/rules truth.

import {
  UNDERGROUND_ENVIRONMENTS, boxFaceSharedSpans, boxIsRoofed, environmentIsUnderground,
  openingFacingFromGeometry, validateShape,
} from './ft3d-shape-core.js';

import { normalizeArchitectureGeometry } from './mesh-architecture-geometry.js';
export { normalizeArchitectureGeometry };

// DAFT3D S26: one list, re-exported so authoring code needs only the creation core.
export { UNDERGROUND_ENVIRONMENTS, environmentIsUnderground };
import {
  hashString, makeTileSet, mulberry32, solveShapeSkin, validateSolution,
} from './ft3d-wfc-core.js';
// DAFT3D S2: the room-type catalog (theme → room type → grammar) is source-controlled JSON.
import ROOM_TYPE_CATALOG_SOURCE from './room-types/catalog-v1.json' with { type: 'json' };

export const CREATION_SCHEMA = 'ft.xz3d.encounter-creation.v1';
export const ZONE_GENERATION_SCHEMA = 'ft.xz3d.zone-generation.v1';
export const SCENE_TRACED_WALL_SCHEMA = 'ft.xz3d.scene-traced-walls.v1';
export const DERIVED_AUTHORING_ENVELOPE_PADDING_FT = 50;
export const DEFAULT_TRACED_WALL_ASSET_IDENTITY = Object.freeze({
  source: 'native-structural-primitive',
  assetId: 'xz3d:structural-wall-box:v1',
  meshId: null,
  surfaceId: null,
  registryRequired: false,
});
// ─── DAFT3D room-type catalog (S2, 2026-09-06) ─────────────────────────────────────────────
// Vocabulary: docs/DAFT3D_FAMILY_VOCABULARY.md. Ids are `<theme>-<room_type>` (one hyphen,
// snake_case halves). The seven legacy kebab room functions persist in encounter creation blobs
// and stay valid forever; they resolve to catalog entries through ROOM_TYPE_ALIASES.roomType.
// Zone tokens are canonical snake_case in the catalog and in profiles; the compiler's own
// dressingZone() literals stay kebab ('wall-band'), so every match boundary converts through
// compilerZoneToken(). Nothing persisted is rewritten by canonicalisation.
export const ROOM_TYPE_CATALOG_SCHEMA = 'ft.xz3d.room-type-catalog.v1';
const ROOM_TYPE_TOKEN_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const ROOM_TYPE_ID_RE = /^[a-z0-9_]+-[a-z0-9_]+$/;
const LEGACY_ROOM_FUNCTIONS = Object.freeze([
  'unassigned',
  'natural-chamber',
  'wet-grotto',
  'hatchery',
  'occupied-cave',
  'ruined-shrine',
  'connector',
]);
const CANONICAL_ZONES = Object.freeze(['corner', 'wall_band', 'center']);
const ZONE_ALIASES = Object.freeze({ 'wall-band': 'wall_band', corner: 'corner', center: 'center', wall_band: 'wall_band' });
const COMPILER_ZONE = Object.freeze({ wall_band: 'wall-band', corner: 'corner', center: 'center' });
const GRAMMAR_SECTIONS = Object.freeze(['anchors', 'wall_band', 'center', 'clutter', 'hazards']);
const SECTION_DEFAULT_ZONE = Object.freeze({ anchors: 'wall_band', wall_band: 'wall_band', center: 'center', clutter: 'center', hazards: 'center', lights: 'wall_band' });
const SECTION_CAPABILITY = Object.freeze({ anchors: 'objects', wall_band: 'objects', center: 'objects', clutter: 'objects', hazards: 'objects', lights: 'lights' });
const GRAMMAR_FACINGS = Object.freeze(['room', 'focus', 'along_wall', 'free']);
const ROOM_TYPE_LANES = Object.freeze(['inventory', 'placeable']);
const GENERATION_KEYS = Object.freeze(['loot', 'lights', 'walls', 'windows', 'curtains', 'doors', 'tiles', 'roofs', 'objects']);

/** Canonical zone token (snake_case) for any zone spelling; unknown tokens pass through. */
export function canonicalZoneToken(value) {
  const token = String(value ?? '').trim().toLowerCase();
  return Object.hasOwn(ZONE_ALIASES, token) ? ZONE_ALIASES[token] : token;
}

/** The compiler's own spelling for a zone (dressingZone() literals), for match boundaries only. */
export function compilerZoneToken(value) {
  const canonical = canonicalZoneToken(value);
  return Object.hasOwn(COMPILER_ZONE, canonical) ? COMPILER_ZONE[canonical] : canonical;
}

function roomTypeToken(value, label) {
  const token = String(value ?? '').trim();
  if (!ROOM_TYPE_TOKEN_RE.test(token)) throw new Error(`room-type catalog: ${label} must be a stable lowercase token, got '${token}'`);
  return token;
}

function roomTypeFamilies(value, label) {
  const raw = String(value ?? '').trim();
  const families = raw.split('|').map(part => part.trim()).filter(Boolean);
  if (!families.length) throw new Error(`room-type catalog: ${label} needs at least one family`);
  return families.map(family => roomTypeToken(family, `${label} family`));
}

function roomTypeCount(value, label, fallback) {
  if (value === undefined || value === null) return fallback;
  const number = Number(value);
  if (!Number.isInteger(number) || number < 0 || number > 64) throw new Error(`room-type catalog: ${label} must be an integer 0-64`);
  return number;
}

function normalizeGrammarLine(raw, section, label) {
  const line = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : null;
  if (!line) throw new Error(`room-type catalog: ${label} must be an object`);
  const families = roomTypeFamilies(line.family, label);
  const min = roomTypeCount(line.min, `${label}.min`, 0);
  const max = roomTypeCount(line.max, `${label}.max`, null);
  if (max !== null && max < min) throw new Error(`room-type catalog: ${label}.max is below min`);
  const zone = canonicalZoneToken(line.zone ?? SECTION_DEFAULT_ZONE[section]);
  if (!CANONICAL_ZONES.includes(zone)) throw new Error(`room-type catalog: ${label}.zone '${zone}' is not corner | wall_band | center`);
  const facing = line.facing === undefined || line.facing === null ? null : String(line.facing).trim();
  if (facing !== null && !GRAMMAR_FACINGS.includes(facing)) throw new Error(`room-type catalog: ${label}.facing '${facing}' is not room | focus | along_wall | free`);
  const anchorRef = line.anchorRef === undefined || line.anchorRef === null ? null : roomTypeToken(line.anchorRef, `${label}.anchorRef`);
  return Object.freeze({
    family: families[0],
    families: Object.freeze(families),
    min,
    max,
    zone,
    facing,
    anchorRef,
    required: line.required === true,
    optional: line.optional === true,
  });
}

function normalizeLightPlan(raw, label) {
  const plan = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : null;
  if (!plan) throw new Error(`room-type catalog: ${label} must be an object`);
  const families = roomTypeFamilies(plan.family, label);
  const perFt2 = Number(plan.perFt2 ?? 0);
  if (!Number.isFinite(perFt2) || perFt2 < 0 || perFt2 > 1) throw new Error(`room-type catalog: ${label}.perFt2 must be 0-1`);
  const min = roomTypeCount(plan.min, `${label}.min`, 0);
  const max = roomTypeCount(plan.max, `${label}.max`, null);
  if (max !== null && max < min) throw new Error(`room-type catalog: ${label}.max is below min`);
  const zone = canonicalZoneToken(plan.zone ?? SECTION_DEFAULT_ZONE.lights);
  if (!CANONICAL_ZONES.includes(zone)) throw new Error(`room-type catalog: ${label}.zone is invalid`);
  return Object.freeze({ family: families[0], families: Object.freeze(families), perFt2, min, max, zone });
}

function normalizeRoomTypeCatalog(source) {
  const catalog = source && typeof source === 'object' && !Array.isArray(source) ? source : null;
  if (!catalog || catalog.schema !== ROOM_TYPE_CATALOG_SCHEMA) throw new Error('room-type catalog schema mismatch');
  const revision = String(catalog.revision ?? '').trim();
  if (!revision) throw new Error('room-type catalog revision is required');
  const themes = new Map();
  for (const [index, raw] of (Array.isArray(catalog.themes) ? catalog.themes : []).entries()) {
    const id = roomTypeToken(raw?.id, `themes[${index}].id`);
    if (id.includes('-')) throw new Error(`room-type catalog: theme id '${id}' must not carry a hyphen`);
    if (themes.has(id)) throw new Error(`room-type catalog: duplicate theme '${id}'`);
    themes.set(id, Object.freeze({
      id,
      label: String(raw.label ?? id),
      preset: roomTypeToken(raw.preset, `themes[${index}].preset`),
      locationTypeIds: Object.freeze((Array.isArray(raw.locationTypeIds) ? raw.locationTypeIds : []).map(value => String(value).toUpperCase())),
    }));
  }
  if (!themes.size) throw new Error('room-type catalog needs at least one theme');
  const entries = new Map();
  const roomTypeAliases = Object.create(null);
  for (const [index, raw] of (Array.isArray(catalog.entries) ? catalog.entries : []).entries()) {
    const label = `entries[${index}]`;
    const id = roomTypeToken(raw?.id, `${label}.id`);
    if (!ROOM_TYPE_ID_RE.test(id)) throw new Error(`room-type catalog: id '${id}' must be <theme>-<room_type> with exactly one hyphen`);
    const [themeId] = id.split('-');
    if (!themes.has(themeId)) throw new Error(`room-type catalog: '${id}' names an unknown theme '${themeId}'`);
    if (String(raw.theme ?? themeId) !== themeId) throw new Error(`room-type catalog: '${id}' declares theme '${raw.theme}' but its id says '${themeId}'`);
    if (entries.has(id) || LEGACY_ROOM_FUNCTIONS.includes(id)) throw new Error(`room-type catalog: duplicate or reserved id '${id}'`);
    const lane = String(raw.lane ?? 'inventory');
    if (!ROOM_TYPE_LANES.includes(lane)) throw new Error(`room-type catalog: '${id}' lane must be inventory | placeable`);
    const grammarRaw = raw.grammar && typeof raw.grammar === 'object' ? raw.grammar : {};
    const grammar = {};
    for (const section of GRAMMAR_SECTIONS) {
      const lines = Array.isArray(grammarRaw[section]) ? grammarRaw[section] : [];
      grammar[section] = Object.freeze(lines.map((line, lineIndex) => normalizeGrammarLine(line, section, `${label}.grammar.${section}[${lineIndex}]`)));
    }
    grammar.lights = grammarRaw.lights ? normalizeLightPlan(grammarRaw.lights, `${label}.grammar.lights`) : null;
    const aliases = (Array.isArray(raw.aliases) ? raw.aliases : []).map(value => String(value).trim()).filter(Boolean);
    for (const alias of aliases) {
      if (Object.hasOwn(roomTypeAliases, alias) && roomTypeAliases[alias] !== id) throw new Error(`room-type catalog: alias '${alias}' claimed twice`);
      roomTypeAliases[alias] = id;
    }
    const toggles = {};
    for (const key of GENERATION_KEYS) {
      if (raw.toggles && typeof raw.toggles[key] === 'boolean') toggles[key] = raw.toggles[key];
    }
    const density = raw.density && typeof raw.density === 'object' ? raw.density : {};
    entries.set(id, Object.freeze({
      id,
      theme: themeId,
      label: String(raw.label ?? id),
      preset: roomTypeToken(raw.preset ?? themes.get(themeId).preset, `${label}.preset`),
      aliases: Object.freeze(aliases),
      lane,
      grammar: Object.freeze(grammar),
      density: Object.freeze({ default: roomTypeCount(density.default, `${label}.density.default`, 2) }),
      surfaces: Object.freeze({
        floor: raw.surfaces?.floor ?? null,
        wall: raw.surfaces?.wall ?? null,
        accent: raw.surfaces?.accent ?? null,
      }),
      containers: Object.freeze({
        fillPools: Object.freeze((Array.isArray(raw.containers?.fillPools) ? raw.containers.fillPools : []).map(String)),
        emptyIsAuthored: raw.containers?.emptyIsAuthored !== false,
      }),
      occupants: Object.freeze({ hint: String(raw.occupants?.hint ?? '') }),
      ambience: raw.ambience ? Object.freeze(normalizeRoomAmbience(raw.ambience)) : null,
      // The room type's default music (music handoff, 2026-09-10): blank = no default, the room follows its encounter.
      music: raw.music ? Object.freeze(normalizeRoomTypeMusic(raw.music)) : null,
      toggles: Object.freeze(toggles),
    }));
  }
  if (!entries.size) throw new Error('room-type catalog needs at least one entry');
  for (const entry of entries.values()) {
    // One owning line per family (alternatives included): a family named twice would split its
    // count budget between two lines, and a light family under `objects` would escape the light
    // plan's ownership (vocabulary §5).
    const owners = new Map();
    const claim = (family, where) => {
      if (owners.has(family)) throw new Error(`room-type catalog: '${entry.id}' names family '${family}' in both ${owners.get(family)} and ${where}`);
      owners.set(family, where);
    };
    for (const section of GRAMMAR_SECTIONS) entry.grammar[section].forEach(line => line.families.forEach(family => claim(family, section)));
    entry.grammar.lights?.families.forEach(family => claim(family, 'lights'));
    for (const section of GRAMMAR_SECTIONS) {
      for (const line of entry.grammar[section]) {
        if (line.anchorRef && !entry.grammar.anchors.some(anchor => anchor.families.includes(line.anchorRef))
          && !entry.grammar.center.some(other => other.families.includes(line.anchorRef))
          && !entry.grammar.wall_band.some(other => other.families.includes(line.anchorRef))) {
          throw new Error(`room-type catalog: '${entry.id}' ${section} line references unknown anchor '${line.anchorRef}'`);
        }
      }
    }
  }
  return Object.freeze({
    schema: ROOM_TYPE_CATALOG_SCHEMA,
    revision,
    themes: Object.freeze([...themes.values()]),
    entries: Object.freeze([...entries.values()]),
    entryById: entries,
    themeById: themes,
    roomTypeAliases: Object.freeze(roomTypeAliases),
  });
}

export const ROOM_TYPE_CATALOG = normalizeRoomTypeCatalog(ROOM_TYPE_CATALOG_SOURCE);
export const ROOM_TYPE_THEMES = ROOM_TYPE_CATALOG.themes;
/** Alias tables, namespaced by token domain (vocabulary §1). */
export const ROOM_TYPE_ALIASES = Object.freeze({
  roomType: ROOM_TYPE_CATALOG.roomTypeAliases,
  zone: ZONE_ALIASES,
});
export const ROOM_FUNCTIONS = Object.freeze([
  ...LEGACY_ROOM_FUNCTIONS,
  ...ROOM_TYPE_CATALOG.entries.map(entry => entry.id),
]);

/** Canonical catalog id for a room function or alias; legacy ids without an entry return themselves. */
export function canonicalRoomTypeId(value) {
  const token = String(value ?? '').trim();
  if (ROOM_TYPE_CATALOG.entryById.has(token)) return token;
  return Object.hasOwn(ROOM_TYPE_ALIASES.roomType, token) ? ROOM_TYPE_ALIASES.roomType[token] : token;
}

/** The catalog entry behind a room function (accepts legacy ids and aliases); null for `unassigned`. */
export function roomTypeEntry(value) {
  return ROOM_TYPE_CATALOG.entryById.get(canonicalRoomTypeId(value)) ?? null;
}

// A function, not a const regex: the room-type catalog normalizes its entries at module load (above).
function isRoomAmbienceUuid(value) {
  return typeof value === 'string' && /^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i.test(value);
}

/** A room type's default music: MusicForge bucket slugs; blank = no default (the room follows its encounter). */
export function normalizeRoomTypeMusic(value) {
  const row = value && typeof value === 'object' ? value : {};
  const pick = v => (typeof v === 'string' && /^[a-z0-9_]{1,64}$/.test(v.trim()) ? v.trim() : '');
  return { bucket: pick(row.bucket), combatBucket: pick(row.combatBucket) };
}

export function normalizeRoomAmbience(value) {
  const row = value && typeof value === 'object' ? value : {};
  return {
    mode: ['inherit', 'custom', 'silence'].includes(row.mode) ? row.mode : 'default',
    audioId: isRoomAmbienceUuid(row.audioId) ? row.audioId.toUpperCase() : '',
    // A WindLevel item id is the room's own band (owner 2026-09-10: a room can be windier than outside).
    // The save endpoint proves it is a real WindLevel; an unknown band simply plays no wind.
    wind: ['inherit', 'off'].includes(row.wind) ? row.wind : isRoomAmbienceUuid(row.wind) ? row.wind.toUpperCase() : 'default',
  };
}

/** Wind exposure: the same "sky reaches here" rule as ambient light. Accepts a draft room or a shape box. */
export function roomIsRoofed(room) {
  if (!room || typeof room !== 'object') return false;
  return boxIsRoofed(room.creation && typeof room.creation === 'object' ? room : { creation: room });
}

/**
 * Resolve only local overrides; encounter day/night/weather selection remains with its audio owner.
 * Wind (owner 2026-09-10): the encounter's band is the default (itself seeded from the location), resolved
 * live here — never copied into the room — so a GM wind change reaches every room that did not override
 * it. A room sealed from the sky defaults to no wind; the room's own setting wins either way.
 */
export function resolveRoomAmbience(value, roomFunction, roofed = false) {
  const authored = normalizeRoomAmbience(value);
  const entry = roomTypeEntry(roomFunction);
  const defaults = entry?.ambience;
  const fromType = authored.mode === 'default' && defaults
    && (defaults.mode === 'silence' || (defaults.mode === 'custom' && defaults.audioId));
  const mode = fromType ? defaults.mode : authored.mode === 'default' ? 'inherit' : authored.mode;
  const [wind, windSource] = authored.wind !== 'default'
    ? [authored.wind, authored.wind === 'inherit' ? 'encounter' : 'room']
    : defaults?.wind === 'off' ? ['off', 'room-type']
      : roofed === true ? ['off', 'roof'] : ['inherit', 'encounter'];
  return {
    mode,
    audioId: mode === 'custom' ? (fromType ? defaults.audioId : authored.audioId) : '',
    wind,
    windSource,
    source: fromType ? 'room-type' : mode === 'inherit' ? 'encounter' : 'room',
    typeLabel: entry ? roomTypeLabel(entry.id) : '',
  };
}

/**
 * True only for a room whose STORED function is a canonical catalog id. A legacy kebab id
 * resolves to an entry through the alias table for matching, but it keeps the legacy compile
 * path (pilot recipe, legacy dressing families) so persisted rooms re-lay identically.
 */
export function roomFunctionIsCatalogId(value) {
  return ROOM_TYPE_CATALOG.entryById.has(String(value ?? '').trim());
}

export function roomTypeLabel(value) {
  const entry = roomTypeEntry(value);
  if (!entry) return String(value ?? '').replace(/-/g, ' ');
  const theme = ROOM_TYPE_CATALOG.themeById.get(entry.theme);
  return `${theme?.label ?? entry.theme} · ${entry.label}`;
}
const generationCapability = (enabled, reason, eligibleFamilyIds = [], eligibleRecipeIds = []) => Object.freeze({
  enabled,
  reason,
  eligibleFamilyIds: Object.freeze([...eligibleFamilyIds]),
  eligibleRecipeIds: Object.freeze([...eligibleRecipeIds]),
});
const roomPresetV2 = (input) => {
  const defaults = Object.freeze({ ...input.surfaces.defaults });
  const eligible = Object.freeze(Object.fromEntries(Object.entries(input.surfaces.eligible)
    .map(([role, ids]) => [role, Object.freeze([...ids])])));
  return Object.freeze({
    schema: 'ft.xz3d.room-preset.v2',
    ...input,
    surfaces: Object.freeze({
      floor: defaults.floor,
      wall: defaults.wall,
      accent: defaults.accent,
      defaults,
      eligible,
    }),
    structure: Object.freeze({ ...input.structure }),
    structural: Object.freeze({ ...input.structural }),
    generation: Object.freeze({
      defaults: Object.freeze({ ...input.generation.defaults, curtains: input.generation.capabilities.windows.enabled === true }),
      capabilities: Object.freeze({ ...input.generation.capabilities, curtains: generationCapability(input.generation.capabilities.windows.enabled, 'Inside-mounted cloth coverings for windows') }),
      missingAssetBehavior: 'omit',
    }),
    eligibility: Object.freeze({
      surfaceIds: Object.freeze([...input.eligibility.surfaceIds]),
      architectureFamilies: Object.freeze([...input.eligibility.architectureFamilies]),
      propRecipeIds: Object.freeze([...input.eligibility.propRecipeIds]),
    }),
    receipts: Object.freeze({ queryOnly: true, writes: 0, reviewedAt: input.receipts.reviewedAt }),
  });
};
const preservedGenerationDefaults = Object.freeze({
  loot: true, lights: true, walls: true, windows: true, doors: true, tiles: true, roofs: true, objects: true,
});
const preservedGenerationCapabilities = Object.freeze(Object.fromEntries(
  Object.keys(preservedGenerationDefaults).map(key => [
    key,
    generationCapability(true, 'Existing accepted presentation capability preserved', [], [`legacy-${key}`]),
  ]),
));
export const ROOM_PRESETS = Object.freeze({
  'legacy-structural': roomPresetV2({
    id: 'legacy-structural',
    label: 'Existing encounter structure',
    selectable: false,
    environment: 'interior',
    enclosure: 'walls-and-ceiling',
    theme: 'stone',
    interior: 'stone',
    connection: Object.freeze({ kind: 'door-intent', label: 'stone threshold', doorItemId: '9B944DEF-132E-9B0E-1795-C7698246570E' }),
    surfaces: {
      defaults: { floor: null, wall: null, ceiling: null, accent: null, trim: null },
      eligible: { floor: [], wall: [], ceiling: [], accent: [], trim: [] },
    },
    structure: {
      floorThicknessFt: 0.25,
      clearHeight: { policy: 'default' },
      boundaryAuthority: 'canonical-room-boundary',
      roofPolicy: 'independent-structure',
      connectionDefault: 'door-intent',
      tracedWallDefaults: { heightFt: 10, thicknessFt: 0.5 },
    },
    structural: { floorFt: 0, ceilingFt: 10, compatibilityCeilingFt: 10 },
    generation: { defaults: preservedGenerationDefaults, capabilities: preservedGenerationCapabilities },
    eligibility: { surfaceIds: [], architectureFamilies: [], propRecipeIds: [] },
    receipts: { reviewedAt: '2026-08-15' },
  }),
  'old-stone-dungeon-interior': roomPresetV2({
    id: 'old-stone-dungeon-interior',
    label: 'Old Stone Dungeon — Interior',
    selectable: true,
    environment: 'interior',
    enclosure: 'walls-and-ceiling',
    theme: 'old-stone-dungeon',
    interior: 'stone',
    connection: Object.freeze({ kind: 'door-intent', label: 'stone door intent', doorItemId: '9B944DEF-132E-9B0E-1795-C7698246570E' }),
    // A stone keep is barred throughout: see through, never climb through.
    windows: Object.freeze({
      spacingFt: 20,
      // Owner 2026-09-08: "it could be random where seventy percent of windows are locked ...
      // having an unlocked window is still fun for the player to try to break in". The 30% that are
      // open are the point of the mechanic, not an oversight.
      lockChance: 0.7,
      mix: Object.freeze([Object.freeze({ itemId: 'B29D5E10-7C34-4A86-9D52-8E60B1F47A3C', weight: 1 })]),
      // DAFT3D S26: every catalog room type builds on this preset, so the room's FUNCTION leans the
      // mix (windowIntentForOpening reads byFunction first; an empty list means no windows at all).
      // A first proposal the owner may retune — a burial vault or a cellar has no window to look out
      // of; a chapel has its stained glass; a laboratory wants light to work by.
      byFunction: Object.freeze({
        'crypt-chapel': Object.freeze([
          Object.freeze({ itemId: '9D9CD8E5-E07E-4BC7-A384-862CAE049ACB', weight: 2 }),
          Object.freeze({ itemId: 'B29D5E10-7C34-4A86-9D52-8E60B1F47A3C', weight: 1 }),
        ]),
        'crypt-burial_chamber': Object.freeze([]),
        'crypt-ossuary': Object.freeze([]),
        'crypt-catacomb_passage': Object.freeze([]),
        'alchemists_laboratory-laboratory': Object.freeze([
          Object.freeze({ itemId: '832C71DA-6E9B-497A-B0F5-1AC9487E3206', weight: 2 }),
          Object.freeze({ itemId: 'B29D5E10-7C34-4A86-9D52-8E60B1F47A3C', weight: 1 }),
        ]),
        'alchemists_laboratory-library': Object.freeze([
          Object.freeze({ itemId: '832C71DA-6E9B-497A-B0F5-1AC9487E3206', weight: 2 }),
          Object.freeze({ itemId: 'B29D5E10-7C34-4A86-9D52-8E60B1F47A3C', weight: 1 }),
        ]),
        'alchemists_laboratory-storage_room': Object.freeze([Object.freeze({ itemId: 'B29D5E10-7C34-4A86-9D52-8E60B1F47A3C', weight: 1 })]),
        'alchemists_laboratory-blast_room': Object.freeze([Object.freeze({ itemId: 'B29D5E10-7C34-4A86-9D52-8E60B1F47A3C', weight: 1 })]),
        'tavern-common_room': Object.freeze([
          Object.freeze({ itemId: '832C71DA-6E9B-497A-B0F5-1AC9487E3206', weight: 1 }),
          Object.freeze({ itemId: '6C4A1F73-2B58-4D91-A0E6-3F71D8C25904', weight: 1 }),
        ]),
        'tavern-guest_room': Object.freeze([
          Object.freeze({ itemId: '832C71DA-6E9B-497A-B0F5-1AC9487E3206', weight: 1 }),
          Object.freeze({ itemId: '6C4A1F73-2B58-4D91-A0E6-3F71D8C25904', weight: 1 }),
        ]),
        'tavern-kitchen': Object.freeze([Object.freeze({ itemId: '6C4A1F73-2B58-4D91-A0E6-3F71D8C25904', weight: 1 })]),
        'tavern-cellar': Object.freeze([]),
      }),
      // DAFT3D S26 follow-up — owner 2026-09-25: "curtains depends on what type of room". A NEW room's
      // Curtains default: yes where people live and work behind glass; no behind bars, stained glass or
      // shutters (a shutter already is the covering). A function not named here takes the preset
      // default, which follows the Windows capability. Read by curtainDefaultForRoom().
      curtainsByFunction: Object.freeze({
        'alchemists_laboratory-laboratory': true,
        'alchemists_laboratory-library': true,
        'tavern-common_room': true,
        'tavern-guest_room': true,
        'crypt-chapel': false,
        'alchemists_laboratory-storage_room': false,
        'alchemists_laboratory-blast_room': false,
        'alchemists_laboratory-dissection_room': false,
        'tavern-kitchen': false,
        unassigned: false,
      }),
    }),
    surfaces: {
      defaults: {
        floor: 'scanslibrary_pe9d59',
        wall: 'scanslibrary_gerq5c',
        ceiling: 'scanslibrary_ynkdmf',
        accent: 'scanslibrary_ynkdmf',
        trim: null,
      },
      eligible: {
        floor: ['scanslibrary_pe9d59', 'scanslibrary_hzbjt3', 'scanslibrary_mfxw5n', 'scanslibrary_yn2us4'],
        wall: ['scanslibrary_gerq5c'],
        ceiling: ['scanslibrary_ynkdmf'],
        accent: ['scanslibrary_ynkdmf'],
        trim: [],
      },
    },
    structure: {
      floorThicknessFt: 0.25,
      clearHeight: { policy: 'default' },
      boundaryAuthority: 'canonical-room-boundary',
      roofPolicy: 'none',
      connectionDefault: 'door-intent',
      tracedWallDefaults: { heightFt: 10, thicknessFt: 0.5 },
    },
    structural: { floorFt: 0, ceilingFt: 10, compatibilityCeilingFt: 10 },
    generation: {
      // DAFT3D S26 (owner D-S26-1): windows populate a new room automatically, like its torches.
      // A room saved earlier keeps its stored toggle (restoreRoomGenerationIntents).
      defaults: {
        loot: false, lights: true, walls: true, windows: true,
        doors: false, tiles: true, roofs: false, objects: true,
      },
      capabilities: {
        loot: generationCapability(false, 'No approved loot recipe'),
        // DAFT3D S2: the room-type catalog carries a light plan per room type (candelabra,
        // lanterns, braziers), so an interior room may ask for lights again.
        lights: generationCapability(true, 'Room-type catalog light plan', [], ['room-type-catalog']),
        walls: generationCapability(true, 'Canonical procedural room boundaries', ['canonical-room-boundary']),
        // The stale "No approved window family" was true until S11 art closed (2026-09-09): barred,
        // glazed, shuttered and stained are all meshed and approved.
        windows: generationCapability(true, 'Exterior walls only — place interior windows with the Windows tool', [], ['window-item-type']),
        doors: generationCapability(false, 'No approved public world-role door mesh'),
        tiles: generationCapability(true, 'Approved eligible floor surfaces', [], ['approved-surface-material']),
        roofs: generationCapability(false, 'No approved roof family'),
        objects: generationCapability(true, 'Room-type catalog grammar (furniture, storage, clutter)', [], ['room-type-catalog']),
      },
    },
    eligibility: {
      surfaceIds: [
        'scanslibrary_pe9d59', 'scanslibrary_gerq5c', 'scanslibrary_ynkdmf',
        'scanslibrary_hzbjt3', 'scanslibrary_mfxw5n', 'scanslibrary_yn2us4',
      ],
      architectureFamilies: ['canonical-room-boundary'],
      propRecipeIds: ['approved-surface-material'],
    },
    receipts: { reviewedAt: '2026-08-15' },
  }),
  'wet-cave-interior': roomPresetV2({
    id: 'wet-cave-interior',
    label: 'Wet Cave — Interior',
    selectable: true,
    environment: 'interior',
    enclosure: 'walls-and-ceiling',
    theme: 'wet-cave',
    interior: 'stone',
    connection: Object.freeze({ kind: 'opening', label: 'natural cavern breach' }),
    surfaces: {
      defaults: {
        floor: 'scanslibrary_hzbjt3', wall: 'scanslibrary_mfxw5n',
        ceiling: 'scanslibrary_yn2us4', accent: 'scanslibrary_yn2us4', trim: null,
      },
      eligible: {
        floor: ['scanslibrary_hzbjt3', 'scanslibrary_mfxw5n', 'scanslibrary_yn2us4'],
        wall: ['scanslibrary_hzbjt3', 'scanslibrary_mfxw5n'],
        ceiling: ['scanslibrary_yn2us4'], accent: ['scanslibrary_yn2us4'], trim: [],
      },
    },
    structure: {
      floorThicknessFt: 0.25,
      clearHeight: { policy: 'default' },
      boundaryAuthority: 'canonical-room-boundary',
      roofPolicy: 'independent-structure',
      connectionDefault: 'opening',
      tracedWallDefaults: { heightFt: 10, thicknessFt: 0.5 },
    },
    structural: { floorFt: 0, ceilingFt: 10, compatibilityCeilingFt: 10 },
    generation: {
      defaults: {
        ...preservedGenerationDefaults,
        // Underground cave volumes have no exterior wall to receive a window.
        // Keep the rest of the accepted wet-cave presentation surface intact.
        windows: false,
      },
      capabilities: {
        ...preservedGenerationCapabilities,
        windows: generationCapability(false, 'Underground cave rooms do not generate windows'),
      },
    },
    eligibility: {
      surfaceIds: ['scanslibrary_hzbjt3', 'scanslibrary_mfxw5n', 'scanslibrary_yn2us4'],
      architectureFamilies: [],
      propRecipeIds: ['wet-cave'],
    },
    receipts: { reviewedAt: '2026-08-15' },
  }),
  'ramshackle-timber-interior': roomPresetV2({
    id: 'ramshackle-timber-interior',
    label: 'Ramshackle Mine Building — Interior',
    // This preset currently exists for authored, query-only adventure candidates.
    // Keep it out of the general room menu until the owner reviews the look.
    selectable: false,
    environment: 'interior',
    enclosure: 'walls-and-ceiling',
    theme: 'ramshackle-mine-timber',
    interior: 'timber',
    connection: Object.freeze({ kind: 'door-intent', label: 'rough timber mine door', doorItemId: '51C6F287-5748-E010-38D9-17E13FC7546E' }),
    // A ramshackle timber building is MOSTLY SHUTTERS with the odd glazed light — glass is
    // expensive, and the owner's call is that shutters "should be more common" for small houses
    // and certain room types, while never being the blanket default (2026-09-07).
    windows: Object.freeze({
      spacingFt: 15,
      // The same 70% as anywhere else — the owner gave one figure, and a building type that quietly
      // used a different one would make "test the windows" mean something different per building
      // without ever saying so.
      lockChance: 0.7,
      mix: Object.freeze([
        Object.freeze({ itemId: '6C4A1F73-2B58-4D91-A0E6-3F71D8C25904', weight: 3 }),
        Object.freeze({ itemId: '832C71DA-6E9B-497A-B0F5-1AC9487E3206', weight: 1 }),
      ]),
    }),
    surfaces: {
      defaults: {
        floor: 'scanslibrary_hzbjt3', wall: null,
        ceiling: null, accent: null, trim: null,
      },
      eligible: {
        floor: ['scanslibrary_hzbjt3'], wall: [],
        ceiling: [], accent: [], trim: [],
      },
    },
    structure: {
      floorThicknessFt: 0.25,
      clearHeight: { policy: 'default' },
      boundaryAuthority: 'canonical-room-boundary',
      roofPolicy: 'none',
      connectionDefault: 'door-intent',
      tracedWallDefaults: { heightFt: 15, thicknessFt: 0.5 },
    },
    structural: { floorFt: 0, ceilingFt: 15, compatibilityCeilingFt: 15 },
    generation: {
      defaults: {
        loot: false, lights: false, walls: true, windows: true,
        doors: true, tiles: true, roofs: false, objects: false,
      },
      capabilities: {
        loot: generationCapability(false, 'Adventure-authored furnishings supply the room dressing'),
        objects: generationCapability(false, 'Adventure-authored furnishings supply the room dressing'),
        lights: generationCapability(false, 'Adventure-authored fixtures supply the room lighting'),
        walls: generationCapability(true, 'Native procedural plank walls and mine framing', ['native-procedural-timber-boundary']),
        windows: generationCapability(true, 'Exterior walls only — place interior windows with the Windows tool', ['native-procedural-timber-window']),
        doors: generationCapability(true, 'Native rough-timber door leaf fitted to the canonical opening', ['native-procedural-timber-door']),
        tiles: generationCapability(true, 'Approved packed-earth floor surface', [], ['approved-surface-material']),
        roofs: generationCapability(false, 'Cutaway review omits an unreviewed roof family'),
      },
    },
    eligibility: {
      surfaceIds: ['scanslibrary_hzbjt3'],
      architectureFamilies: ['native-procedural-timber-boundary', 'native-procedural-timber-door', 'native-procedural-timber-window'],
      propRecipeIds: ['approved-surface-material'],
    },
    receipts: { reviewedAt: '2026-08-29' },
  }),
  'exterior-open': roomPresetV2({
    id: 'exterior-open',
    label: 'Legacy Exterior / Open Ground',
    // Compatibility-only: encounter Scenery now owns the full X/Y ground
    // plane. Keep compiling stored drafts, but do not offer this as a new
    // room/structure type.
    selectable: false,
    environment: 'exterior-open',
    enclosure: 'none',
    theme: 'open-ground',
    interior: 'open',
    connection: Object.freeze({ kind: 'open', label: 'open terrain transition' }),
    surfaces: {
      defaults: { floor: null, wall: null, ceiling: null, accent: null, trim: null },
      // Existing open-ground rooms normally inherit encounter Scenery. A deliberately
      // bounded local platform may opt into the reviewed Outer Space regolith surface;
      // the override is explicit and does not change ordinary exterior defaults.
      eligible: { floor: ['scanslibrary_mvxfpf'], wall: [], ceiling: [], accent: [], trim: [] },
    },
    // Shape blob v1 still requires a finite box ceiling. This compatibility
    // value is not a physical roof and is deliberately excluded from the
    // structural/view envelope below.
    structure: {
      floorThicknessFt: 0,
      clearHeight: { policy: 'fixed', valueFt: 1000 },
      boundaryAuthority: 'canonical-room-boundary',
      roofPolicy: 'none',
      connectionDefault: 'auto',
      tracedWallDefaults: { heightFt: 10, thicknessFt: 0.5 },
    },
    structural: { floorFt: 0, ceilingFt: null, compatibilityCeilingFt: 1000 },
    generation: { defaults: preservedGenerationDefaults, capabilities: preservedGenerationCapabilities },
    eligibility: { surfaceIds: ['scanslibrary_mvxfpf'], architectureFamilies: [], propRecipeIds: [] },
    receipts: { reviewedAt: '2026-08-15' },
  }),
  'terrain-pad': roomPresetV2({
    id: 'terrain-pad',
    label: 'Terrain pad (ditch / hill)',
    // A terrain pad is GROUND MOVED, not a room: one authored ground volume at its own
    // elevation. It is the only preset whose floor is AUTHORED rather than derived from its
    // room floor — see `floorPolicy` below and the branch it drives in roomFromBox(). Plan:
    // docs/TERRAIN_PAD_PLAN_20260903.md.
    selectable: true,
    environment: 'exterior-open',
    enclosure: 'none',
    theme: 'open-ground',
    interior: 'open',
    connection: Object.freeze({ kind: 'open', label: 'open terrain transition' }),
    surfaces: {
      // No surface of its own: a pad wears the encounter Scenery ground so it reads as the
      // terrain itself moved, and follows the biome when that changes (plan decision 8).
      defaults: { floor: null, wall: null, ceiling: null, accent: null, trim: null },
      eligible: { floor: [], wall: [], ceiling: [], accent: [], trim: [] },
    },
    structure: {
      floorThicknessFt: 0,
      clearHeight: { policy: 'fixed', valueFt: 1000 },
      boundaryAuthority: 'canonical-room-boundary',
      // ⭐ The one genuinely new preset capability. Every other preset-owned room takes
      // `room.floorFt + preset.structural.floorFt`; a pad reads `box.floor`, which is
      // what lets a GM drag it to an arbitrary elevation.
      floorPolicy: 'authored',
      roofPolicy: 'none',
      connectionDefault: 'auto',
      tracedWallDefaults: { heightFt: 10, thicknessFt: 0.5 },
    },
    structural: { floorFt: 0, ceilingFt: null, compatibilityCeilingFt: 1000 },
    generation: {
      defaults: {
        loot: false, lights: false, walls: false, windows: false,
        doors: false, tiles: true, roofs: false, objects: false,
      },
      capabilities: {
        loot: generationCapability(false, 'Ground dressing comes from encounter Scenery'),
        objects: generationCapability(false, 'Ground dressing comes from encounter Scenery'),
        lights: generationCapability(false, 'Open ground carries no fixture family'),
        walls: generationCapability(false, 'A terrain pad is open ground, never an enclosure'),
        windows: generationCapability(false, 'Open ground has no wall to receive a window'),
        doors: generationCapability(false, 'Open ground has no threshold'),
        // `tiles` is the floor switch: off = no pad surface AND no board aperture, which is the
        // sanctioned way to let the terrain back through (plan decision 1 / the Tiles rule).
        tiles: generationCapability(true, 'Inherits the encounter Scenery ground surface', [], []),
        roofs: generationCapability(false, 'Open ground is never roofed'),
      },
    },
    eligibility: { surfaceIds: [], architectureFamilies: [], propRecipeIds: [] },
    receipts: { reviewedAt: '2026-09-03' },
  }),
});
/** Compatibility helper: continuous-world rooms all author an explicit physical floor. */
export function roomPresetFloorIsAuthored(preset) {
  return preset != null;
}
/** True for the terrain-pad preset — ground moved to its own elevation, not a room. */
export function roomIsTerrainPad(room) {
  return String(room?.presetId ?? '') === 'terrain-pad';
}
/**
 * Is this pair of footprints a CONFLICT for authoring?
 *
 * Rooms may touch to form a connection but never overlap — EXCEPT that two terrain pads may,
 * because overlapping pads are how a hillside is terraced, and the shape core already treats box
 * overlap as a connection rather than an error. A pad overlapping a real room stays forbidden:
 * terrain running through a building's floor is the defect the ground-board aperture work fixed.
 *
 * Lives here, beside the geometry it depends on, so the authoring surface and its test exercise
 * ONE implementation rather than two agreeing restatements.
 */
export function roomFootprintsConflict(left, right) {
  if (!left || !right) return false;
  if (!same(left.floorFt ?? left.floor, right.floorFt ?? right.floor)) return false;
  if (roomIsTerrainPad(left) && roomIsTerrainPad(right)) return false;
  return roomFootprintsOverlap(left, right);
}
/**
 * Does this floor take an aperture in the Scenery ground board, and how should it be cut?
 *
 * The board must never cap or pierce an authored floor. Three eligibility rules:
 *   • an interior room floor ALWAYS takes one;
 *   • a TERRAIN PAD always takes one, raised or sunken, so relief cannot pierce its skirt;
 *   • any other exterior-open floor inherits encounter Scenery (its ground IS its floor), so it
 *     takes one only when it sits BELOW the board — a legacy pit or ravine terrain would bury.
 * The room's Tiles switch is the ONE sanctioned way to suppress an aperture and let the terrain
 * back through.
 *
 * Containment: a floor at or above the board seat covers its own aperture, so cut INWARD
 * ('under-floor') and let the floor hide the snap. A floor below the seat cannot, so cut OUTWARD
 * ('clear') and guarantee the authored opening is fully open rather than part-capped.
 *
 * Returns null when no aperture is due. Pure — the renderer supplies the numbers.
 */
export function boardApertureForFloor(input) {
  const {
    floorKind,
    tilesOn,
    terrainPad = false,
    floorFt,
    groundSeatFt,
    epsilonFt = 0.05,
  } = input ?? {};
  if (!tilesOn) return null;
  if (!Number.isFinite(floorFt) || !Number.isFinite(groundSeatFt)) return null;
  const belowBoard = floorFt < groundSeatFt - epsilonFt;
  if (floorKind !== 'room-floor' && !terrainPad && !belowBoard) return null;
  return {
    containment: belowBoard ? 'clear' : 'under-floor',
    belowBoard,
    terrainPad: terrainPad === true,
  };
}
/**
 * The door IDENTITIES a doorway can hang (owner 2026-09-07: "labelling the doors specifically with
 * their correct item IDs is more important" — the item chooses the mesh, so a plain wooden door
 * draws a wooden door and a wooden DOUBLE door draws a double-door mesh).
 *
 * `leaves` is what makes that work: a doorway wide enough for two leaves picks the double identity
 * of the same material. `meshAvailable` is measured, not assumed — counts from the live registry on
 * 2026-09-07 (`tools/probes/daft3d_s11_door_catalog_recon.php`), where the whole catalog previously
 * claimed `false` even though wooden, iron and stone doors all had approved meshes.
 *
 * `opacity`, `tab` and `looks` come from the VISION pass over every distinct approved door mesh
 * (`demos/xz3d/door-mesh-taxonomy-v1.json`, 84 assets judged by eye on 2026-09-07); the taxonomy
 * contract asserts these constants against that file so the two cannot drift.
 *   • `opacity` is mechanically load-bearing — only a `solid` door blocks line of sight.
 *   • `tab` keeps the contemporary/industrial pack off the fantasy doors panel (owner D-S11-4:
 *     "they should be put in sort of the sci-fi section ... part of a different tab system").
 *   • `looks` is the number of distinct meshes the identity owns once the size collapse lands.
 *   • `pending` marks an identity whose catalog row is authored but not yet ingested.
 */
export const DOOR_INTENT_CATALOG = Object.freeze([
  Object.freeze({ id: '51C6F287-5748-E010-38D9-17E13FC7546E', label: 'Door, Wooden', material: 'wooden', leaves: 1, meshAvailable: true, opacity: 'solid', tab: 'doors', looks: 30 }),
  Object.freeze({ id: 'CFA55207-D596-5EC5-BA49-BFC22F9A1658', label: 'Door, Double, Wooden', material: 'wooden', leaves: 2, meshAvailable: true, opacity: 'solid', tab: 'doors', looks: 10 }),
  Object.freeze({ id: '9B944DEF-132E-9B0E-1795-C7698246570E', label: 'Door, Stone', material: 'stone', leaves: 1, meshAvailable: true, opacity: 'solid', tab: 'doors', looks: 3 }),
  Object.freeze({ id: '37F55C3F-CEF4-29FF-D2CE-1A330ADF6FCC', label: 'Door, Iron', material: 'iron', leaves: 1, meshAvailable: true, opacity: 'solid', tab: 'doors', looks: 5 }),
  Object.freeze({ id: '5DF1D80E-761C-5C85-909F-1A89194336DD', label: 'Door, Double, Iron', material: 'iron', leaves: 2, meshAvailable: true, opacity: 'solid', tab: 'doors', looks: 2 }),
  Object.freeze({ id: 'DB287CE5-6B3E-4B14-B693-27C2A5B17813', label: 'Bronze Laboratory Door', material: 'bronze', leaves: 1, meshAvailable: true, opacity: 'solid', tab: 'doors', looks: 2 }),
  Object.freeze({ id: '90EDF803-5302-5F32-9809-DD089063E323', label: 'Gate, Iron', material: 'iron', leaves: 1, meshAvailable: true, opacity: 'barred', tab: 'doors', looks: 12 }),
  Object.freeze({ id: '07B48961-9426-C1B0-FA86-14B539BF21B5', label: 'Gate, Double, Iron', material: 'iron', leaves: 2, meshAvailable: true, opacity: 'barred', tab: 'doors', looks: 3 }),
  Object.freeze({ id: '9AA00718-03BB-51BF-89F4-28F0A9CB1B7E', label: 'Portcullis, Iron', material: 'iron', leaves: 1, meshAvailable: true, opacity: 'barred', tab: 'doors', looks: 1 }),
  Object.freeze({ id: 'D658FB67-ED3E-C663-4729-C15A8DF62F12', label: 'Door, Steel', material: 'steel', leaves: 1, meshAvailable: true, opacity: 'solid', tab: 'scifi', looks: 9 }),
  Object.freeze({ id: '00974A0B-D1C6-EF85-799D-C94F17459DE7', label: 'Door, Double, Steel', material: 'steel', leaves: 2, meshAvailable: true, opacity: 'solid', tab: 'scifi', looks: 6 }),
  Object.freeze({ id: '1D0B9F2B-ACB0-F1D9-E9B9-1FCE98EB5AA2', label: 'Gate, Steel', material: 'steel', leaves: 2, meshAvailable: true, opacity: 'grille', tab: 'scifi', looks: 1 }),
  Object.freeze({ id: '761FFA20-63FB-4925-A994-04C86BD36B05', label: 'Door, Mithral', material: 'mithral', leaves: 1, meshAvailable: false, opacity: 'solid', tab: 'doors', looks: 0 }),
  Object.freeze({ id: '81A6594A-35E5-453E-92E7-D6E4E20844AC', label: 'Door, Adamantine', material: 'adamantine', leaves: 1, meshAvailable: false, opacity: 'solid', tab: 'doors', looks: 0 }),
  Object.freeze({ id: '87FC877B-91A6-91F8-E86D-AD51A2D48AEE', label: 'Door, Vines', material: 'vines', leaves: 1, meshAvailable: false, opacity: 'solid', tab: 'doors', looks: 0 }),
]);

/** The identities a GM may hang today: everything whose catalog row actually exists. */
export const DOOR_INTENT_LIVE = Object.freeze(DOOR_INTENT_CATALOG.filter(entry => entry.pending !== true));

/** A doorway at least this wide is hung with two leaves rather than one. */
export const DOOR_DOUBLE_LEAF_MIN_WIDTH_FT = 10;

/**
 * The door identity a doorway of this width should hang, in this material: the DOUBLE variant when
 * the doorway is wide enough and one exists, else the single. This is what makes the mesh correct
 * without anyone choosing a mesh — the item is the choice (owner 2026-09-07).
 */
export function doorIntentForWidth(materialOrItemId, widthFt) {
  const seed = doorIntent(materialOrItemId);
  const material = DOOR_INTENT_CATALOG.some(entry => entry.id === materialOrItemId)
    ? seed.material
    : (String(materialOrItemId ?? '').toLowerCase() || seed.material);
  const family = DOOR_INTENT_CATALOG.filter(entry => entry.material === material);
  if (!family.length) return seed;
  const wantsDouble = Number.isFinite(widthFt) && widthFt >= DOOR_DOUBLE_LEAF_MIN_WIDTH_FT - EPS;
  return family.find(entry => (wantsDouble ? entry.leaves === 2 : entry.leaves === 1))
    ?? family.find(entry => entry.leaves === 1)
    ?? seed;
}

const EPS = 1e-6;
const DEFAULT_GRID_FT = 5;
export const DEFAULT_CLEAR_HEIGHT_FT = 10;
const FEET_PER_METER = 1 / 0.3048;
export const MAX_VERTICAL_CONNECTION_RESIDUAL_FT = 0.25;
export const MAX_VERTICAL_CONNECTION_ANCHOR_EVALUATIONS = 81;
const DENSITY_RATES = [0, 0.055, 0.11, 0.18];
/** Legacy per-function dressing families; the catalog supplies these for every catalog entry. */
const GRAMMARS = Object.freeze({
  unassigned: ['rubble', 'stone-cluster'],
  'natural-chamber': ['stalagmites', 'rubble', 'fungal-ring', 'stone-cluster'],
  'wet-grotto': ['wet-rock', 'fungal-ring', 'mineral-shelf'],
  hatchery: ['egg-cluster', 'shell-fragments', 'warm-mud-vent', 'nest-scrape'],
  'occupied-cave': ['bedroll', 'crate-stack', 'brazier', 'supply-bundle'],
  'ruined-shrine': ['broken-column', 'offering-stone', 'candle-cluster', 'carved-rubble'],
  connector: ['rubble', 'wall-stones'],
});

function finiteNumber(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

/** Families the seeded dressing pass may scatter for a room function (catalog first, legacy second). */
export function roomTypeDressingFamilies(roomFunction) {
  // A legacy kebab id keeps its legacy family list verbatim (order included): the dressing pass
  // draws by seeded index, so a re-ordered list would silently re-lay every persisted cave room.
  if (Object.hasOwn(GRAMMARS, roomFunction)) return GRAMMARS[roomFunction];
  const entry = roomFunctionIsCatalogId(roomFunction) ? roomTypeEntry(roomFunction) : null;
  if (entry) {
    // Every grammar line with budget left after its mandatory `min` requests may be drawn by the
    // seeded density pass (so a zero-minimum line such as a hazard is reachable); the recipe's
    // categoryLimits cap the total per family at `max`.
    const families = [];
    for (const section of GRAMMAR_SECTIONS) {
      for (const line of entry.grammar[section]) {
        if (line.max !== null && line.max <= line.min) continue;
        families.push(line.family);
      }
    }
    // An entry whose lines are all fixed-count (or light-plan only) leaves the density pass
    // NOTHING to draw: return the empty list, never the legacy fallback (Codex S2 review).
    return Object.freeze(families);
  }
  return GRAMMARS.unassigned;
}

/**
 * DAFT3D S2 — the adapter from a room-type catalog entry to a compileZoneGeneration() recipe.
 * The catalog is not the compiler's recipe (content plan §4.1): this expands the grammar into
 * request rules (family → candidate tags, the dressing pass draws from them by density) and
 * explicit supplemental requests (anchors first, then wall band, center, clutter, hazards, then
 * the light plan: max(min, round(perFt2 × area))). A toggle or a disabled preset capability
 * removes the whole section it owns (objects: every furniture section; lights: the light plan).
 * Facing / anchorRef / required ride on the requests for the S6 placer; S2's compiler ignores them.
 */
export function roomTypeToZoneRecipe({ entry: entryInput, preset, room, toggles = null } = {}) {
  const entry = typeof entryInput === 'string' ? roomTypeEntry(entryInput) : entryInput;
  if (!entry || !ROOM_TYPE_CATALOG.entryById.has(entry.id)) throw new Error('roomTypeToZoneRecipe needs a catalog entry');
  const resolvedPreset = preset ?? ROOM_PRESETS[entry.preset] ?? ROOM_PRESETS['legacy-structural'];
  const widthFt = room ? Math.max(0, room.x.max - room.x.min) : 0;
  const depthFt = room ? Math.max(0, room.y.max - room.y.min) : 0;
  const areaFt2 = widthFt * depthFt;
  const capabilityOn = key => {
    const capability = resolvedPreset.generation?.capabilities?.[key];
    if (capability && capability.enabled === false) return false;
    if (toggles && typeof toggles[key] === 'boolean') return toggles[key];
    if (typeof entry.toggles[key] === 'boolean') return entry.toggles[key];
    return resolvedPreset.generation?.defaults?.[key] !== false;
  };
  const tagsFor = families => [...new Set(families.flatMap(family => [family, canonicalZoneToken(family)]))].sort();
  const requestRules = {};
  const supplementalRequests = [];
  const droppedSections = [];
  const categoryLimits = {};
  for (const section of GRAMMAR_SECTIONS) {
    const capability = SECTION_CAPABILITY[section];
    if (!capabilityOn(capability)) {
      if (entry.grammar[section].length) droppedSections.push(section);
      // A dropped section's families get a zero budget so the seeded density pass cannot
      // request them either (the same filter that enforces `max`).
      entry.grammar[section].forEach(line => { categoryLimits[line.family] = 0; });
      continue;
    }
    for (const line of entry.grammar[section]) {
      const candidateTags = tagsFor(line.families);
      requestRules[line.family] = { outputLane: entry.lane, candidateTags };
      if (line.max !== null) categoryLimits[line.family] = Math.min(categoryLimits[line.family] ?? Infinity, line.max);
      for (let index = 0; index < line.min; index++) {
        supplementalRequests.push({
          id: `${entry.id}:${section}:${line.family}:${index + 1}`,
          category: line.family,
          outputLane: entry.lane,
          candidateTags,
          preferredZone: line.zone,
          section,
          capability,
          facing: line.facing,
          anchorRef: line.anchorRef,
          required: line.required,
        });
      }
    }
  }
  const lights = entry.grammar.lights;
  if (lights && capabilityOn('lights')) {
    const candidateTags = tagsFor(lights.families);
    requestRules[lights.family] = { outputLane: entry.lane, candidateTags };
    let count = Math.max(lights.min, Math.round(lights.perFt2 * areaFt2));
    if (lights.max !== null) {
      count = Math.min(count, lights.max);
      categoryLimits[lights.family] = Math.min(categoryLimits[lights.family] ?? Infinity, lights.max);
    }
    for (let index = 0; index < count; index++) {
      supplementalRequests.push({
        id: `${entry.id}:lights:${lights.family}:${index + 1}`,
        category: lights.family,
        outputLane: entry.lane,
        candidateTags,
        preferredZone: lights.zone,
        section: 'lights',
        capability: 'lights',
        facing: 'free',
        anchorRef: null,
        required: false,
      });
    }
  } else if (lights) {
    droppedSections.push('lights');
    categoryLimits[lights.family] = 0;
  }
  return {
    id: `room-type:${entry.id}`,
    version: ROOM_TYPE_CATALOG.revision,
    label: roomTypeLabel(entry.id),
    theme: entry.theme,
    roomFunction: entry.id,
    requestRules,
    supplementalRequests,
    categoryLimits,
    surfaceVariants: [...(resolvedPreset.surfaces?.eligible?.floor ?? [])],
    queryOnly: true,
    roomType: {
      id: entry.id,
      lane: entry.lane,
      areaFt2,
      droppedSections,
      fillPools: [...entry.containers.fillPools],
      emptyIsAuthored: entry.containers.emptyIsAuthored,
    },
  };
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function snap(value, gridFt) {
  return Math.round(value / gridFt) * gridFt;
}

function validId(value, fallback) {
  const normalized = String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '-');
  return (/^[a-z0-9_-]{1,32}$/.test(normalized) ? normalized : fallback).slice(0, 32);
}

function roomPreset(value) {
  return ROOM_PRESETS[value] ?? ROOM_PRESETS['legacy-structural'];
}

const ROOM_SURFACE_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,63}$/;
function normalizedRoomSurfaceOverrides(preset, value) {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  // DAFT3D S12: the Walls / Floors painters offer every APPROVED registry surface, not only the
  // preset's eligible list, so a well-formed stored id is kept as authored (the renderer resolves
  // it through the approved catalog and falls back for an unknown id); anything malformed
  // resolves to the preset default as before.
  const resolve = (role) => {
    const requested = typeof source[role] === 'string' ? source[role].trim() : '';
    return requested && (preset.surfaces.eligible[role]?.includes(requested) || ROOM_SURFACE_ID_PATTERN.test(requested))
      ? requested
      : preset.surfaces.defaults[role] ?? null;
  };
  return {
    floor: resolve('floor'),
    wall: resolve('wall'),
    ceiling: resolve('ceiling'),
    // Optional roof paint preserves existing floor-derived roofs when absent.
    ...(typeof source.roof === 'string' && ROOM_SURFACE_ID_PATTERN.test(source.roof.trim())
      ? { roof: source.roof.trim() } : {}),
  };
}

function legacyStoryBaseFt(storyAuthority, storyId) {
  const stories = Array.isArray(storyAuthority?.stories) ? storyAuthority.stories : [];
  const story = stories.find(candidate => candidate?.id === storyId)
    ?? stories.find(candidate => candidate?.index === 0);
  return finiteNumber(story?.baseElevationFt, 0);
}

function rotateArchitecturePointM(point, quarterTurns) {
  const [localX, localY, localZ] = point;
  const rotations = [
    [localX, localZ],
    [localZ, -localX],
    [-localX, -localZ],
    [-localZ, localX],
  ];
  const [x, y] = rotations[quarterTurns];
  return { x: x * FEET_PER_METER, y: y * FEET_PER_METER, z: localY * FEET_PER_METER };
}

export function verticalConnectionCompatibility(input) {
  const asset = input?.asset;
  const sourceRoom = input?.sourceRoom;
  const targetRoom = input?.targetRoom;
  const rotationQuarterTurns = ((Math.round(finiteNumber(input?.rotationQuarterTurns, 0)) % 4) + 4) % 4;
  const exactAnchorRequested = input?.anchorFt !== undefined;
  const requestedAnchorFt = input?.anchorFt
    && ['x', 'y', 'z'].every(axis => Number.isFinite(Number(input.anchorFt[axis])))
    ? {
        x: Number(input.anchorFt.x),
        y: Number(input.anchorFt.y),
        z: Number(input.anchorFt.z),
      }
    : null;
  const reasons = [];
  if (exactAnchorRequested && !requestedAnchorFt) reasons.push('anchor-invalid');
  const runtime = asset?.runtime;
  const runtimeValid = asset?.generationEnabled === false
    && asset?.authoringStatus === 'geometry_authored_review_pending'
    && runtime?.kind === 'glb'
    && /^https:\/\/cdn\.fatetide\.com\//.test(String(runtime?.url ?? ''))
    && /^[a-f0-9]{64}$/.test(String(runtime?.sha256 ?? '').toLowerCase())
    && Number.isSafeInteger(Number(runtime?.bytes)) && Number(runtime.bytes) > 0;
  if (!runtimeValid) reasons.push('runtime-contract-invalid');
  let geometry = null;
  try {
    geometry = normalizeVerticalConnectionGeometry(asset?.geometry);
  } catch {
    reasons.push('geometry-contract-invalid');
  }
  const route = geometry?.routes.find(candidate => candidate.kind === 'stair') ?? geometry?.routes[0] ?? null;
  const lower = geometry?.connectors.find(connector => connector.kind === 'lower_level') ?? null;
  const upper = geometry?.connectors.find(connector => connector.kind === 'upper_level') ?? null;
  const support = geometry?.footprints.support ?? [];
  const cutouts = geometry?.footprints.floorCutout ?? [];
  if (!route || !lower || !upper || support.length < 1 || cutouts.length < 1) {
    if (geometry) reasons.push('stair-geometry-incomplete');
  }
  if (!sourceRoom || !targetRoom) reasons.push('room-floor-context-missing');
  const floorDeltaFt = targetRoom && sourceRoom
    ? Number(targetRoom.floorFt ?? targetRoom.floor) - Number(sourceRoom.floorFt ?? sourceRoom.floor)
    : 0;
  const authoredLevelDeltaFt = route ? route.levelDeltaM * FEET_PER_METER : 0;
  const residualFt = floorDeltaFt - authoredLevelDeltaFt;
  if (floorDeltaFt <= EPS) reasons.push('floor-delta-not-positive');
  if (route && residualFt < -EPS) reasons.push('authored-rise-exceeds-floor-delta');
  if (route && residualFt > MAX_VERTICAL_CONNECTION_RESIDUAL_FT + EPS) {
    reasons.push('residual-exceeds-quarter-foot-policy');
  }
  let anchorFt = null;
  let candidateAnchorEvaluations = 0;
  if (route && cutouts.length && sourceRoom && targetRoom) {
    const rotatedCutout = cutouts[0].pointsM.map(point => rotateArchitecturePointM(point, rotationQuarterTurns));
    const cutoutCenter = rotatedCutout.reduce((sum, point) => ({
      x: sum.x + point.x / rotatedCutout.length,
      y: sum.y + point.y / rotatedCutout.length,
    }), { x: 0, y: 0 });
    const targetCenter = {
      x: (targetRoom.x.min + targetRoom.x.max) / 2,
      y: (targetRoom.y.min + targetRoom.y.max) / 2,
    };
    const routeBaseFt = Math.min(...route.centerlineM.map(point => (
      rotateArchitecturePointM(point, rotationQuarterTurns).z
    )));
    const centeredAnchorFt = {
      x: targetCenter.x - cutoutCenter.x,
      y: targetCenter.y - cutoutCenter.y,
      z: (sourceRoom.floorFt ?? sourceRoom.floor) - routeBaseFt,
    };
    const containmentReasons = candidateAnchor => {
      const failures = [];
      const transform = point => {
        const rotated = rotateArchitecturePointM(point, rotationQuarterTurns);
        return {
          x: candidateAnchor.x + rotated.x,
          y: candidateAnchor.y + rotated.y,
          z: candidateAnchor.z + rotated.z,
        };
      };
      if (!support.every(polygon => polygon.pointsM.every(point => {
        const transformed = transform(point);
        return roomContainsPoint(sourceRoom, transformed.x, transformed.y);
      }))) failures.push('support-outside-source-room');
      if (!route.centerlineM.every(point => {
        const transformed = transform(point);
        return roomContainsPoint(sourceRoom, transformed.x, transformed.y);
      })) failures.push('route-outside-source-room');
      if (!cutouts.every(polygon => polygon.pointsM.every(point => {
        const transformed = transform(point);
        return roomContainsPoint(targetRoom, transformed.x, transformed.y);
      }))) failures.push('cutout-outside-target-room');
      const transformedSupport = support.map(polygon => ({ pointsFt: polygon.pointsM.map(transform) }));
      const transformedCutouts = cutouts.map(polygon => ({ pointsFt: polygon.pointsM.map(transform) }));
      const stairwellContour = stairwellOpeningContour(transformedSupport, transformedCutouts);
      const clippedStairwell = clipConvexPolygon(stairwellContour, roomFootprintContour(targetRoom, 12));
      if (clippedStairwell.length < 3 || contourAreaFt2(clippedStairwell) < 20) {
        failures.push('stairwell-overlap-too-small');
      }
      const lowerPosition = transform(lower.positionM);
      const upperPosition = transform(upper.positionM);
      if (!roomContainsPoint(sourceRoom, lowerPosition.x, lowerPosition.y)) {
        failures.push('lower-endpoint-outside-source-room');
      }
      if (!roomContainsPoint(targetRoom, upperPosition.x, upperPosition.y)) {
        failures.push('upper-endpoint-outside-target-room');
      }
      return failures;
    };
    if (exactAnchorRequested) {
      if (requestedAnchorFt) {
        candidateAnchorEvaluations += 1;
        const exactReasons = containmentReasons(requestedAnchorFt);
        if (Math.abs(requestedAnchorFt.z - centeredAnchorFt.z) > EPS) {
          exactReasons.push('anchor-z-mismatch');
        }
        if (exactReasons.length === 0) anchorFt = requestedAnchorFt;
        else reasons.push(...exactReasons);
      }
    } else {
      const searchStepFt = 0.25;
      const maxRing = Math.floor((Math.sqrt(MAX_VERTICAL_CONNECTION_ANCHOR_EVALUATIONS) - 1) / 2);
      const offsets = [{ x: 0, y: 0 }];
      for (let ring = 1; ring <= maxRing; ring++) {
        for (let x = -ring; x <= ring; x++) {
          offsets.push({ x, y: -ring }, { x, y: ring });
        }
        for (let y = -ring + 1; y < ring; y++) {
          offsets.push({ x: -ring, y }, { x: ring, y });
        }
      }
      let centeredFailures = [];
      for (const offset of offsets.slice(0, MAX_VERTICAL_CONNECTION_ANCHOR_EVALUATIONS)) {
        const candidateAnchor = {
          x: centeredAnchorFt.x + offset.x * searchStepFt,
          y: centeredAnchorFt.y + offset.y * searchStepFt,
          z: centeredAnchorFt.z,
        };
        candidateAnchorEvaluations += 1;
        const candidateFailures = containmentReasons(candidateAnchor);
        if (offset.x === 0 && offset.y === 0) centeredFailures = candidateFailures;
        if (candidateFailures.length === 0) {
          anchorFt = candidateAnchor;
          break;
        }
      }
      if (!anchorFt) reasons.push(...centeredFailures);
    }
  }
  return {
    variant: String(asset?.variant ?? ''),
    compatible: reasons.length === 0,
    reasons: Array.from(new Set(reasons)),
    rotationQuarterTurns,
    anchorFt,
    facts: {
      floorDeltaFt,
      authoredLevelDeltaFt,
      residualFt,
      usableWidthFt: route ? route.usableWidthM * FEET_PER_METER : 0,
      supportPolygonCount: support.length,
      cutoutPolygonCount: cutouts.length,
      candidateAnchorEvaluations,
      candidateAnchorEvaluationLimit: MAX_VERTICAL_CONNECTION_ANCHOR_EVALUATIONS,
      anchorMode: exactAnchorRequested ? 'exact' : 'bounded-search',
      scale: 1,
      generationEnabled: false,
    },
    queryOnly: true,
    writes: 0,
  };
}

// Placement supports the existing Y-up mesh-local stair lane; the metadata contract is shared.
function normalizeVerticalConnectionGeometry(raw) {
  const geometry = normalizeArchitectureGeometry(raw);
  if (geometry.coordinateFrame.upAxis !== 'positive_y'
    || geometry.coordinateFrame.origin !== 'mesh_local'
    || geometry.measurement.status !== 'geometry_authored_review_pending'
    || !geometry.routes?.length || !geometry.connectors || geometry.connectors.length < 2
    || geometry.routes.some(route => !route.walkable)
    || !geometry.footprints?.floorCutout?.length) {
    throw new Error('vertical connection metadata requires supported routes, endpoints, and a floor cutout');
  }
  return geometry;
}

function normalizeVerticalConnections(input, rooms) {
  if (!Array.isArray(input)) return [];
  if (input.length > 16) throw new Error('creation draft supports at most 16 vertical connections');
  const roomById = new Map(rooms.map(room => [room.id, room]));
  const seen = new Set();
  return input.map((source, index) => {
    const id = validId(source?.id, `vertical-${index + 1}`);
    if (seen.has(id)) throw new Error(`vertical connection ${id} is duplicated`);
    seen.add(id);
    const sourceRoom = roomById.get(String(source?.sourceRoomId ?? ''));
    const targetRoom = roomById.get(String(source?.targetRoomId ?? ''));
    if (!sourceRoom || !targetRoom) throw new Error(`vertical connection ${id} references a missing room`);
    if (!(targetRoom.floorFt > sourceRoom.floorFt + EPS)) {
      throw new Error(`vertical connection ${id} must rise from a lower floor to a higher floor`);
    }
    const asset = source?.asset;
    const runtime = asset?.runtime;
    if (!asset || asset.generationEnabled !== false
      || asset.authoringStatus !== 'geometry_authored_review_pending'
      || runtime?.kind !== 'glb'
      || !/^https:\/\/cdn\.fatetide\.com\//.test(String(runtime.url ?? ''))
      || !/^[a-f0-9]{64}$/.test(String(runtime.sha256 ?? '').toLowerCase())
      || !Number.isSafeInteger(Number(runtime.bytes)) || Number(runtime.bytes) < 1) {
      throw new Error(`vertical connection ${id} requires an approved generation-disabled CDN GLB`);
    }
    const anchor = source?.anchorFt ?? {};
    if (![anchor.x, anchor.y, anchor.z].every(value => Number.isFinite(Number(value)))) {
      throw new Error(`vertical connection ${id} requires a finite anchor`);
    }
    const rotationQuarterTurns = ((Math.round(finiteNumber(source.rotationQuarterTurns, 0)) % 4) + 4) % 4;
    const scaleXYZ = source.scaleXYZ ?? [1, 1, 1];
    if (!Array.isArray(scaleXYZ) || scaleXYZ.length !== 3
      || !scaleXYZ.every(n => typeof n === 'number' && Number.isFinite(n) && n >= 0.05 && n <= 24)) {
      throw new Error(`vertical connection ${id} requires three finite axis scales`);
    }
    return {
      id,
      kind: 'stair',
      sourceRoomId: sourceRoom.id,
      targetRoomId: targetRoom.id,
      fromFloorFt: sourceRoom.floorFt,
      toFloorFt: targetRoom.floorFt,
      anchorFt: { x: Number(anchor.x), y: Number(anchor.y), z: Number(anchor.z) },
      rotationQuarterTurns,
      scaleXYZ: [...scaleXYZ],
      traversalEnabled: source.traversalEnabled === true,
      asset: {
        meshId: String(asset.meshId ?? '').slice(0, 36),
        variant: String(asset.variant ?? '').slice(0, 128),
        label: String(asset.label ?? 'Stairs').slice(0, 128),
        runtime: {
          kind: 'glb',
          url: String(runtime.url),
          sha256: String(runtime.sha256).toLowerCase(),
          bytes: Number(runtime.bytes),
        },
        generationEnabled: false,
        authoringStatus: 'geometry_authored_review_pending',
        geometry: normalizeVerticalConnectionGeometry(asset.geometry),
      },
      queryOnly: true,
      persistence: 'none',
    };
  });
}

function normalizeDiscreteLadderConnections(input, rooms) {
  if (!Array.isArray(input)) return [];
  if (input.length > 16) throw new Error('creation draft supports at most 16 discrete ladder connections');
  const roomById = new Map(rooms.map(room => [room.id, room]));
  const seen = new Set();
  return input.map((source, index) => {
    const id = validId(source?.id, `ladder-${index + 1}`);
    if (seen.has(id)) throw new Error(`discrete ladder connection ${id} is duplicated`);
    seen.add(id);
    const sourceRoom = roomById.get(String(source?.sourceRoomId ?? ''));
    const targetRoom = roomById.get(String(source?.targetRoomId ?? ''));
    if (!sourceRoom || !targetRoom) throw new Error(`discrete ladder connection ${id} references a missing room`);
    if (!(targetRoom.floorFt > sourceRoom.floorFt + EPS)) {
      throw new Error(`discrete ladder connection ${id} must rise from a lower floor to a higher floor`);
    }
    const anchor = source?.anchorFt ?? {};
    const riseFt = finiteNumber(source?.riseFt, Number.NaN);
    // Legacy (pre-2026-08-29) rows carried only the source anchor + rise and none of the
    // relocation-contract flags (Echoes of Destiny's stored shape). Upgrade them in place:
    // the target anchor sits directly above the source anchor by the authored rise. Rows
    // that DO carry any of the contract fields are held to the full strict contract.
    const legacyRow = !!source && typeof source === 'object'
      && !['targetAnchorFt', 'transferMode', 'gmControl', 'seamlessVerticalMovement']
        .some(key => Object.hasOwn(source, key));
    const targetAnchor = legacyRow && riseFt > 0
      ? { x: anchor.x, y: anchor.y, z: finiteNumber(anchor.z, Number.NaN) + riseFt }
      : (source?.targetAnchorFt ?? {});
    if (![anchor.x, anchor.y, anchor.z, targetAnchor.x, targetAnchor.y, targetAnchor.z]
      .every(value => Number.isFinite(Number(value)))) {
      throw new Error(`discrete ladder connection ${id} requires finite source and target anchors`);
    }
    if (!(riseFt > 0) || Math.abs((Number(targetAnchor.z) - Number(anchor.z)) - riseFt) > 0.25) {
      throw new Error(`discrete ladder connection ${id} rise must match its authored anchor delta`);
    }
    if (source?.queryOnly !== true || (!legacyRow && (
      source?.transferMode !== 'discrete-room-relocation'
      || source?.gmControl !== 'move-party-to-room'
      || source?.seamlessVerticalMovement !== false))) {
      throw new Error(`discrete ladder connection ${id} violated its query-only relocation contract`);
    }
    return {
      id,
      itemId: String(source?.itemId ?? '').slice(0, 36),
      fromFloorFt: sourceRoom.floorFt,
      toFloorFt: targetRoom.floorFt,
      sourceRoomId: sourceRoom.id,
      targetRoomId: targetRoom.id,
      anchorFt: { x: Number(anchor.x), y: Number(anchor.y), z: Number(anchor.z) },
      targetAnchorFt: { x: Number(targetAnchor.x), y: Number(targetAnchor.y), z: Number(targetAnchor.z) },
      riseFt,
      transferMode: 'discrete-room-relocation',
      gmControl: 'move-party-to-room',
      seamlessVerticalMovement: false,
      queryOnly: true,
    };
  });
}

function doorIntent(value) {
  return DOOR_INTENT_CATALOG.find(candidate => candidate.id === value) ?? DOOR_INTENT_CATALOG[0];
}

function normalizedRoomFootprint(source, x, y) {
  const width = x.max - x.min;
  const depth = y.max - y.min;
  const maximumRadiusFt = Math.max(0, Math.min(width, depth));
  const requestedRadiusFt = source?.kind === 'rounded-rect'
    ? finiteNumber(source.cornerRadiusFt, Math.min(10, maximumRadiusFt / 2))
    : 0;
  const requested = source?.kind === 'rounded-rect' && source.cornerRadiiFt
    ? source.cornerRadiiFt
    : {};
  const cornerRadiiFt = {
    southWest: clamp(finiteNumber(requested.southWest, requestedRadiusFt), 0, maximumRadiusFt),
    southEast: clamp(finiteNumber(requested.southEast, requestedRadiusFt), 0, maximumRadiusFt),
    northEast: clamp(finiteNumber(requested.northEast, requestedRadiusFt), 0, maximumRadiusFt),
    northWest: clamp(finiteNumber(requested.northWest, requestedRadiusFt), 0, maximumRadiusFt),
  };
  const ratios = [
    [width, cornerRadiiFt.southWest + cornerRadiiFt.southEast],
    [width, cornerRadiiFt.northWest + cornerRadiiFt.northEast],
    [depth, cornerRadiiFt.southWest + cornerRadiiFt.northWest],
    [depth, cornerRadiiFt.southEast + cornerRadiiFt.northEast],
  ].filter(([, sum]) => sum > EPS).map(([span, sum]) => span / sum);
  const scale = Math.min(1, ...ratios);
  Object.keys(cornerRadiiFt).forEach(key => { cornerRadiiFt[key] *= scale; });
  const cornerRadiusFt = Math.max(...Object.values(cornerRadiiFt));
  return cornerRadiusFt > EPS
    ? { kind: 'rounded-rect', cornerRadiusFt, cornerRadiiFt }
    : {
      kind: 'rect',
      cornerRadiusFt: 0,
      cornerRadiiFt: { southWest: 0, southEast: 0, northEast: 0, northWest: 0 },
    };
}

export function roomCornerRadii(room) {
  return { ...normalizedRoomFootprint(room.footprint, room.x, room.y).cornerRadiiFt };
}

export function roomContainsPoint(room, x, y) {
  if (x < room.x.min - EPS || x > room.x.max + EPS
    || y < room.y.min - EPS || y > room.y.max + EPS) return false;
  const footprint = normalizedRoomFootprint(room.footprint, room.x, room.y);
  if (footprint.kind === 'rect') return true;
  const tests = [
    { radius: footprint.cornerRadiiFt.southWest, cornerX: room.x.min, cornerY: room.y.min, signX: 1, signY: 1 },
    { radius: footprint.cornerRadiiFt.southEast, cornerX: room.x.max, cornerY: room.y.min, signX: -1, signY: 1 },
    { radius: footprint.cornerRadiiFt.northEast, cornerX: room.x.max, cornerY: room.y.max, signX: -1, signY: -1 },
    { radius: footprint.cornerRadiiFt.northWest, cornerX: room.x.min, cornerY: room.y.max, signX: 1, signY: -1 },
  ];
  for (const test of tests) {
    if (test.radius <= EPS) continue;
    const centerX = test.cornerX + test.signX * test.radius;
    const centerY = test.cornerY + test.signY * test.radius;
    const withinCornerSquare = test.signX * (x - centerX) < 0 && test.signY * (y - centerY) < 0;
    if (withinCornerSquare
      && (x - centerX) ** 2 + (y - centerY) ** 2 > test.radius ** 2 + EPS) return false;
  }
  return true;
}

export function roomContainsRectangle(room, rectangle) {
  return [
    [rectangle.minX, rectangle.minY],
    [rectangle.maxX, rectangle.minY],
    [rectangle.maxX, rectangle.maxY],
    [rectangle.minX, rectangle.maxY],
  ].every(([x, y]) => roomContainsPoint(room, x, y));
}

export function roomFootprintContour(room, arcSegments = 8) {
  const footprint = normalizedRoomFootprint(room.footprint, room.x, room.y);
  if (footprint.kind === 'rect') {
    return [
      { x: room.x.min, y: room.y.min },
      { x: room.x.max, y: room.y.min },
      { x: room.x.max, y: room.y.max },
      { x: room.x.min, y: room.y.max },
    ];
  }
  const segments = clamp(Math.round(finiteNumber(arcSegments, 8)), 2, 32);
  const corners = [
    { radius: footprint.cornerRadiiFt.southEast, x: room.x.max, y: room.y.min, signX: -1, signY: 1, start: -Math.PI / 2 },
    { radius: footprint.cornerRadiiFt.northEast, x: room.x.max, y: room.y.max, signX: -1, signY: -1, start: 0 },
    { radius: footprint.cornerRadiiFt.northWest, x: room.x.min, y: room.y.max, signX: 1, signY: -1, start: Math.PI / 2 },
    { radius: footprint.cornerRadiiFt.southWest, x: room.x.min, y: room.y.min, signX: 1, signY: 1, start: Math.PI },
  ];
  const points = corners.flatMap(corner => Array.from({ length: segments + 1 }, (_, index) => {
    if (corner.radius <= EPS) return { x: corner.x, y: corner.y };
    const angle = corner.start + index / segments * Math.PI / 2;
    const centerX = corner.x + corner.signX * corner.radius;
    const centerY = corner.y + corner.signY * corner.radius;
    return { x: centerX + Math.cos(angle) * corner.radius, y: centerY + Math.sin(angle) * corner.radius };
  })).filter((point, index, source) => index === 0 || (
    Math.abs(point.x - source[index - 1].x) > EPS
    || Math.abs(point.y - source[index - 1].y) > EPS
  ));
  if (points.length > 1
    && Math.abs(points[0].x - points[points.length - 1].x) <= EPS
    && Math.abs(points[0].y - points[points.length - 1].y) <= EPS) points.pop();
  return points;
}

export function roomFootprintsOverlap(left, right) {
  if (left.x.min >= right.x.max - EPS || left.x.max <= right.x.min + EPS
    || left.y.min >= right.y.max - EPS || left.y.max <= right.y.min + EPS) return false;
  const polygons = [roomFootprintContour(left), roomFootprintContour(right)];
  const axes = polygons.flatMap(points => points.map((point, index) => {
    const next = points[(index + 1) % points.length];
    const edgeX = next.x - point.x;
    const edgeY = next.y - point.y;
    const length = Math.hypot(edgeX, edgeY) || 1;
    return { x: -edgeY / length, y: edgeX / length };
  }));
  return axes.every(axis => {
    const project = points => points.reduce((range, point) => {
      const value = point.x * axis.x + point.y * axis.y;
      return { min: Math.min(range.min, value), max: Math.max(range.max, value) };
    }, { min: Infinity, max: -Infinity });
    const a = project(polygons[0]);
    const b = project(polygons[1]);
    return a.min < b.max - EPS && a.max > b.min + EPS;
  });
}

/** Plan proximity is independent of elevation. Rounded corners use the real contour. */
export function roomsTouchInPlan(left, right, gridFt = DEFAULT_GRID_FT) {
  if (!left || !right) return false;
  if (roomFootprintsOverlap(left, right)) return true;
  const a = roomFootprintContour(left, 16);
  const b = roomFootprintContour(right, 16);
  const distanceToEdge = (point, start, end) => {
    const dx = end.x - start.x, dy = end.y - start.y;
    const t = clamp(((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy || 1), 0, 1);
    return Math.hypot(point.x - start.x - t * dx, point.y - start.y - t * dy);
  };
  const distance = (first, second) => Math.min(...first.flatMap(point => second.map((start, index) => (
    distanceToEdge(point, start, second[(index + 1) % second.length])
  ))));
  return Math.min(distance(a, b), distance(b, a)) <= gridFt + EPS;
}

/** Resolve a member's chain without changing any room's content identity. */
export function resolveRoomPlace(rooms, roomId) {
  // Normalized links are one level; ordinary room lookups need no index allocation.
  let room = rooms.find(room => room.id === (typeof roomId === 'string' ? roomId : roomId?.id)) ?? null;
  const visited = new Set();
  while (room?.mergedInto && !visited.has(room.id)) {
    visited.add(room.id);
    room = rooms.find(candidate => candidate.id === room.mergedInto) ?? room;
  }
  return room;
}

function normalizeRoomMerges(rooms, gridFt) {
  if (!rooms.some(room => room.mergedInto)) return [];
  const byId = new Map(rooms.map(room => [room.id, room]));
  // Reuse this pass's index while validating authored chains.
  const primaryFor = start => {
    let room = start;
    while (room.mergedInto) room = byId.get(room.mergedInto);
    return room;
  };
  const warnings = [];
  const drop = (room, reason) => {
    warnings.push({ roomId: room.id, targetRoomId: room.mergedInto, reason });
    room.mergedInto = null;
  };
  for (const room of rooms) {
    if (!room.mergedInto) continue;
    if (room.mergedInto === room.id) drop(room, 'self');
    else if (!byId.has(room.mergedInto)) drop(room, 'missing');
  }
  const done = new Set();
  for (const start of rooms) {
    const path = [], indices = new Map();
    let room = start;
    while (room?.mergedInto && !done.has(room.id)) {
      if (indices.has(room.id)) {
        path.slice(indices.get(room.id)).forEach(member => drop(member, 'cycle'));
        break;
      }
      indices.set(room.id, path.length);
      path.push(room);
      room = byId.get(room.mergedInto);
    }
    path.forEach(member => done.add(member.id));
  }
  // A member may touch any room already connected to its primary, not necessarily its
  // declared target. Cut only the link that disconnects a whole remote branch.
  let changed = true;
  while (changed) {
    changed = false;
    const groups = new Map();
    for (const room of rooms) {
      const primary = primaryFor(room);
      if (!groups.has(primary)) groups.set(primary, []);
      groups.get(primary).push(room);
    }
    for (const [primary, group] of groups) {
      if (group.length === 1) continue;
      const connected = new Set([primary.id]);
      let grew = true;
      while (grew) {
        grew = false;
        for (const member of group.filter(room => !connected.has(room.id))) {
          if (group.some(other => connected.has(other.id) && roomsTouchInPlan(member, other, gridFt))) {
            connected.add(member.id);
            grew = true;
          }
        }
      }
      for (const member of group.filter(room => room.mergedInto && !connected.has(room.id)
        && connected.has(room.mergedInto))) {
        drop(member, 'not-touching');
        changed = true;
      }
    }
  }
  for (const room of rooms) {
    if (room.mergedInto) room.mergedInto = primaryFor(room).id;
  }
  return warnings;
}

/** Validate a proposed link set without cloning room geometry or mutating the caller. */
export function roomMergeLinks(rooms, gridFt = DEFAULT_GRID_FT) {
  const links = rooms.map(room => Object.assign(Object.create(room), {
    id: room.id, mergedInto: room.mergedInto ?? null,
  }));
  const warnings = normalizeRoomMerges(links, gridFt);
  return { links: links.map(room => ({ roomId: room.id, mergedInto: room.mergedInto })), warnings };
}

function cloneOpening(opening) {
  const cloned = {
    id: opening.id,
    plane: { axis: opening.plane.axis, at: opening.plane.at },
    span: { min: opening.span.min, max: opening.span.max },
    z: { min: opening.z.min, max: opening.z.max },
    passable: opening.passable !== false,
    transparent: opening.transparent !== false,
  };
  if (opening.passesLight !== undefined) cloned.passesLight = opening.passesLight;
  if (['intact', 'damaged', 'cleared', 'destroyed'].includes(opening.structureState)) {
    cloned.structureState = opening.structureState;
  }
  if (opening.coveringState) cloned.coveringState = JSON.parse(JSON.stringify(opening.coveringState));
  if (opening.creation && typeof opening.creation === 'object') {
    cloned.creation = JSON.parse(JSON.stringify(opening.creation));
    delete cloned.creation.storyId;
    if (cloned.creation.wallBreach && typeof cloned.creation.wallBreach === 'object') {
      delete cloned.creation.wallBreach.storyId;
    }
  }
  return cloned;
}

function normalizeCreationOpening(opening, rooms) {
  const cloned = cloneOpening(opening);
  if (!cloned.creation) return cloned;
  const roomIds = Array.isArray(cloned.creation.rooms) ? cloned.creation.rooms.map(String) : [];
  const crossFloorShared = (cloned.creation.mergedPlace === true || cloned.creation.crossFloorShared === true)
    && new Set(rooms.filter(candidate => roomIds.includes(candidate.id)).map(candidate => candidate.floorFt)).size > 1;
  if (crossFloorShared) delete cloned.creation.floorFt;
  const room = rooms.find(candidate => roomIds.includes(candidate.id))
    ?? rooms.find(candidate => cloned.z.max > candidate.floor + EPS
      && cloned.z.min < candidate.ceiling - EPS);
  const floorFt = finiteNumber(cloned.creation.floorFt, room?.floorFt);
  if (!crossFloorShared && Number.isFinite(floorFt)) cloned.creation.floorFt = floorFt;
  if (cloned.creation.wallBreach?.runKey && Number.isFinite(floorFt)) {
    const parts = String(cloned.creation.wallBreach.runKey).split(':');
    if (parts.length >= 5 && !Number.isFinite(Number(parts[0]))) {
      cloned.creation.wallBreach.runKey = [numberKey(floorFt), ...parts.slice(1)].join(':');
    }
  }
  return cloned;
}

function stripLegacyStoryFields(value) {
  if (Array.isArray(value)) return value.map(stripLegacyStoryFields);
  if (!value || typeof value !== 'object') return value;
  const removed = new Set([
    'storyAuthority', 'storyId', 'sourceStoryId', 'targetStoryId', 'activeStoryId', 'activeStoryIds',
  ]);
  return Object.fromEntries(Object.entries(value)
    .filter(([key]) => !removed.has(key))
    .map(([key, nested]) => [key, stripLegacyStoryFields(nested)]));
}

function normalizeBuildingRoofs(input, rooms) {
  const source = input && typeof input === 'object' ? input : {};
  return Object.fromEntries(Object.entries(source).map(([key, raw]) => {
    const row = stripLegacyStoryFields(raw);
    if (!row || typeof row !== 'object' || Array.isArray(row) || Number.isFinite(row.z)) return [key, row];
    const targetRoom = rooms.find(room => room.id === raw?.targetRoomId);
    const sourceRoom = rooms.find(room => room.id === raw?.sourceRoomId || room.id === raw?.roomId);
    const z = targetRoom?.floorFt ?? sourceRoom?.physicalCeilingFt;
    return [key, Number.isFinite(z) ? { ...row, z } : row];
  }));
}

/**
 * DAFT3D S8: the room's persisted generation INTENT (`boxes[].creation.generation`), so a reload
 * replays the same layout: type, seed, density, toggles, recipe id/version, catalog revision,
 * refresh nonce and suppressed request ids. Null when the room was never generated.
 */
export const ROOM_GENERATION_INTENT_SCHEMA = 'ft.room-generation-intent.v1';
const ROOM_GENERATION_TOGGLE_KEYS = Object.freeze(['loot', 'lights', 'walls', 'windows', 'curtains', 'doors', 'tiles', 'roofs', 'objects']);
export function normalizeRoomGenerationIntent(input) {
  if (!input || typeof input !== 'object') return null;
  if (input.schema !== undefined && input.schema !== ROOM_GENERATION_INTENT_SCHEMA) return null;
  const roomTypeId = typeof input.roomTypeId === 'string' ? input.roomTypeId.trim() : '';
  if (!roomTypeId) return null;
  const toggles = {};
  const sourceToggles = input.toggles && typeof input.toggles === 'object' ? input.toggles : {};
  ROOM_GENERATION_TOGGLE_KEYS.forEach(key => {
    if (Object.prototype.hasOwnProperty.call(sourceToggles, key)) toggles[key] = Boolean(sourceToggles[key]);
  });
  const suppressed = Array.isArray(input.suppressedRequestIds)
    ? [...new Set(input.suppressedRequestIds.filter(value => typeof value === 'string' && value.trim()).map(value => value.trim()))].sort()
    : [];
  return {
    schema: ROOM_GENERATION_INTENT_SCHEMA,
    roomTypeId,
    seed: typeof input.seed === 'string' ? input.seed : '',
    density: clamp(Math.round(finiteNumber(input.density, 0)), 0, 3),
    toggles,
    recipeId: typeof input.recipeId === 'string' ? input.recipeId : '',
    recipeVersion: typeof input.recipeVersion === 'string' ? input.recipeVersion : '',
    catalogRevision: typeof input.catalogRevision === 'string' ? input.catalogRevision : '',
    refreshNonce: Math.max(0, Math.round(finiteNumber(input.refreshNonce, 0))),
    suppressedRequestIds: suppressed,
  };
}

function roomFromBox(box, index, gridFt, legacyStoryAuthority = null, importingShape = false) {
  const metadata = box.creation && typeof box.creation === 'object' ? box.creation : {};
  const roomFunction = ROOM_FUNCTIONS.includes(metadata.function) ? metadata.function : 'unassigned';
  const requestedPresetId = typeof box.presetId === 'string' ? box.presetId : metadata.presetId;
  const preset = roomPreset(requestedPresetId);
  const presetOwned = preset.id !== 'legacy-structural';
  // A preset owns the kind of enclosure it can generate, but DA's Walls switch
  // may deliberately suppress that enclosure. Read both live-draft and stored
  // creation metadata so the no-walls choice survives compile and re-import.
  const requestedEnclosure = box.enclosure ?? metadata.enclosure;
  const enclosure = preset.enclosure === 'none' || requestedEnclosure === 'none'
    ? 'none'
    : preset.enclosure;
  // New drafts author the physical room floor directly. Legacy story-shaped input is accepted only
  // as an intake bridge; it is converted to feet here and never copied into normalized output.
  const requestedStoryId = typeof box.storyId === 'string' ? box.storyId : metadata.storyId;
  const legacyFloorFt = legacyStoryBaseFt(legacyStoryAuthority, requestedStoryId)
    + finiteNumber(preset.structural.floorFt, 0);
  // Persisted box coordinates are gameplay authority. Authoring hints may lag a server-side
  // geometry edit; they must never move an imported box back to an earlier elevation.
  const floor = finiteNumber(importingShape ? box.floor : box.floorFt ?? metadata.floorFt ?? box.floor, legacyFloorFt);
  // Older saved preset rooms predate the explicit override field. Preserve their
  // authored height on intake; modern null uses the room clear-height default.
  const storedClearHeight = Number.isFinite(box.ceiling) && Number.isFinite(box.floor)
    ? box.ceiling - box.floor : null;
  const explicitClearHeightFt = box.clearHeightFt ?? metadata.clearHeightFt;
  const clearHeightOverrideFt = presetOwned && preset.structural.ceilingFt !== null
    && metadata.clearHeightOverrideFt !== null
    && metadata.clearHeightOverrideFt !== undefined
    && metadata.clearHeightOverrideFt !== ''
    && Number.isFinite(Number(metadata.clearHeightOverrideFt))
    ? clamp(Number(metadata.clearHeightOverrideFt), 5, 100)
    : null;
  const requestedClearHeightFt = [explicitClearHeightFt, storedClearHeight, clearHeightOverrideFt]
    .find(value => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)));
  const clearHeightFt = preset.structural.ceilingFt === null ? null : clamp(finiteNumber(
    requestedClearHeightFt,
    DEFAULT_CLEAR_HEIGHT_FT,
  ), 5, 100);
  const physicalCeilingFt = presetOwned
    ? (preset.structural.ceilingFt === null
      ? null
      : floor + clearHeightFt)
    : finiteNumber(box.physicalCeilingFt ?? box.ceiling, preset.structural.ceilingFt);
  const ceiling = physicalCeilingFt === null
    ? preset.structural.compatibilityCeilingFt
    : physicalCeilingFt;
  const x = {
    // Preserve imported authored geometry exactly. The interactive drawing
    // surface snaps NEW edits; normalization must not silently move legacy
    // faces away from their declared openings.
    min: finiteNumber(box.x?.min, index * gridFt),
    max: finiteNumber(box.x?.max, (index + 4) * gridFt),
  };
  const y = {
    min: finiteNumber(box.y?.min, 0),
    max: finiteNumber(box.y?.max, 4 * gridFt),
  };
  const savedGeneration = metadata.generation;
  const generation = normalizeRoomGenerationIntent(importingShape
    && savedGeneration?.schema === ROOM_GENERATION_INTENT_SCHEMA
    && !Object.prototype.hasOwnProperty.call(savedGeneration, 'roomTypeId')
    ? { ...savedGeneration, roomTypeId: roomFunction }
    : savedGeneration);
  return {
    id: validId(box.id, `room-${index + 1}`),
    mergedInto: typeof metadata.mergedInto === 'string' && metadata.mergedInto.trim()
      ? metadata.mergedInto.trim() : null,
    floorFt: floor,
    x,
    y,
    footprint: normalizedRoomFootprint(box.footprint ?? metadata.footprint, x, y),
    presetId: preset.id,
    ...(['earth', 'stone', 'worked'].includes(metadata.solidMaterial) ? { solidMaterial: metadata.solidMaterial } : {}),
    environment: presetOwned ? preset.environment : (box.environment ?? preset.environment),
    enclosure,
    floor,
    ceiling,
    physicalCeilingFt,
    clearHeightFt: physicalCeilingFt === null ? null : physicalCeilingFt - floor,
    clearHeightOverrideFt,
    surfaceOverrides: normalizedRoomSurfaceOverrides(preset, metadata.surfaceOverrides),
    interior: presetOwned ? preset.interior : (typeof box.interior === 'string' ? box.interior : preset.interior),
    function: roomFunction,
    density: clamp(Math.round(finiteNumber(metadata.density, 1)), 0, 3),
    locked: metadata.locked === true,
    // Author flag (owner rule 2026-08-27): the roof was REMOVED — the room is open to the sky for
    // gameplay light and casts no roof shadow, while walls/enclosure/environment stay preset-owned.
    // Meaningful on interior rooms only (latent — kept, not cleared — while the preset is exterior).
    ambience: metadata.ambience && typeof metadata.ambience === 'object' ? normalizeRoomAmbience(metadata.ambience) : null,
    climate: metadata.climate && typeof metadata.climate === 'object'
      ? { temperature: isRoomAmbienceUuid(metadata.climate.temperature) ? metadata.climate.temperature.toUpperCase() : 'inherit' } : null,
    roofRemoved: metadata.roofRemoved === true,
    ...(metadata.exteriorShell === true ? { exteriorShell: true } : {}),
    ...(metadata.renderSurfaces === false ? { renderSurfaces: false } : {}),
    // Declarative wall sconces (2026-08-28): 'sconces' | 'none'; positions derive from the box (shape core).
    lightFixtures: metadata.lightFixtures === 'sconces' ? 'sconces' : 'none',
    // DAFT3D S26b: walls the GM ruled "no generated windows here", by SIDE, so the ruling survives a move.
    windowSuppressedFaces: normalizeWindowSuppressedFaces(metadata.windowSuppressedFaces),
    generation,
  };
}

/**
 * DAFT3D S26b — the four sides of a room a GM can rule "no generated windows here". A side, not a
 * coordinate: `x:min` is the west wall wherever the room is moved or resized. Unknown keys are dropped.
 */
export const WINDOW_FACE_KEYS = Object.freeze(['x:min', 'x:max', 'y:min', 'y:max']);

function normalizeWindowSuppressedFaces(value) {
  if (!Array.isArray(value)) return [];
  return WINDOW_FACE_KEYS.filter(key => value.includes(key));
}

/** Which side of `room` the wall plane `axis = at` is, or null when it is none of them. */
export function windowFaceKey(room, axis, at) {
  if ((axis !== 'x' && axis !== 'y') || !room?.[axis]) return null;
  if (same(at, room[axis].min)) return `${axis}:min`;
  if (same(at, room[axis].max)) return `${axis}:max`;
  return null;
}

function normalizeCanvas(input, rooms, gridFt) {
  const hasRooms = rooms.length > 0;
  const fallback = {
    x: {
      min: hasRooms
        ? Math.floor((Math.min(...rooms.map(room => room.x.min))
          - DERIVED_AUTHORING_ENVELOPE_PADDING_FT) / gridFt) * gridFt
        : -gridFt * 10,
      max: hasRooms
        ? Math.ceil((Math.max(...rooms.map(room => room.x.max))
          + DERIVED_AUTHORING_ENVELOPE_PADDING_FT) / gridFt) * gridFt
        : gridFt * 10,
    },
    y: {
      min: hasRooms
        ? Math.floor((Math.min(...rooms.map(room => room.y.min))
          - DERIVED_AUTHORING_ENVELOPE_PADDING_FT) / gridFt) * gridFt
        : -gridFt * 10,
      max: hasRooms
        ? Math.ceil((Math.max(...rooms.map(room => room.y.max))
          + DERIVED_AUTHORING_ENVELOPE_PADDING_FT) / gridFt) * gridFt
        : gridFt * 10,
    },
    environment: 'interior',
  };
  const source = input && typeof input === 'object' && !Array.isArray(input) ? input : fallback;
  const canvas = {
    x: {
      min: snap(finiteNumber(source.x?.min, fallback.x.min), gridFt),
      max: snap(finiteNumber(source.x?.max, fallback.x.max), gridFt),
    },
    y: {
      min: snap(finiteNumber(source.y?.min, fallback.y.min), gridFt),
      max: snap(finiteNumber(source.y?.max, fallback.y.max), gridFt),
    },
    environment: source.environment === 'exterior-open' ? 'exterior-open' : 'interior',
  };
  if (!(canvas.x.max > canvas.x.min) || !(canvas.y.max > canvas.y.min)) {
    throw new Error('creation canvas must have positive X/Y extent');
  }
  for (const room of rooms) {
    if (room.x.min < canvas.x.min - EPS || room.x.max > canvas.x.max + EPS
      || room.y.min < canvas.y.min - EPS || room.y.max > canvas.y.max + EPS) {
      throw new Error(`room ${room.id} must remain inside the X/Y canvas`);
    }
  }
  return canvas;
}

export function creationDraftFromShape(shape, { seed = 'room-1', gridFt = DEFAULT_GRID_FT } = {}) {
  validateShape(shape);
  const authoring = shape.authoring && typeof shape.authoring === 'object' ? shape.authoring : {};
  const draftGrid = clamp(Math.round(finiteNumber(authoring.gridFt, gridFt)), 5, 20);
  return normalizeCreationDraft({
    schema: CREATION_SCHEMA,
    seed: typeof authoring.seed === 'string' && authoring.seed ? authoring.seed : seed,
    gridFt: draftGrid,
    canvas: authoring.canvas,
    rooms: shape.boxes.map((box, index) => roomFromBox(box, index, draftGrid, authoring.storyAuthority, true)),
    verticalConnections: authoring.verticalConnections,
    ladderConnections: authoring.ladderConnections,
    manualOpenings: (shape.openings ?? []).map(cloneOpening),
    // Persisted apertures are already authored, including earlier generated ones.
    // Editing/reloading must not run today's presets over that saved geometry.
    generateOpenings: false,
    connectionOverrides: authoring.connectionOverrides,
    architecturalDressing: authoring.architecturalDressing,
    buildingRoofs: authoring.buildingRoofs,
  });
}

const ARCHITECTURAL_DRESSING_KINDS = new Set([
  'column', 'platform', 'rug', 'planter', 'mist-pool', 'treasure', 'table', 'bench',
  'bed', 'shelf', 'cabinet', 'throne', 'tub', 'brazier', 'statue', 'mat',
]);
const ARCHITECTURAL_DRESSING_PALETTES = new Set([
  'stone', 'lapis', 'gold', 'wood', 'crimson', 'verdant', 'mist', 'bronze', 'ivory', 'charcoal',
]);

function normalizeArchitecturalDressing(input, rooms) {
  if (!Array.isArray(input)) return [];
  if (input.length > 512) throw new Error('architectural dressing requires 0-512 rows');
  const roomById = new Map(rooms.map(room => [room.id, room]));
  const seen = new Set();
  return input.map((source, index) => {
    if (!source || typeof source !== 'object' || Array.isArray(source)) {
      throw new Error(`architectural dressing row ${index + 1} must be an object`);
    }
    const id = validId(source.id, `architectural-dressing-${index + 1}`);
    if (seen.has(id)) throw new Error(`architectural dressing id must be unique: ${id}`);
    seen.add(id);
    const roomId = String(source.roomId ?? '');
    const room = roomById.get(roomId);
    if (!room) throw new Error(`architectural dressing ${id} references unknown room ${roomId}`);
    const kind = String(source.kind ?? 'platform');
    if (!ARCHITECTURAL_DRESSING_KINDS.has(kind)) {
      throw new Error(`architectural dressing ${id} has unsupported kind ${kind}`);
    }
    const positionFt = {
      x: finiteNumber(source.positionFt?.x, Number.NaN),
      y: finiteNumber(source.positionFt?.y, Number.NaN),
      z: finiteNumber(source.positionFt?.z, room.floor),
    };
    const sizeFt = {
      x: clamp(finiteNumber(source.sizeFt?.x, 5), 0.5, 200),
      y: clamp(finiteNumber(source.sizeFt?.y, 5), 0.5, 200),
      z: clamp(finiteNumber(source.sizeFt?.z, 1), 0.05, 30),
    };
    if (!Number.isFinite(positionFt.x) || !Number.isFinite(positionFt.y) || !Number.isFinite(positionFt.z)) {
      throw new Error(`architectural dressing ${id} requires finite positionFt coordinates`);
    }
    if (positionFt.x < room.x.min - EPS || positionFt.x > room.x.max + EPS
      || positionFt.y < room.y.min - EPS || positionFt.y > room.y.max + EPS) {
      throw new Error(`architectural dressing ${id} origin must remain inside room ${roomId}`);
    }
    const palette = ARCHITECTURAL_DRESSING_PALETTES.has(String(source.palette))
      ? String(source.palette)
      : 'stone';
    return {
      id, floorFt: room.floorFt, roomId, kind, positionFt, sizeFt,
      rotationQuarterTurns: ((Math.round(finiteNumber(source.rotationQuarterTurns, 0)) % 4) + 4) % 4,
      palette, queryOnly: true,
    };
  });
}

export function normalizeCreationDraft(input) {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    throw new Error('creation draft must be an object');
  }
  const gridFt = clamp(Math.round(finiteNumber(input.gridFt, DEFAULT_GRID_FT)), 5, 20);
  const seed = String(input.seed ?? 'room-1').trim().slice(0, 64) || 'room-1';
  if (!Array.isArray(input.rooms) || input.rooms.length > 64) {
    throw new Error('creation draft requires 0-64 rooms');
  }
  const seen = new Set();
  const rooms = input.rooms.map((source, index) => {
    const room = roomFromBox({
      id: source.id,
      floorFt: source.floorFt,
      x: source.x,
      y: source.y,
      presetId: source.presetId,
      environment: source.environment,
      enclosure: source.enclosure,
      floor: source.floor,
      ceiling: source.ceiling,
      physicalCeilingFt: source.physicalCeilingFt,
      interior: source.interior,
      creation: {
        presetId: source.presetId,
        mergedInto: source.mergedInto,
        solidMaterial: source.solidMaterial,
        footprint: source.footprint,
        floorFt: source.floorFt,
        clearHeightFt: source.clearHeightFt,
        clearHeightOverrideFt: source.clearHeightOverrideFt,
        surfaceOverrides: source.surfaceOverrides,
        function: source.function,
        density: source.density,
        locked: source.locked,
        roofRemoved: source.roofRemoved,
        exteriorShell: source.exteriorShell,
        renderSurfaces: source.renderSurfaces,
        ambience: source.ambience,
        climate: source.climate,
        lightFixtures: source.lightFixtures,
        windowSuppressedFaces: source.windowSuppressedFaces,
        generation: source.generation ?? null,
      },
    }, index, gridFt, input.storyAuthority);
    if (seen.has(room.id)) room.id = validId(`${room.id}-${index + 1}`, `room-${index + 1}`);
    seen.add(room.id);
    if (!(room.x.max > room.x.min) || !(room.y.max > room.y.min)) {
      throw new Error(`room ${room.id} must be at least one grid cell wide and deep`);
    }
    if (!(room.ceiling > room.floor)) throw new Error(`room ${room.id} ceiling must exceed floor`);
    return room;
  });
  const mergeWarnings = normalizeRoomMerges(rooms, gridFt);
  const canvas = normalizeCanvas(input.canvas, rooms, gridFt);
  const verticalConnections = normalizeVerticalConnections(input.verticalConnections, rooms);
  const ladderConnections = normalizeDiscreteLadderConnections(input.ladderConnections, rooms);
  const manualOpenings = Array.isArray(input.manualOpenings)
    ? input.manualOpenings.map(opening => normalizeCreationOpening(opening, rooms))
    : [];
  const roomIds = new Set(rooms.map(room => room.id));
  const connectionOverrides = Array.isArray(input.connectionOverrides)
    ? input.connectionOverrides.flatMap((source) => {
      const pair = Array.isArray(source?.roomIds)
        ? Array.from(new Set(source.roomIds.map(value => String(value)))).sort()
        : [];
      if (pair.length !== 2 || !pair.every(roomId => roomIds.has(roomId))) return [];
      const mode = ['wall', 'opening', 'door-intent'].includes(source.mode) ? source.mode : 'auto';
      if (mode === 'auto') return [];
      const selectedDoor = doorIntent(source.doorItemId);
      return [{
        roomIds: pair,
        mode,
        doorItemId: mode === 'door-intent' ? selectedDoor.id : null,
        doorMeshId: mode === 'door-intent' ? String(source.doorMeshId ?? '').trim().toUpperCase() || null : null,
        doorWidthFt: mode === 'door-intent'
          ? clamp(snap(finiteNumber(source.doorWidthFt, gridFt), gridFt), gridFt, gridFt * 4)
          : null,
        queryOnly: true,
      }];
    })
    : [];
  const architecturalDressing = normalizeArchitecturalDressing(input.architecturalDressing, rooms);
  return {
    schema: CREATION_SCHEMA,
    seed,
    gridFt,
    canvas,
    rooms,
    mergeWarnings: [...(Array.isArray(input.mergeWarnings) ? input.mergeWarnings : []), ...mergeWarnings],
    verticalConnections,
    ladderConnections,
    manualOpenings,
    generateOpenings: input.generateOpenings !== false,
    // DAFT3D S26: the ENCOUNTER's environment (the ECE fills it from the encounter row). Not part of
    // the shape: it is re-derived every session, so moving an encounter underground needs no migration.
    underground: input.underground === true,
    connectionOverrides,
    architecturalDressing,
    buildingRoofs: normalizeBuildingRoofs(input.buildingRoofs, rooms),
  };
}

function rangesOverlap(a, b) {
  return { min: Math.max(a.min, b.min), max: Math.min(a.max, b.max) };
}

function same(valueA, valueB) {
  return Math.abs(valueA - valueB) < EPS;
}

function connectionOverrideForPair(overrides, a, b) {
  const pair = [a.id, b.id].sort();
  return overrides.find(override => (
    override.roomIds[0] === pair[0] && override.roomIds[1] === pair[1]
  )) ?? null;
}

function roomsSharePlace(rooms, a, b) {
  if ((!a.mergedInto && !b.mergedInto) || !rooms.length || a.id === b.id) return false;
  // All compiler callers hold normalized, one-level links.
  return (a.mergedInto ?? a.id) === (b.mergedInto ?? b.id);
}

function sharedRoomFace(a, b) {
  const straightSideSpan = (room, axis, at) => {
    const radii = normalizedRoomFootprint(room.footprint, room.x, room.y).cornerRadiiFt;
    if (axis === 'x') {
      const west = same(at, room.x.min);
      return {
        min: room.y.min + (west ? radii.southWest : radii.southEast),
        max: room.y.max - (west ? radii.northWest : radii.northEast),
      };
    }
    const south = same(at, room.y.min);
    return {
      min: room.x.min + (south ? radii.southWest : radii.northWest),
      max: room.x.max - (south ? radii.southEast : radii.northEast),
    };
  };
  if (same(a.x.max, b.x.min) || same(b.x.max, a.x.min)) {
    const at = same(a.x.max, b.x.min) ? a.x.max : b.x.max;
    const span = rangesOverlap(rangesOverlap(a.y, b.y),
      rangesOverlap(straightSideSpan(a, 'x', at), straightSideSpan(b, 'x', at)));
    return { axis: 'x', at, span };
  }
  if (same(a.y.max, b.y.min) || same(b.y.max, a.y.min)) {
    const at = same(a.y.max, b.y.min) ? a.y.max : b.y.max;
    const span = rangesOverlap(rangesOverlap(a.x, b.x),
      rangesOverlap(straightSideSpan(a, 'y', at), straightSideSpan(b, 'y', at)));
    return { axis: 'y', at, span };
  }
  return null;
}

/** Full shared face above the higher floor; the lower portion remains a rise/ledge. */
export function roomMergeOpeningForPair(a, b, gridFt = DEFAULT_GRID_FT, connectionOverrides = []) {
  if (connectionOverrideForPair(connectionOverrides, a, b)) return null;
  const face = sharedRoomFace(a, b);
  if (!face || face.span.max - face.span.min <= EPS) return null;
  const z = { min: Math.max(a.floor, b.floor), max: Math.min(a.ceiling, b.ceiling) };
  if (z.max - z.min <= EPS) return null;
  const pairKey = [a.id, b.id].sort().join('|');
  return {
    id: `merge-${hashString(`${pairKey}|${face.axis}|${face.at}`).toString(36)}`.slice(0, 32),
    plane: { axis: face.axis, at: face.at },
    span: { ...face.span },
    z,
    passable: true,
    transparent: true,
    creation: {
      automatic: true,
      mergedPlace: true,
      ...(same(a.floorFt, b.floorFt) ? { floorFt: a.floorFt } : {}),
      rooms: [a.id, b.id].sort(),
      connectionIntent: { kind: 'opening', theme: 'merged-place', label: 'open shared place', overrideMode: 'auto', queryOnly: true },
    },
  };
}

function connectionIntentForPair(a, b, override = null) {
  const presets = [roomPreset(a.presetId), roomPreset(b.presetId)];
  if (presets.some(preset => preset.enclosure === 'none')) return null;
  if (override?.mode === 'wall') return null;
  if (override?.mode === 'opening') {
    return {
      kind: 'opening',
      theme: 'wet-cave',
      label: 'natural cavern breach',
      overrideMode: 'opening',
      queryOnly: true,
    };
  }
  if (override?.mode === 'door-intent') {
    const selectedDoor = doorIntent(override.doorItemId);
    return {
      kind: 'door-intent',
      theme: 'system-door-intent',
      label: selectedDoor.label,
      doorItemId: selectedDoor.id,
      doorMeshId: override.doorMeshId ?? null,
      doorMeshAvailable: selectedDoor.meshAvailable,
      overrideMode: 'door-intent',
      queryOnly: true,
    };
  }
  const themed = presets
    .filter(preset => preset.id !== 'legacy-structural')
    .sort((left, right) => left.id.localeCompare(right.id))[0]
    ?? ROOM_PRESETS['legacy-structural'];
  return {
    kind: themed.connection.kind,
    theme: themed.theme,
    label: themed.connection.label,
    // DAFT3D S11: the PRESET hangs its own door — a stone hall a stone door, a timber mine a
    // wooden one — so an automatic connection is a real, material-correct door rather than
    // everything defaulting to the first item in the catalog (owner ruling D-S11-1).
    ...(themed.connection.doorItemId ? { doorItemId: themed.connection.doorItemId } : {}),
    overrideMode: 'auto',
    queryOnly: true,
  };
}

export function automaticOpeningForPair(a, b, gridFt = DEFAULT_GRID_FT, connectionOverrides = [], rooms = []) {
  const mergedPlace = roomsSharePlace(rooms, a, b);
  if (mergedPlace) {
    const merged = roomMergeOpeningForPair(a, b, gridFt, connectionOverrides);
    if (merged) return merged;
  }
  return automaticConnectionOpeningForPair(a, b, gridFt, connectionOverrides, mergedPlace);
}

function automaticConnectionOpeningForPair(a, b, gridFt, connectionOverrides, mergedPlace = false) {
  const crossFloor = !same(a.floorFt, b.floorFt);
  if (crossFloor && (!mergedPlace || !connectionOverrideForPair(connectionOverrides, a, b))) return null;
  const face = sharedRoomFace(a, b);
  const axis = face?.axis ?? null;
  const at = face?.at ?? null;
  const overlap = face?.span ?? null;
  if (!axis || !overlap || overlap.max - overlap.min < gridFt - EPS) return null;
  const override = connectionOverrideForPair(connectionOverrides, a, b);
  const intent = connectionIntentForPair(a, b, override);
  if (!intent || intent.kind === 'open') return null;
  const z = { min: Math.max(a.floor, b.floor), max: Math.min(a.ceiling, b.ceiling) };
  if (z.max - z.min < 2) return null;
  const availableWidth = Math.floor((overlap.max - overlap.min) / gridFt) * gridFt;
  const requestedWidth = override?.mode === 'door-intent'
    ? finiteNumber(override.doorWidthFt, gridFt)
    : 10;
  const width = Math.min(requestedWidth, availableWidth);
  const center = (overlap.min + overlap.max) / 2;
  const pairKey = [a.id, b.id].sort().join('|');
  return {
    id: `auto-${hashString(`${pairKey}|${axis}|${at}`).toString(36)}`.slice(0, 32),
    plane: { axis, at },
    span: { min: center - width / 2, max: center + width / 2 },
    z: { min: z.min, max: Math.min(z.max, z.min + 8) },
    passable: true,
    transparent: true,
    creation: {
      automatic: true,
      ...(crossFloor ? { crossFloorShared: true } : { floorFt: a.floorFt }),
      rooms: [a.id, b.id].sort(),
      connectionIntent: intent,
    },
  };
}

export function openingCovers(auto, manual) {
  return manual.plane.axis === auto.plane.axis
    && same(manual.plane.at, auto.plane.at)
    && manual.span.min <= auto.span.max - EPS
    && manual.span.max >= auto.span.min + EPS;
}

export function automaticOpenings(rooms, manualOpenings = [], gridFt = DEFAULT_GRID_FT, connectionOverrides = [], explicitOnly = false) {
  const generated = [];
  // Ordinary scenes use the existing connection path without any per-pair place work.
  const hasMerges = rooms.some(room => room.mergedInto);
  for (let left = 0; left < rooms.length; left++) {
    for (let right = left + 1; right < rooms.length; right++) {
      if (explicitOnly && !connectionOverrideForPair(connectionOverrides, rooms[left], rooms[right])
        && !(hasMerges && roomsSharePlace(rooms, rooms[left], rooms[right]))) continue;
      const opening = hasMerges
        ? automaticOpeningForPair(rooms[left], rooms[right], gridFt, connectionOverrides, rooms)
        : automaticConnectionOpeningForPair(rooms[left], rooms[right], gridFt, connectionOverrides);
      if (!opening || (opening.creation?.mergedPlace !== true
        && manualOpenings.some(manual => openingCovers(opening, manual)))) continue;
      generated.push(opening);
    }
  }
  return generated.sort((a, b) => a.id.localeCompare(b.id));
}

/**
 * ═══ WINDOWS (DAFT3D S11) ══════════════════════════════════════════════════════════════════
 *
 * ⭐ WHAT MAKES A WINDOW A WINDOW (owner, 2026-09-07): "we can see through it and it lets light
 * through from the external environment or for the adjoining room". That definition decides two
 * things this code had wrong on its first pass.
 *
 *   1. A WINDOW IS NOT ONLY EXTERIOR. "or for the adjoining room" — a window may look from one room
 *      into the next. That DEFINITION stands; its GENERATION half was reversed by the owner on
 *      2026-09-25 (D-S26-1): "we typically do not generate interior windows — a GM or encounter
 *      creator sets those by hand". So the generator sweeps only the EXTERIOR stretches of a face
 *      (the part no other walled room abuts), and an interior window is the GM's deliberate act with
 *      the Windows tool on a shared wall. Facing is stamped, never stored on the item
 *      (`creation.facing`, derived on every compile — see openingFacingFromGeometry()).
 *   2. A GENERATED WINDOW MUST BE SEE-THROUGH. `Window, Shuttered` is opaque while shut, so it is
 *      never a default — a timber building gets GLAZED windows and a stone keep gets BARRED ones,
 *      both of which pass sight and light. Shutters remain an identity a GM places deliberately.
 *
 * The other difference from a door is unchanged and is why the ladder decouples sight from passage:
 * a doorway starts at the floor, a window starts at chest height, so you can see through a window
 * you cannot walk through.
 *
 * The identity comes from the room's PRESET, exactly as a door's material does; a cave gets none,
 * because a cave has no built wall to cut. `transparentWhenShut` is carried onto the opening so the
 * runtime ladder can answer the sight question without re-reading the catalog.
 */
export const WINDOW_INTENT_CATALOG = Object.freeze([
  Object.freeze({
    id: '832C71DA-6E9B-497A-B0F5-1AC9487E3206',
    label: 'Window, Glazed',
    closure: 'glazed',
    material: 'glass',
    transparentWhenShut: true,
    looks: 6,
  }),
  Object.freeze({
    id: 'B29D5E10-7C34-4A86-9D52-8E60B1F47A3C',
    label: 'Window, Barred',
    closure: 'barred',
    material: 'iron',
    transparentWhenShut: true,
    looks: 4,
  }),
  Object.freeze({
    id: '6C4A1F73-2B58-4D91-A0E6-3F71D8C25904',
    label: 'Window, Shuttered',
    closure: 'shuttered',
    material: 'wooden',
    // Opaque shutters are deliberate choices, never a generated
    // default (owner 2026-09-07: "what makes a window a window is that we can see through it and
    // it lets light through"). A GM places these deliberately — a barn, a storehouse, a shuttered
    // winter room — and their shutters open.
    transparentWhenShut: false,
    looks: 6,
  }),
  Object.freeze({
    id: '9D9CD8E5-E07E-4BC7-A384-862CAE049ACB',
    label: 'Window, Stained Glass',
    closure: 'stained',
    material: 'glass',
    transparentWhenShut: false,
    passesLight: true,
    looks: 6,
    pending: false,
  }),

]);

/** Sill and head, in feet above the room floor. A window you could step through would be a door. */
export const WINDOW_SILL_FT = 3;
export const WINDOW_HEAD_FT = 6.5;
/** One grid cell wide, so a window never straddles a corner or eats a whole wall run. */
export const WINDOW_WIDTH_FT = 5;
/** Leave this much wall between a window and the end of its exterior stretch. */
const WINDOW_MARGIN_FT = 5;

export function windowIntent(itemIdOrClosure) {
  const value = String(itemIdOrClosure ?? '').trim();
  return WINDOW_INTENT_CATALOG.find(entry => entry.id === value)
    ?? WINDOW_INTENT_CATALOG.find(entry => entry.closure === value.toLowerCase())
    ?? null;
}

/**
 * A number in [0,1) that depends only on what it is given — the same draft always yields the same
 * value for the same window.
 *
 * ⚠ NEVER draw from a shared sequential stream for per-object choices. Inserting one window would
 * shift every later draw and reshuffle an encounter the GM has already played through. Each object
 * hashes its OWN identity, so adding one changes only that one (Codex consult, 2026-09-07 — the
 * same discipline the seeded random locking will need).
 */
export function stableUnitInterval(...parts) {
  return (hashString(parts.map(part => String(part)).join('|')) >>> 0) / 4294967296;
}

/**
 * Which window identity this particular opening gets, from the room's weighted mix.
 *
 * ⭐ Owner 2026-09-07: shutters "typically should not be the default, but that is also a design
 * choice — probably for small houses and such or certain room types they should be more common".
 * So a preset does not name ONE window; it names a MIX, and the room's function can lean it. A
 * ramshackle timber building is mostly shutters with the odd glazed light, because glass is
 * expensive; a stone keep is barred throughout.
 */
export function windowIntentForOpening(preset, room, openingId, seed) {
  const mix = preset?.windows?.byFunction?.[room?.function] ?? preset?.windows?.mix ?? [];
  const entries = mix
    .map(entry => ({ intent: windowIntent(entry.itemId), weight: Math.max(0, finiteNumber(entry.weight, 1)) }))
    .filter(entry => entry.intent && entry.intent.pending !== true && entry.weight > 0);
  if (!entries.length) return null;
  const total = entries.reduce((sum, entry) => sum + entry.weight, 0);
  let roll = stableUnitInterval(seed ?? '', 'window-identity/v1', openingId) * total;
  for (const entry of entries) {
    roll -= entry.weight;
    if (roll < EPS) return entry.intent;
  }
  return entries[entries.length - 1].intent;
}

/** One span minus another: the zero, one or two pieces left over. */
function subtractSpan(span, cut) {
  const hit = rangesOverlap(span, cut);
  if (hit.max <= hit.min + EPS) return [span];
  const out = [];
  if (hit.min - span.min > EPS) out.push({ min: span.min, max: hit.min });
  if (span.max - hit.max > EPS) out.push({ min: hit.max, max: span.max });
  return out;
}

/**
 * The stretches of one face of a room that look OUTSIDE — the face minus every part another walled
 * room abuts across the window band (DAFT3D S26, D-S26-1). An L-shaped neighbour therefore leaves the
 * un-abutted remainder of a face exterior, and the windows there survive.
 *
 * @return {Array<{min:number,max:number}>}
 */
export function exteriorFaceSpans(room, rooms, axis, at) {
  const along = axis === 'x' ? 'y' : 'x';
  const band = { min: room.floor + WINDOW_SILL_FT, max: Math.min(room.ceiling, room.floor + WINDOW_HEAD_FT) };
  let free = [{ min: room[along].min, max: room[along].max }];
  for (const shared of boxFaceSharedSpans(room, rooms, axis, at, band)) {
    free = free.flatMap(span => subtractSpan(span, shared));
  }
  return free;
}

/**
 * The EXTERIOR stretches of one side of a room that nothing has already claimed.
 *
 * A shared stretch is gone before this starts (exteriorFaceSpans). What is left still loses the part
 * any existing opening occupies, plus a margin — which keeps a generated window off a hand-placed
 * one and off an exterior door, the same rule by which two doors cannot claim one doorway.
 *
 * @return {Array<{min:number,max:number}>}
 */
function freeWallSpansForSide(room, axis, at, takenOpenings, rooms = [room]) {
  let free = exteriorFaceSpans(room, rooms, axis, at);
  for (const opening of takenOpenings) {
    if (opening.plane?.axis !== axis || !same(opening.plane.at, at)) continue;
    if (Number.isFinite(opening.creation?.floorFt) && !same(opening.creation.floorFt, room.floorFt)) continue;
    // Leave clear wall either side of whatever is already cut there.
    const shadow = { min: opening.span.min - WINDOW_MARGIN_FT, max: opening.span.max + WINDOW_MARGIN_FT };
    free = free.flatMap(span => subtractSpan(span, shadow));
  }
  return free.filter(span => span.max - span.min >= WINDOW_WIDTH_FT + WINDOW_MARGIN_FT * 2 - EPS);
}

/**
 * Every window a set of rooms should have, placed on their EXTERIOR walls only (D-S26-1).
 *
 * Deterministic: windows are spaced evenly along each free stretch, so the same draft always
 * compiles to the same windows and a re-compile never shuffles them (the same property the wall-run
 * keys needed). A neighbour that eats part of a face shifts only THAT face's slots; the other faces
 * keep their ids, identities and locks.
 *
 * `underground` (the encounter's environment, D-S26-1 ruling 5) generates nothing — a barred window
 * onto solid rock is not a window. Hand placement ignores it. `roomIds` limits which rooms are
 * swept; every room still counts as a neighbour.
 */
export function automaticWindowOpenings(rooms, gridFt = DEFAULT_GRID_FT, existingOpenings = [], seed = '', { underground = false, roomIds = null } = {}) {
  const generated = [];
  if (underground === true) return generated;
  const only = Array.isArray(roomIds) ? new Set(roomIds.map(String)) : null;
  // Saved generated windows keep their deterministic slots on reload. They suppress a duplicate
  // candidate below, but must not become new obstacles that shift the layout into the gaps.
  const layoutOpenings = existingOpenings.filter(opening => !(opening.creation?.automatic === true
    && opening.creation?.connectionIntent?.kind === 'window-intent'));
  for (const room of rooms) {
    if (only && !only.has(String(room.id))) continue;
    const preset = roomPreset(room.presetId);
    if (!(preset?.windows?.mix?.length) && !preset?.windows?.byFunction) continue;
    const spacing = Math.max(gridFt * 2, finiteNumber(preset?.windows?.spacingFt, 20));
    const sides = [
      { axis: 'x', at: room.x.min },
      { axis: 'x', at: room.x.max },
      { axis: 'y', at: room.y.min },
      { axis: 'y', at: room.y.max },
    ];
    for (const side of sides) {
      // S26b: the GM ruled this wall windowless. Its would-be slots are simply not raised; the other
      // walls keep their deterministic slots (ids hash the wall, not a running count).
      if ((room.windowSuppressedFaces ?? []).includes(windowFaceKey(room, side.axis, side.at))) continue;
      for (const span of freeWallSpansForSide(room, side.axis, side.at, layoutOpenings, rooms)) {
        const usable = span.max - span.min - WINDOW_MARGIN_FT * 2;
        const count = Math.max(1, Math.floor(usable / spacing));
        for (let index = 0; index < count; index++) {
          const centre = span.min + WINDOW_MARGIN_FT + (usable * (index + 0.5)) / count;
          const openingId = `win-${hashString(`${room.id}|${side.axis}|${side.at}|${centre.toFixed(3)}`).toString(36)}`.slice(0, 32);
          // The identity is drawn PER WINDOW from the room's mix, so one building can carry mostly
          // shutters with the odd glazed light — and the draw is stable, so a recompile never
          // reshuffles which is which.
          const intent = windowIntentForOpening(preset, room, openingId, seed);
          if (!intent) continue;
          // ⭐ RANDOMLY LOCKED, the way Project Zomboid does it (owner 2026-09-07) — and drawn PER
          // WINDOW from its own id, never from a shared sequential stream, so inserting one window
          // cannot reshuffle the locks on all the others.
          //
          // ⚠ This is the INITIAL state only. It is applied when the inventory row is first created
          // and never re-applied: a recompile must not re-lock a window the party already picked,
          // nor unlock one a GM locked on purpose (Codex consult, 2026-09-07).
          const lockChance = Math.max(0, Math.min(1, finiteNumber(preset?.windows?.lockChance, 0)));
          const startsLocked = lockChance > 0
            && stableUnitInterval(seed ?? '', 'window-initial-lock/v1', openingId) < lockChance;
          const opening = {
            id: openingId,
            plane: { axis: side.axis, at: side.at },
            span: { min: centre - WINDOW_WIDTH_FT / 2, max: centre + WINDOW_WIDTH_FT / 2 },
            // Off the floor — this is what makes it a window rather than a doorway.
            z: { min: room.floor + WINDOW_SILL_FT, max: Math.min(room.ceiling, room.floor + WINDOW_HEAD_FT) },
            // Shut by default, and impassable at every rung short of broken. The runtime ladder
            // overrides both; these are the authored defaults a reader sees before it runs.
            passable: false,
            transparent: intent.transparentWhenShut,
            ...(intent.passesLight !== undefined ? { passesLight: intent.passesLight } : {}),
            creation: {
              automatic: true,
              floorFt: room.floorFt,
              rooms: [room.id],
              facing: 'exterior',
              connectionIntent: {
                kind: 'window-intent',
                label: `${intent.closure} window`,
                windowItemId: intent.id,
                transparentWhenShut: intent.transparentWhenShut,
                // Authoring intent, read once when the row is born. The live lock is a STATUS on
                // that row — this never becomes the runtime answer to "is it locked".
                startsLocked,
                queryOnly: true,
              },
            },
          };
          if (opening.z.max - opening.z.min < 2) continue;
          if (existingOpenings.some(other => openingCovers(opening, other))) continue;
          generated.push(opening);
        }
      }
    }
  }
  return generated.sort((a, b) => a.id.localeCompare(b.id));
}

/**
 * DAFT3D S26 follow-up — the doorways ONE room should have with its neighbours, added to a draft that
 * no longer generates by itself.
 *
 * ⚠ The same gap reconcileRoomWindows() closes: a draft reloaded from a stored shape has
 * \`generateOpenings: false\`, so automaticOpenings() runs in explicit-override mode only and two rooms
 * drawn against each other in a SAVED encounter were left with no doorway between them (D-S11-1 says
 * adjoining rooms connect with no extra gesture). This adds the missing automatic doorway for every
 * pair that includes \`roomId\`, exactly as the compile would have raised it — same id, same intent, same
 * material — and never a second one: anything already cut on that stretch (a saved doorway, a hand-
 * placed door or window, a cleared aperture) wins, and a connection override of 'wall' still closes it.
 *
 * It also RETIRES this room's automatic doorways whose pair no longer raises that doorway — the two
 * rooms stopped touching (a shrink, a move) or a 'wall' override closed them — exactly what a fresh
 * compile would drop. The editor settles a bound door row like a retired window:
 * untouched it goes with its opening, touched it is adopted.
 * A doorway to a DELETED neighbour is the delete path's business, not this one's.
 */
export function reconcileRoomDoors(input, roomId) {
  const draft = normalizeCreationDraft(input);
  const room = draft.rooms.find(candidate => candidate.id === String(roomId));
  if (!room) return { manualOpenings: draft.manualOpenings, added: [], retired: [] };
  // Every automatic two-room opening (a doorway, or a cavern breach an 'opening' override made) is
  // judged against what the pair raises NOW. The id encodes only the pair and the plane, so the KIND and
  // a GM-chosen door material are compared too: switching a boundary door ↔ breach, or to another door,
  // keeps the id and would otherwise leave the old one standing (Sonnet review, 2026-09-25).
  const retired = draft.manualOpenings.filter((opening) => {
    const pair = Array.isArray(opening.creation?.rooms) ? opening.creation.rooms.map(String) : [];
    const have = opening.creation?.connectionIntent;
    if (opening.creation?.automatic !== true || !have || have.kind === 'window-intent'
      || pair.length !== 2 || !pair.includes(room.id)) return false;
    const other = draft.rooms.find(candidate => candidate.id === pair.find(id => id !== room.id));
    if (!other) return false;
    const expected = automaticOpeningForPair(room, other, draft.gridFt, draft.connectionOverrides, draft.rooms);
    const want = expected?.creation?.connectionIntent;
    // Geometry too (Codex review, 2026-09-25): a resize moves the valid doorway while keeping its id, and
    // the stale one would survive off the wall beside a same-id replacement — a shape that fails
    // validation. Retired and re-raised, the door row is re-laid at the new place by the structure commit.
    return !expected || expected.id !== opening.id || want?.kind !== have.kind
      || (want?.overrideMode === 'door-intent' && (want.doorItemId ?? null) !== (have.doorItemId ?? null))
      || expected.plane.axis !== opening.plane.axis || !same(expected.plane.at, opening.plane.at)
      || !same(expected.span.min, opening.span.min) || !same(expected.span.max, opening.span.max)
      || !same(expected.z.min, opening.z.min) || !same(expected.z.max, opening.z.max);
  });
  const gone = new Set(retired.map(opening => opening.id));
  const kept = draft.manualOpenings.filter(opening => !gone.has(opening.id));
  const added = [];
  for (const other of draft.rooms) {
    if (other.id === room.id) continue;
    const opening = automaticOpeningForPair(room, other, draft.gridFt, draft.connectionOverrides, draft.rooms);
    // A GENERATED window never holds a doorway off: the doorway wins the wall it stands in, and that
    // window stands on a now-shared stretch the window reconcile retires anyway.
    // openingCovers() compares plane and span only, so an opening on ANOTHER floor whose wall happens to
    // align must not hold this floor's doorway off either.
    const blockers = [...kept, ...added].filter(existing => !(existing.creation?.automatic === true
      && existing.creation?.connectionIntent?.kind === 'window-intent')
      && !(Number.isFinite(existing.creation?.floorFt) && !same(existing.creation.floorFt, room.floorFt)));
    if (!opening || blockers.some(existing => openingCovers(opening, existing))) continue;
    added.push(opening);
  }
  return { manualOpenings: [...kept, ...added], added: added.map(opening => opening.id), retired: [...gone] };
}

/**
 * DAFT3D S26 follow-up: the Curtains default for a NEW room of this preset and function (owner
 * 2026-09-25: "curtains depends on what type of room"). A room already saved keeps its stored toggle.
 */
export function curtainDefaultForRoom(presetId, roomFunction) {
  const preset = roomPreset(presetId);
  const byFunction = preset?.windows?.curtainsByFunction?.[roomFunction];
  return typeof byFunction === 'boolean' ? byFunction : preset?.generation?.defaults?.curtains === true;
}

/** A window this room's generator raised (not hand-placed, not another room's). */
function isGeneratedWindowOf(opening, roomId) {
  const rooms = Array.isArray(opening?.creation?.rooms) ? opening.creation.rooms.map(String) : [];
  return opening?.creation?.automatic === true
    && opening.creation.connectionIntent?.kind === 'window-intent'
    && rooms.length === 1 && rooms[0] === String(roomId);
}

/** Does any part of this window's span stand on a stretch another walled room now abuts? */
function windowTouchesSharedStretch(room, rooms, opening) {
  return boxFaceSharedSpans(room, rooms, opening.plane.axis, opening.plane.at, opening.z)
    .some((shared) => {
      const hit = rangesOverlap(shared, opening.span);
      return hit.max > hit.min + EPS;
    });
}

/** Does this window still stand in one of the room's own walls (its plane, its length, its height)? */
function windowFitsRoomWall(room, opening) {
  const axis = opening.plane?.axis;
  if (axis !== 'x' && axis !== 'y') return false;
  const along = axis === 'x' ? 'y' : 'x';
  return (same(opening.plane.at, room[axis].min) || same(opening.plane.at, room[axis].max))
    && opening.span.min >= room[along].min - EPS && opening.span.max <= room[along].max + EPS
    && opening.z.min >= room.floor - EPS && opening.z.max <= room.ceiling + EPS;
}

/**
 * DAFT3D S26 — bring ONE room's GENERATED windows in line with where its walls look now.
 *
 * ⚠ Why this exists beside automaticWindowOpenings(): a draft reloaded from a stored shape has
 * \`generateOpenings: false\` (reloading must never run today's presets over saved geometry), and its
 * earlier generated windows ride in \`manualOpenings\` with \`automatic: true\`. So in an encounter that
 * has been saved even once, the compile alone neither retires a window whose face became shared nor
 * raises one on a new room. The editor calls this at the moments the owner ruled on instead:
 *
 *   grow: true  — the room's own Generate / Refresh: retire every generated window not at a current
 *                 exterior slot, add the missing slots. Surviving windows keep their object (and so
 *                 their binding, lock draw and structure state), because a slot's id is stable.
 *   grow: false — a NEIGHBOUR changed (drawn against it, resized, deleted): retire only the generated
 *                 windows standing on a stretch that is now shared; add nothing. Deleting the
 *                 neighbour regrows the face on that room's own next Refresh (D-S26-1 ruling 4).
 *
 * Hand-placed windows (\`automatic: false\`) and other rooms' openings are never touched. Pure: returns
 * the next \`manualOpenings\` and the ids retired / added; the caller commits them.
 */
export function reconcileRoomWindows(input, roomId, { grow = true } = {}) {
  return reconcileHeldRoomWindows(normalizeCreationDraft(input), roomId, { grow });
}

/** The editor already holds a normalized draft; keep surviving openings by reference. */
export function reconcileHeldRoomWindows(draft, roomId, { grow = true } = {}) {
  const room = draft.rooms.find(candidate => candidate.id === String(roomId));
  if (!room) return { manualOpenings: draft.manualOpenings, retired: [], added: [] };
  const current = draft.manualOpenings.filter(opening => isGeneratedWindowOf(opening, room.id));
  const others = draft.manualOpenings.filter(opening => !isGeneratedWindowOf(opening, room.id));
  if (!grow) {
    // Also the ones a resize left OFF the room's walls: a saved window on a face that moved would make
    // the shape fail validation, and the resize with it (Codex review, 2026-09-25).
    const retired = current.filter(opening => !windowFitsRoomWall(room, opening)
      || windowTouchesSharedStretch(room, draft.rooms, opening)
      || (room.windowSuppressedFaces ?? []).includes(windowFaceKey(room, opening.plane.axis, opening.plane.at)));
    const gone = new Set(retired.map(opening => opening.id));
    return {
      manualOpenings: draft.manualOpenings.filter(opening => !gone.has(opening.id)),
      retired: retired.map(opening => opening.id),
      added: [],
    };
  }
  const doors = automaticOpenings(draft.rooms, draft.manualOpenings, draft.gridFt, draft.connectionOverrides, !draft.generateOpenings);
  const desired = automaticWindowOpenings(draft.rooms, draft.gridFt, [...doors, ...others], draft.seed, {
    underground: draft.underground,
    roomIds: [room.id],
  });
  const desiredIds = new Set(desired.map(opening => opening.id));
  const currentIds = new Set(current.map(opening => opening.id));
  const kept = current.filter(opening => desiredIds.has(opening.id));
  const added = desired.filter(opening => !currentIds.has(opening.id));
  return {
    manualOpenings: [...others, ...kept, ...added],
    retired: current.filter(opening => !desiredIds.has(opening.id)).map(opening => opening.id),
    added: added.map(opening => opening.id),
  };
}

/**
 * `creation.facing` on a window opening — 'exterior' or 'interior' — derived from the rooms. Doors
 * are not stamped: a doorway joins two rooms and has no side (D-S11-11); a reader that needs a door's
 * facing derives it (openingFacing() in the shape core).
 */
function stampWindowFacing(opening, rooms) {
  if (opening?.creation?.connectionIntent?.kind !== 'window-intent') return opening;
  opening.creation.facing = openingFacingFromGeometry(rooms, opening);
  return opening;
}

/** Projected intact state alone does not make an unbound automatic doorway authored content. */
export function roomOpeningIsReplaceable(opening) {
  return opening?.creation?.automatic === true
    && opening.creation?.connectionIntent?.kind !== 'window-intent'
    && !opening.creation?.connectionIntent?.doorInventoryId
    && (opening.structureState === undefined || opening.structureState === 'intact');
}

/** An override-derived cross-floor cut is active only while its boxes share a place. */
export function roomOpeningIsDormant(opening, rooms) {
  if (opening.creation?.automatic !== true || opening.creation.crossFloorShared !== true
    || opening.creation.rooms?.length !== 2) return false;
  const [a, b] = opening.creation.rooms.map(id => rooms.find(room => room.id === id));
  return Boolean(a && b && !roomsSharePlace(rooms, a, b));
}

export function shapeFromCreationDraft(input, { generateOpenings = input.generateOpenings !== false } = {}) {
  const draft = normalizeCreationDraft(input);
  // Saved generated connections can predate a merge. Re-derive only that pair's automatic
  // opening so its full shared face replaces the old narrow doorway on copy/reload.
  const manualOpenings = draft.manualOpenings.filter(opening => {
    if (roomOpeningIsDormant(opening, draft.rooms)) return false;
    // A merged-place opening is wholly derived from current membership. A saved one must
    // never hold a face open after its link is removed, refused, or overridden.
    if (opening.creation?.automatic === true && opening.creation?.mergedPlace === true) return false;
    if (!roomOpeningIsReplaceable(opening) || !Array.isArray(opening.creation?.rooms)
      || opening.creation.rooms.length !== 2) return true;
    const [a, b] = opening.creation.rooms.map(id => draft.rooms.find(room => room.id === id));
    return !a || !b || !roomsSharePlace(draft.rooms, a, b)
      || Boolean(connectionOverrideForPair(draft.connectionOverrides, a, b));
  });
  const autoOpenings = automaticOpenings(
    draft.rooms,
    manualOpenings,
    draft.gridFt,
    draft.connectionOverrides,
    !generateOpenings,
  );
  // A regenerated merge cut keeps only the scalar provenance needed to restore
  // an earlier automatic connection under the saved-shape explicit-only policy.
  const unmergedAutomaticPairs = new Set(draft.manualOpenings.filter(opening => roomOpeningIsReplaceable(opening)
    && (opening.creation?.mergedPlace !== true || opening.creation.unmergedAutomatic === true)
    && opening.creation?.rooms?.length === 2).map(opening => [...opening.creation.rooms].sort().join('|')));
  for (const opening of autoOpenings) if (opening.creation?.mergedPlace === true
    && unmergedAutomaticPairs.has([...opening.creation.rooms].sort().join('|'))) opening.creation.unmergedAutomatic = true;
  // Windows come AFTER the doorways, and are suppressed by anything already cut there: a doorway
  // wins the wall it stands in, and a hand-authored opening wins over an automatic window.
  const autoWindows = generateOpenings ? automaticWindowOpenings(
    draft.rooms,
    draft.gridFt,
    [...autoOpenings, ...manualOpenings],
    draft.seed,
    { underground: draft.underground },
  ) : [];
  // DAFT3D S26: every window — generated, carried forward or hand-placed — is stamped with where it
  // looks, from geometry, on every compile. Never stored on the item, never edited by hand.
  const openings = [...manualOpenings.map(cloneOpening), ...autoOpenings, ...autoWindows]
    .map(opening => stampWindowFacing(opening, draft.rooms));
  const shape = {
    v: 1,
    unit: 'ft',
    boxes: draft.rooms.map(room => ({
      id: room.id,
      floorFt: room.floorFt,
      x: { ...room.x },
      y: { ...room.y },
      floor: room.floor,
      ceiling: room.ceiling,
      interior: room.interior,
      creation: {
        floorFt: room.floorFt,
        presetId: room.presetId,
        ...(room.solidMaterial ? { solidMaterial: room.solidMaterial } : {}),
        footprint: { ...room.footprint },
        environment: room.environment,
        enclosure: room.enclosure,
        physicalCeilingFt: room.physicalCeilingFt,
        clearHeightFt: room.clearHeightFt,
        clearHeightOverrideFt: room.clearHeightOverrideFt,
        surfaceOverrides: { ...room.surfaceOverrides },
        function: room.function,
        density: room.density,
        locked: room.locked,
        ...(room.mergedInto ? { mergedInto: room.mergedInto } : {}),
        // Omitted when false so an untouched room's blob does not change shape.
        ...(room.roofRemoved === true ? { roofRemoved: true } : {}),
        ...(room.exteriorShell === true ? { exteriorShell: true } : {}),
        ...(room.renderSurfaces === false ? { renderSurfaces: false } : {}),
        ...(room.ambience ? { ambience: { ...room.ambience } } : {}),
        ...(room.climate ? { climate: { ...room.climate } } : {}),
        ...(room.lightFixtures === 'sconces' ? { lightFixtures: 'sconces' } : {}),
        // Omitted when empty so an untouched room's blob does not change shape (S26b).
        ...(room.windowSuppressedFaces?.length ? { windowSuppressedFaces: [...room.windowSuppressedFaces] } : {}),
        // DAFT3D S8: replay intent rides on the box; omitted for a never-generated room.
        ...(room.generation ? { generation: cloneJson(room.generation) } : {}),
      },
    })),
    openings,
    authoring: {
      schema: CREATION_SCHEMA,
      seed: draft.seed,
      gridFt: draft.gridFt,
      // X/Y extents are a derived runtime generation/support envelope, not encounter
      // authorship. Persist rooms, walls, and object origins; consumers derive bounds.
      canvasPersistence: 'derived-from-authored-content',
      autoOpenings: generateOpenings,
      connectionOverrides: draft.connectionOverrides,
      verticalConnections: draft.verticalConnections,
      ladderConnections: draft.ladderConnections,
      architecturalDressing: draft.architecturalDressing,
      buildingRoofs: draft.buildingRoofs,
      roomFootprints: draft.rooms.map(room => ({
        roomId: room.id,
        kind: room.footprint.kind,
        cornerRadiusFt: room.footprint.cornerRadiusFt,
        cornerRadiiFt: { ...room.footprint.cornerRadiiFt },
      })),
      roundedFootprintPersistence: 'query-only-not-in-encounter-shape-v1',
      canvasOnly: draft.rooms.length === 0,
      canvasOnlyPersistence: null,
    },
  };
  validateShape(shape);
  return shape;
}

function roomForPoint(rooms, x, y, floorFt) {
  return rooms
    .filter(room => same(room.floorFt, floorFt) && roomContainsPoint(room, x, y))
    .sort((a, b) => {
      const areaA = (a.x.max - a.x.min) * (a.y.max - a.y.min);
      const areaB = (b.x.max - b.x.min) * (b.y.max - b.y.min);
      return areaA - areaB || a.id.localeCompare(b.id);
    })[0] ?? null;
}

/** Persisted position selects its physical volume, then the place's primary.
 * Floor contact wins over a smaller containing footprint; ceiling-only contact
 * is excluded with the continuous-world 0.01 ft tolerance. */
export function roomPlaceForPoint(rooms, x, y, z) {
  if (![x, y, z].every(Number.isFinite)) return null;
  let selected = null, selectedArea = Infinity, selectedContact = false;
  for (const room of rooms) {
    if (!Number.isFinite(room.floor) || !Number.isFinite(room.ceiling) || room.ceiling <= room.floor
      || z < room.floor - .01 || !roomContainsPoint(room, x, y)) continue;
    const contact = Math.abs(z - room.floor) <= .01;
    if (!contact && z >= room.ceiling - .01) continue;
    const area = (room.x.max - room.x.min) * (room.y.max - room.y.min);
    if (contact !== selectedContact ? contact : area < selectedArea
      || area === selectedArea && (!selected || room.id.localeCompare(selected.id) < 0)) {
      selected = room; selectedArea = area; selectedContact = contact;
    }
  }
  return selected ? resolveRoomPlace(rooms, selected.id) : null;
}

function shuffled(values, rng) {
  const result = values.slice();
  for (let index = result.length - 1; index > 0; index--) {
    const other = Math.floor(rng() * (index + 1));
    [result[index], result[other]] = [result[other], result[index]];
  }
  return result;
}

function dressingZone(cell) {
  const walls = cell.edges.filter(edge => edge === 'wall').length;
  if (walls >= 2) return 'corner';
  if (walls === 1) return 'wall-band';
  return 'center';
}

function compileDressing(draft, solution) {
  const doorClear = new Set();
  for (const cell of solution.cells) {
    if (cell.edges.includes('door')) {
      doorClear.add(cell.key);
      cell.neighbors.filter(Boolean).forEach(key => doorClear.add(key));
    }
  }
  const placements = [];
  for (const room of draft.rooms) {
    const eligible = solution.cells.filter(cell => (
      roomForPoint(draft.rooms, cell.cx, cell.cy, cell.floorFt)?.id === room.id
      && !doorClear.has(cell.key)
    ));
    const rate = DENSITY_RATES[room.density] ?? DENSITY_RATES[1];
    const rng = mulberry32(hashString(`${draft.seed}|dressing|${room.id}|${room.function}|${room.density}`));
    const desired = Math.min(eligible.length, Math.floor(eligible.length * rate + rng()));
    const grammar = roomTypeDressingFamilies(room.function);
    if (!grammar.length) continue;
    const candidates = shuffled(eligible, rng).sort((a, b) => {
      const rank = { corner: 0, 'wall-band': 1, center: 2 };
      return rank[dressingZone(a)] - rank[dressingZone(b)];
    });
    for (const cell of candidates.slice(0, desired)) {
      placements.push({
        id: `dress-${room.id}-${placements.length + 1}`,
        roomId: room.id,
        floorFt: room.floorFt,
        roomFunction: room.function,
        category: grammar[Math.floor(rng() * grammar.length)],
        zone: dressingZone(cell),
        cellKey: cell.key,
        positionFt: { x: cell.cx, y: cell.cy, z: room.floor },
        rotationQuarterTurns: Math.floor(rng() * 4),
        authority: 'generated-free',
      });
    }
  }
  return placements;
}

function numberKey(value) {
  return Number(value.toFixed(6)).toString();
}

function segmentForCellEdge(cell, direction, cellFt) {
  const half = cellFt / 2;
  if (direction === 0) {
    return { axis: 'y', at: cell.cy + half, span: { min: cell.cx - half, max: cell.cx + half } };
  }
  if (direction === 1) {
    return { axis: 'x', at: cell.cx + half, span: { min: cell.cy - half, max: cell.cy + half } };
  }
  if (direction === 2) {
    return { axis: 'y', at: cell.cy - half, span: { min: cell.cx - half, max: cell.cx + half } };
  }
  return { axis: 'x', at: cell.cx - half, span: { min: cell.cy - half, max: cell.cy + half } };
}

/**
 * Does this opening go all the way DOWN to the floor of the box it is cut into?
 *
 * ⚠ THIS IS WHAT SEPARATES A DOORWAY FROM A WINDOW, and it is load-bearing in two places: an
 * opening that reaches the floor breaks the wall into two runs and makes the cell edge crossable,
 * while one that starts at chest height does neither — the wall carries on under the sill and over
 * the head, and you cannot walk through it. Without this test the S11 windows split every wall run
 * they sat in (a 40 ft room went from 4 runs to 8) and turned solid wall into a doorway.
 */
function openingReachesFloor(shape, opening) {
  const sill = opening?.z?.min;
  if (!Number.isFinite(sill)) return true;
  const box = (shape.boxes ?? []).find(candidate => (
    (!Number.isFinite(opening.creation?.floorFt) || same(candidate.floor, opening.creation.floorFt))
    && sill <= candidate.floor + EPS + 0.5
  ));
  // No box claims it low down ⇒ it is raised off the floor of everything it touches.
  return box !== undefined;
}

function openingForSegment(shape, segment, floorFt) {
  const midpoint = (segment.span.min + segment.span.max) / 2;
  return (shape.openings ?? []).find(opening => (
    (!Number.isFinite(opening.creation?.floorFt) || same(opening.creation.floorFt, floorFt))
    &&
    opening.plane.axis === segment.axis
    && same(opening.plane.at, segment.at)
    && midpoint >= opening.span.min - EPS
    && midpoint <= opening.span.max + EPS
    // A window is a hole in a wall that is still standing; only a floor-level opening is a way through.
    && (opening.creation?.mergedPlace === true || opening.creation?.crossFloorShared === true
      || openingReachesFloor(shape, opening))
  )) ?? null;
}

function floorCellKey(floorFt, coordinateKey) {
  return `${numberKey(floorFt)}:${coordinateKey}`;
}

function clipSolutionToRoomFootprints(solution, draft, shape, floorFt) {
  const cells = solution.cells
    .filter(cell => roomForPoint(draft.rooms, cell.cx, cell.cy, floorFt))
    .map(cell => ({
      ...cell,
      key: floorCellKey(floorFt, cell.key),
      floorFt,
      footprintRoomId: null,
    }));
  const cellsByCoordinate = new Map(cells.map(cell => [`${cell.ix},${cell.iy}`, cell]));
  const directions = [
    { dx: 0, dy: 1 },
    { dx: 1, dy: 0 },
    { dx: 0, dy: -1 },
    { dx: -1, dy: 0 },
  ];
  for (const cell of cells) {
    const owner = roomForPoint(draft.rooms, cell.cx, cell.cy, floorFt);
    cell.neighbors = [];
    cell.edges = directions.map((direction, directionIndex) => {
      const adjacent = cellsByCoordinate.get(`${cell.ix + direction.dx},${cell.iy + direction.dy}`) ?? null;
      cell.neighbors.push(adjacent?.key ?? null);
      if (!adjacent) {
        // The OUTER boundary can still carry an authored opening — a dungeon entrance is a door to
        // nowhere-in-particular. It used to read `wall` unconditionally, so an exterior doorway kept
        // its wall: furniture could block the entrance, and under DAFT3D S14 a wall RUN (and its
        // inventory row) would be written straight across the opening. The shape already tells
        // Janus the opening is passable, so this only aligns the builder with it (2026-09-07).
        const boundaryOpening = openingForSegment(
          shape,
          segmentForCellEdge(cell, directionIndex, solution.cellFt),
          floorFt,
        );
        return boundaryOpening ? 'door' : 'wall';
      }
      const otherOwner = roomForPoint(draft.rooms, adjacent.cx, adjacent.cy, floorFt);
      if (owner?.id === otherOwner?.id) return 'open';
      const opening = openingForSegment(
        shape,
        segmentForCellEdge(cell, directionIndex, solution.cellFt),
        floorFt,
      );
      return opening ? 'door' : 'wall';
    });
    cell.footprintRoomId = owner?.id ?? null;
  }
  const grid = Object.fromEntries(cells.map(cell => {
    const localKey = `${cell.ix},${cell.iy}`;
    return [cell.key, solution.grid[localKey]];
  }));
  return {
    ...solution,
    cells,
    grid,
    footprintAuthority: {
      schema: 'ft.xz3d.room-footprint-authority.v1',
      containment: 'cell-center-inside-canonical-footprint',
      clippedCellCount: solution.cells.length - cells.length,
      queryOnly: true,
    },
  };
}

function mergeBoundarySegments(segments) {
  const groups = new Map();
  for (const segment of segments) {
    const intent = segment.connectionIntent;
    const key = [
      segment.kind,
      numberKey(segment.floorFt),
      segment.axis,
      numberKey(segment.at),
      numberKey(segment.z.min),
      numberKey(segment.z.max),
      segment.roomIds.join('|'),
      intent?.kind ?? '',
      intent?.theme ?? '',
      intent?.label ?? '',
      // Two doors bound to DIFFERENT inventory rows are two different objects and must never merge
      // into one opening — the survivor would carry one binding and the other door's lock, vitality
      // and rubble would be orphaned. Part of the grouping key for the same reason `label` is.
      intent?.doorInventoryId ?? '',
    ].join('::');
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(segment);
  }
  const merged = [];
  for (const group of groups.values()) {
    group.sort((left, right) => left.span.min - right.span.min || left.span.max - right.span.max);
    for (const source of group) {
      const prior = merged[merged.length - 1];
      if (prior && prior._group === group && same(prior.span.max, source.span.min)) {
        prior.span.max = source.span.max;
        prior.canonicalKey = `${numberKey(prior.floorFt)}:${prior.axis}:${numberKey(prior.at)}:${numberKey(prior.span.min)}:${numberKey(prior.span.max)}`;
        prior.id = `${prior.kind}-${hashString(prior.canonicalKey).toString(36)}`;
        continue;
      }
      const canonicalKey = `${numberKey(source.floorFt)}:${source.axis}:${numberKey(source.at)}:${numberKey(source.span.min)}:${numberKey(source.span.max)}`;
      merged.push({
        ...source,
        geometry: 'line',
        id: `${source.kind}-${hashString(canonicalKey).toString(36)}`,
        canonicalKey,
        span: { ...source.span },
        z: { ...source.z },
        _group: group,
      });
    }
  }
  return merged
    .map(({ _group, ...segment }) => segment)
    .sort((left, right) => left.canonicalKey.localeCompare(right.canonicalKey) || left.kind.localeCompare(right.kind));
}

function subtractBoundaryIntervals(span, cuts) {
  const ordered = cuts
    .map(cut => ({ min: Math.max(span.min, cut.min), max: Math.min(span.max, cut.max) }))
    .filter(cut => cut.max > cut.min + EPS)
    .sort((a, b) => a.min - b.min || a.max - b.max);
  const remaining = [];
  let cursor = span.min;
  for (const cut of ordered) {
    if (cut.min > cursor + EPS) remaining.push({ min: cursor, max: cut.min });
    cursor = Math.max(cursor, cut.max);
  }
  if (cursor < span.max - EPS) remaining.push({ min: cursor, max: span.max });
  return remaining;
}

function applyRoundedBoundaryAuthority(draft, sourceRuns, selectedRoomIds = null) {
  const roundedRoomIds = new Set(draft.rooms
    .filter(room => room.footprint.kind === 'rounded-rect' && room.enclosure !== 'none'
      && (!selectedRoomIds || selectedRoomIds.has(room.id)))
    .map(room => room.id));
  if (!roundedRoomIds.size) return sourceRuns;
  const runs = sourceRuns.filter(run => !(
    run.kind === 'wall'
    && run.roomIds.length === 1
    && roundedRoomIds.has(run.roomIds[0])
  ));
  for (const room of draft.rooms.filter(candidate => roundedRoomIds.has(candidate.id))) {
    const radii = normalizedRoomFootprint(room.footprint, room.x, room.y).cornerRadiiFt;
    const z = { min: room.floor, max: room.physicalCeilingFt ?? room.floor };
    if (!(z.max > z.min + EPS)) continue;
    const sides = [
      { id: 'west', axis: 'x', at: room.x.min, span: { min: room.y.min + radii.southWest, max: room.y.max - radii.northWest } },
      { id: 'east', axis: 'x', at: room.x.max, span: { min: room.y.min + radii.southEast, max: room.y.max - radii.northEast } },
      { id: 'south', axis: 'y', at: room.y.min, span: { min: room.x.min + radii.southWest, max: room.x.max - radii.southEast } },
      { id: 'north', axis: 'y', at: room.y.max, span: { min: room.x.min + radii.northWest, max: room.x.max - radii.northEast } },
    ];
    for (const side of sides) {
      if (!(side.span.max > side.span.min + EPS)) continue;
      const occupied = runs.filter(run => (
        run.geometry === 'line'
        && run.axis === side.axis
        && same(run.at, side.at)
        && run.roomIds.includes(room.id)
      )).map(run => run.span);
      for (const span of subtractBoundaryIntervals(side.span, occupied)) {
        const canonicalKey = `${numberKey(room.floorFt)}:${side.axis}:${numberKey(side.at)}:${numberKey(span.min)}:${numberKey(span.max)}`;
        runs.push({
          id: `wall-${hashString(canonicalKey).toString(36)}`,
          canonicalKey,
          kind: 'wall',
          floorFt: room.floorFt,
          geometry: 'line',
          axis: side.axis,
          at: side.at,
          span,
          z: { ...z },
          roomIds: [room.id],
          openingId: null,
          connectionIntent: null,
        });
      }
    }
    const corners = [
      { id: 'south-east', radius: radii.southEast, x: room.x.max - radii.southEast, y: room.y.min + radii.southEast, start: -Math.PI / 2 },
      { id: 'north-east', radius: radii.northEast, x: room.x.max - radii.northEast, y: room.y.max - radii.northEast, start: 0 },
      { id: 'north-west', radius: radii.northWest, x: room.x.min + radii.northWest, y: room.y.max - radii.northWest, start: Math.PI / 2 },
      { id: 'south-west', radius: radii.southWest, x: room.x.min + radii.southWest, y: room.y.min + radii.southWest, start: Math.PI },
    ];
    for (const corner of corners.filter(candidate => candidate.radius > EPS)) {
      const canonicalKey = `${numberKey(room.floorFt)}:arc:${room.id}:${corner.id}:${numberKey(corner.x)}:${numberKey(corner.y)}:${numberKey(corner.radius)}`;
      runs.push({
        id: `wall-${hashString(canonicalKey).toString(36)}`,
        canonicalKey,
        kind: 'wall',
        floorFt: room.floorFt,
        geometry: 'arc',
        curve: {
          kind: 'arc',
          center: { x: corner.x, y: corner.y },
          radiusFt: corner.radius,
          startRadians: corner.start,
          endRadians: corner.start + Math.PI / 2,
        },
        z: { ...z },
        roomIds: [room.id],
        openingId: null,
        connectionIntent: null,
      });
    }
  }
  return runs.sort((left, right) => (
    left.canonicalKey < right.canonicalKey ? -1 : left.canonicalKey > right.canonicalKey ? 1 : 0
  ));
}

function contourAreaFt2(contour) {
  return Math.abs(contour.reduce((area, point, index) => {
    const next = contour[(index + 1) % contour.length];
    return area + point.x * next.y - next.x * point.y;
  }, 0)) / 2;
}

function contourIdentity(contour) {
  return contour.map(point => `${numberKey(point.x)},${numberKey(point.y)}`).join('|');
}

export function convexHull2d(points) {
  const unique = Array.from(new Map(points.map(point => [
    `${numberKey(point.x)},${numberKey(point.y)}`,
    { x: point.x, y: point.y },
  ])).values()).sort((left, right) => left.x - right.x || left.y - right.y);
  if (unique.length <= 2) return unique;
  const cross = (origin, left, right) => (
    (left.x - origin.x) * (right.y - origin.y)
      - (left.y - origin.y) * (right.x - origin.x)
  );
  const lower = [];
  for (const point of unique) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], point) <= EPS) {
      lower.pop();
    }
    lower.push(point);
  }
  const upper = [];
  for (let index = unique.length - 1; index >= 0; index--) {
    const point = unique[index];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], point) <= EPS) {
      upper.pop();
    }
    upper.push(point);
  }
  lower.pop();
  upper.pop();
  return [...lower, ...upper];
}

function stairwellOpeningContour(supportPolygons, floorCutoutPolygons) {
  return convexHull2d([
    ...supportPolygons.flatMap(polygon => polygon.pointsFt),
    ...floorCutoutPolygons.flatMap(polygon => polygon.pointsFt),
  ]);
}

function clipConvexPolygon(subject, clipContour) {
  if (subject.length < 3 || clipContour.length < 3) return [];
  const clipSignedArea = clipContour.reduce((area, point, index) => {
    const next = clipContour[(index + 1) % clipContour.length];
    return area + point.x * next.y - next.x * point.y;
  }, 0) / 2;
  const orientation = clipSignedArea >= 0 ? 1 : -1;
  const inside = (point, edgeStart, edgeEnd) => orientation * (
    (edgeEnd.x - edgeStart.x) * (point.y - edgeStart.y)
      - (edgeEnd.y - edgeStart.y) * (point.x - edgeStart.x)
  ) >= -EPS;
  const intersection = (start, end, edgeStart, edgeEnd) => {
    const subjectX = end.x - start.x;
    const subjectY = end.y - start.y;
    const edgeX = edgeEnd.x - edgeStart.x;
    const edgeY = edgeEnd.y - edgeStart.y;
    const denominator = subjectX * edgeY - subjectY * edgeX;
    if (Math.abs(denominator) <= EPS) return { ...end };
    const t = ((edgeStart.x - start.x) * edgeY - (edgeStart.y - start.y) * edgeX) / denominator;
    return { x: start.x + t * subjectX, y: start.y + t * subjectY };
  };
  let output = subject.map(point => ({ x: point.x, y: point.y }));
  for (let edgeIndex = 0; edgeIndex < clipContour.length; edgeIndex++) {
    const edgeStart = clipContour[edgeIndex];
    const edgeEnd = clipContour[(edgeIndex + 1) % clipContour.length];
    const input = output;
    output = [];
    if (input.length === 0) break;
    let start = input[input.length - 1];
    for (const end of input) {
      const startInside = inside(start, edgeStart, edgeEnd);
      const endInside = inside(end, edgeStart, edgeEnd);
      if (endInside) {
        if (!startInside) output.push(intersection(start, end, edgeStart, edgeEnd));
        output.push(end);
      } else if (startInside) {
        output.push(intersection(start, end, edgeStart, edgeEnd));
      }
      start = end;
    }
  }
  return output;
}

function insetContourTowardCentroid(contour, insetFt) {
  if (contour.length < 3 || insetFt <= EPS) return contour.map(point => ({ ...point }));
  const centroid = contour.reduce((sum, point) => ({
    x: sum.x + point.x / contour.length,
    y: sum.y + point.y / contour.length,
  }), { x: 0, y: 0 });
  return contour.map(point => {
    const dx = centroid.x - point.x;
    const dy = centroid.y - point.y;
    const distance = Math.hypot(dx, dy);
    const amount = Math.min(insetFt, distance * 0.1);
    return distance > EPS
      ? { x: point.x + dx / distance * amount, y: point.y + dy / distance * amount }
      : { ...point };
  });
}

function compileTerrainCutaway(draft) {
  const groundFt = 0;
  const contour = [
    { x: draft.canvas.x.min, y: draft.canvas.y.min },
    { x: draft.canvas.x.max, y: draft.canvas.y.min },
    { x: draft.canvas.x.max, y: draft.canvas.y.max },
    { x: draft.canvas.x.min, y: draft.canvas.y.max },
  ];
  const coverage = draft.rooms
    .filter(room => room.floorFt < groundFt - EPS
      && Number.isFinite(room.physicalCeilingFt)
      && room.physicalCeilingFt >= groundFt - EPS)
    .map(room => {
      const coveredContour = roomFootprintContour(room, 12);
      return {
        roomId: room.id,
        floorFt: room.floorFt,
        contour: coveredContour,
        areaFt2: contourAreaFt2(coveredContour),
        identity: contourIdentity(coveredContour),
      };
    })
    .sort((left, right) => left.roomId.localeCompare(right.roomId));
  const sourceAreaFt2 = contourAreaFt2(contour);
  const coveredAreaFt2 = Math.min(
    sourceAreaFt2,
    coverage.reduce((sum, item) => sum + item.areaFt2, 0),
  );
  return {
    schema: 'ft.xz3d.terrain-cutaway-authority.v1',
    z: groundFt,
    contour,
    coverage,
    coverageKind: 'physical-ground-plane-contour-difference',
    coverageIdentity: hashString(JSON.stringify({
      contour: contourIdentity(contour),
      coverage: coverage.map(item => [item.roomId, item.identity]),
    })).toString(16).padStart(8, '0'),
    sourceAreaFt2,
    coveredAreaFt2,
    remainingAreaFt2: Math.max(0, sourceAreaFt2 - coveredAreaFt2),
    remainingFragmentCount: coveredAreaFt2 >= sourceAreaFt2 - EPS ? 0 : 1,
    queryOnly: true,
    persistence: 'none',
    writes: 0,
  };
}

function compileDerivedRoofs(draft) {
  const roofs = [];
  for (const sourceRoom of draft.rooms) {
    if (roomPreset(sourceRoom.presetId).structure.roofPolicy === 'none' || sourceRoom.enclosure === 'none'
      || !Number.isFinite(sourceRoom.physicalCeilingFt)
      || sourceRoom.roofRemoved === true) continue;
    const roofFt = sourceRoom.physicalCeilingFt;
    const contour = roomFootprintContour(sourceRoom, 12);
    const sourceIdentity = contourIdentity(contour);
    const coverage = draft.rooms
      .filter(room => room.id !== sourceRoom.id
        && same(room.floorFt, roofFt)
        && roomFootprintContour(room, 12).every(point => roomContainsPoint(sourceRoom, point.x, point.y)))
      .map(room => {
        const coveredContour = roomFootprintContour(room, 12);
        return {
          roomId: room.id,
          floorFt: room.floorFt,
          contour: coveredContour,
          areaFt2: contourAreaFt2(coveredContour),
          identity: contourIdentity(coveredContour),
        };
      })
      .sort((left, right) => left.roomId.localeCompare(right.roomId));
    const fullyCovered = coverage.some(item => item.identity === sourceIdentity);
    const sourceAreaFt2 = contourAreaFt2(contour);
    const coveredAreaFt2 = fullyCovered
      ? sourceAreaFt2
      : Math.min(sourceAreaFt2, coverage.reduce((sum, item) => sum + item.areaFt2, 0));
    const coverageIdentity = hashString(JSON.stringify({
      sourceRoomId: sourceRoom.id,
      sourceIdentity,
      coverage: coverage.map(item => [item.roomId, item.identity]),
    })).toString(16).padStart(8, '0');
    roofs.push({
      id: `roof-${sourceRoom.id}-${numberKey(roofFt)}`,
      sourceRoomId: sourceRoom.id,
      z: roofFt,
      surfaceId: sourceRoom.surfaceOverrides.roof ?? sourceRoom.surfaceOverrides.floor ?? roomPreset(sourceRoom.presetId).surfaces.floor,
      contour,
      coverage,
      coverageKind: 'canonical-contour-difference',
      coverageIdentity,
      sourceAreaFt2,
      coveredAreaFt2,
      remainingAreaFt2: Math.max(0, sourceAreaFt2 - coveredAreaFt2),
      remainingFragmentCount: fullyCovered ? 0 : 1,
      queryOnly: true,
      persistence: 'none',
      writes: 0,
    });
  }
  return roofs;
}

function compileVerticalConnections(draft) {
  return draft.verticalConnections.map((connection) => {
    const axes = connection.scaleXYZ ?? [1, 1, 1];
    const transformPoint = point => {
      const rotated = rotateArchitecturePointM(point.map((n, i) => n * axes[i]), connection.rotationQuarterTurns);
      return {
        x: connection.anchorFt.x + rotated.x,
        y: connection.anchorFt.y + rotated.y,
        z: connection.anchorFt.z + rotated.z,
      };
    };
    const floorDeltaFt = connection.toFloorFt - connection.fromFloorFt;
    const routes = connection.asset.geometry.routes.map(route => ({
      key: route.key,
      kind: route.kind,
      usableWidthFt: route.usableWidthM * Math.min(axes[0], axes[2]) * FEET_PER_METER,
      authoredLevelDeltaFt: route.levelDeltaM * axes[1] * FEET_PER_METER,
      floorDeltaFt,
      residualCapFt: floorDeltaFt - route.levelDeltaM * axes[1] * FEET_PER_METER,
      residualAdapterFt: floorDeltaFt - route.levelDeltaM * axes[1] * FEET_PER_METER,
      residualAdapterKind: 'terminal-edge-riser-outside-aperture',
      centerlineFt: route.centerlineM.map(transformPoint),
    }));
    const connectors = connection.asset.geometry.connectors.map(connector => ({
      key: connector.key,
      kind: connector.kind,
      positionFt: transformPoint(connector.positionM),
      widthFt: Number.isFinite(connector.widthM) ? connector.widthM * Math.min(axes[0], axes[2]) * FEET_PER_METER : null,
    }));
    const polygons = family => (connection.asset.geometry.footprints[family] ?? []).map(polygon => ({
      key: polygon.key,
      pointsFt: polygon.pointsM.map(transformPoint),
    }));
    const targetRoom = draft.rooms.find(room => room.id === connection.targetRoomId);
    const sourceRoom = draft.rooms.find(room => room.id === connection.sourceRoomId);
    if (!sourceRoom || routes.some(route => (
      route.residualCapFt < -EPS || route.residualCapFt > MAX_VERTICAL_CONNECTION_RESIDUAL_FT + EPS
    ))) {
      throw new Error(`vertical connection ${connection.id} does not fit its physical floor rise`);
    }
    const supportFootprints = polygons('support');
    if (supportFootprints.length < 1 || supportFootprints.some(polygon => (
      !polygon.pointsFt.every(point => roomContainsPoint(sourceRoom, point.x, point.y))
    ))) {
      throw new Error(`vertical connection ${connection.id} support must remain inside its source room`);
    }
    if (routes.some(route => !route.centerlineFt.every(point => (
      roomContainsPoint(sourceRoom, point.x, point.y)
    )))) {
      throw new Error(`vertical connection ${connection.id} route must remain inside its source room`);
    }
    const authoredFloorCutouts = polygons('floorCutout');
    const stairwellEnvelope = stairwellOpeningContour(supportFootprints, authoredFloorCutouts);
    const stairwellContour = targetRoom
      ? insetContourTowardCentroid(
        clipConvexPolygon(stairwellEnvelope, roomFootprintContour(targetRoom, 12)),
        0.05,
      )
      : [];
    const floorCutouts = [{
      key: `${authoredFloorCutouts.map(polygon => polygon.key).join('+')}+full-stairwell-route-envelope`,
      roomId: connection.targetRoomId,
      z: connection.toFloorFt,
      contour: stairwellContour,
      authoredCutoutKeys: authoredFloorCutouts.map(polygon => polygon.key),
      derivation: 'room-clipped-inset-support-plus-terminal-cutout-convex-hull',
      boundaryInsetFt: 0.05,
      envelopeAreaFt2: contourAreaFt2(stairwellEnvelope),
      areaFt2: contourAreaFt2(stairwellContour),
    }];
    if (!targetRoom || floorCutouts.some(cutout => (
      cutout.contour.length < 3
      || cutout.contour.some(point => !roomContainsPoint(targetRoom, point.x, point.y))
    ))) {
      throw new Error(`vertical connection ${connection.id} floor cutout must remain inside its target room`);
    }
    return {
      id: connection.id,
      kind: 'stair',
      sourceRoomId: connection.sourceRoomId,
      targetRoomId: connection.targetRoomId,
      fromFloorFt: connection.fromFloorFt,
      toFloorFt: connection.toFloorFt,
      asset: JSON.parse(JSON.stringify(connection.asset)),
      anchorFt: { ...connection.anchorFt },
      rotationQuarterTurns: connection.rotationQuarterTurns,
      routes,
      connectors,
      supportFootprints,
      floorCutouts,
      placementAuthority: 'database-authored-geometry-profile',
      scale: 1,
      scaleXYZ: [...axes],
      queryOnly: true,
      persistence: 'none',
      writes: 0,
      gameplayTraversal: connection.traversalEnabled === true,
    };
  });
}

// Imported rectangular rooms can have faces between grid lines (guest-room partitions,
// narrow posts, or an entire translated building). Their walls and apertures must use
// the authored faces, while the WFC grid continues to supply floor skin and dressing.
function authoredBoundarySegments(draft, shape, floorFt, selectedRoomIds = null) {
  const rooms = draft.rooms.filter(room => same(room.floorFt, floorFt));
  const byKey = new Map();
  for (const room of rooms.filter(room => room.enclosure !== 'none'
    && (!selectedRoomIds || selectedRoomIds.has(room.id)))) {
    for (const axis of ['x', 'y']) for (const side of ['min', 'max']) {
      const spanAxis = axis === 'x' ? 'y' : 'x';
      const faceSpan = (other, face) => {
        const range = { ...other[spanAxis] };
        if (other.footprint.kind !== 'rounded-rect') return range;
        const r = normalizedRoomFootprint(other.footprint, other.x, other.y).cornerRadiiFt;
        const corners = axis === 'x'
          ? (face === 'min' ? ['southWest', 'northWest'] : ['southEast', 'northEast'])
          : (face === 'min' ? ['southWest', 'southEast'] : ['northWest', 'northEast']);
        return { min: range.min + r[corners[0]], max: range.max - r[corners[1]] };
      };
      const at = room[axis][side], span = faceSpan(room, side);
      const neighborSpan = other => faceSpan(other, side === 'min' ? 'max' : 'min');
      const neighbors = rooms.filter(other => other.id !== room.id
        && same(other[axis][side === 'min' ? 'max' : 'min'], at)
        && neighborSpan(other).max > span.min + EPS && neighborSpan(other).min < span.max - EPS);
      const openings = shape.openings.filter(opening => opening.plane.axis === axis
        && same(opening.plane.at, at)
        && (!Number.isFinite(opening.creation?.floorFt) || same(opening.creation.floorFt, floorFt))
        && opening.span.max > span.min + EPS && opening.span.min < span.max - EPS);
      const cuts = [...new Set([span.min, span.max,
        ...neighbors.flatMap(other => [neighborSpan(other).min, neighborSpan(other).max]),
        ...openings.flatMap(opening => [opening.span.min, opening.span.max]),
      ].filter(value => value >= span.min - EPS && value <= span.max + EPS))].sort((a, b) => a - b);
      for (let index = 1; index < cuts.length; index++) {
        const min = cuts[index - 1], max = cuts[index];
        if (max - min <= EPS) continue;
        const middle = (min + max) / 2;
        const across = neighbors.filter(other => middle > neighborSpan(other).min - EPS && middle < neighborSpan(other).max + EPS);
        const participating = [room, ...across.filter(other => other.enclosure !== 'none')];
        const ceilings = participating.map(other => other.physicalCeilingFt).filter(Number.isFinite);
        const z = { min: Math.max(...participating.map(other => other.floor)), max: ceilings.length ? Math.min(...ceilings) : room.floor };
        if (!(z.max > z.min + EPS)) continue;
        const segment = { axis, at, span: { min, max } };
        const opening = openingForSegment(shape, segment, floorFt);
        const connectionIntent = opening?.creation?.connectionIntent ?? (opening ? { kind: 'opening', theme: 'manual', label: 'authored opening', queryOnly: true } : null);
        const candidate = { ...segment, floorFt, z,
          kind: connectionIntent?.kind === 'door-intent' ? 'door-intent' : (opening ? 'opening' : 'wall'),
          roomIds: [room.id, ...across.map(other => other.id)].sort(), openingId: opening?.id ?? null, connectionIntent };
        const key = `${numberKey(floorFt)}:${axis}:${numberKey(at)}:${numberKey(min)}:${numberKey(max)}`;
        byKey.set(key, candidate);
      }
    }
  }
  return [...byKey.values()];
}

function splitMergedRiseRuns(runs, shape) {
  return runs.flatMap(run => {
    if (run.kind === 'wall') return [run];
    const opening = shape.openings.find(candidate => candidate.id === run.openingId);
    if (!opening || (opening.creation?.mergedPlace !== true && opening.creation?.crossFloorShared !== true)
      || (opening.z.min <= run.z.min + EPS && opening.z.max >= run.z.max - EPS)) return [run];
    const parts = [];
    const wallPart = (min, max, suffix) => {
      if (max <= min + EPS) return;
      const canonicalKey = suffix ? `${run.canonicalKey}:${suffix}` : run.canonicalKey;
      parts.push({ ...run,
        id: `wall-${hashString(canonicalKey).toString(36)}`,
        canonicalKey, kind: 'wall', z: { min, max }, openingId: null, connectionIntent: null });
    };
    wallPart(run.z.min, Math.min(opening.z.min, run.z.max), opening.z.max < run.z.max - EPS ? 'lower' : '');
    if (opening.z.max > run.z.min + EPS && opening.z.min < run.z.max - EPS) {
      parts.push({ ...run, z: { min: Math.max(opening.z.min, run.z.min), max: Math.min(opening.z.max, run.z.max) } });
    }
    wallPart(Math.max(opening.z.max, run.z.min), run.z.max, opening.z.min > run.z.min + EPS ? 'upper' : '');
    return parts;
  });
}

/** An overlap is one place's interior, even when the raster assigns its cells to two boxes. */
function removeMergedInteriorWalls(draft, runs) {
  const byId = new Map(draft.rooms.map(room => [room.id, room]));
  const placeById = new Map(draft.rooms.map(room => [room.id, resolveRoomPlace(draft.rooms, room.id)?.id]));
  const uniqueCuts = values => values.sort((a, b) => a - b).filter((value, index, all) =>
    index === 0 || value > all[index - 1] + EPS);
  const inside = (other, point, height) => height > other.floor + EPS && height < other.ceiling - EPS
    && point.x > other.x.min + EPS && point.x < other.x.max - EPS
    && point.y > other.y.min + EPS && point.y < other.y.max - EPS
    && roomContainsPoint(other, point.x, point.y);
  return runs.flatMap(run => {
    if (run.kind !== 'wall' || !['line', 'arc'].includes(run.geometry)) return [run];
    const owners = run.roomIds.map(id => byId.get(id)).filter(Boolean);
    if (owners.length === 0 || owners.some((room, index) => owners.slice(index + 1)
      .some(other => connectionOverrideForPair(draft.connectionOverrides, room, other)))) return [run];
    const samePlace = draft.rooms.filter(other => owners.some(owner => owner.id !== other.id
      && placeById.get(owner.id) === placeById.get(other.id)
      && !connectionOverrideForPair(draft.connectionOverrides, owner, other))
      && other.floor < run.z.max - EPS && other.ceiling > run.z.min + EPS);
    if (!samePlace.length) return [run];
    if (run.geometry === 'arc') {
      const { center, radiusFt, startRadians, endRadians } = run.curve;
      const pointAt = angle => ({ x: center.x + radiusFt * Math.cos(angle),
        y: center.y + radiusFt * Math.sin(angle) });
      const angles = [startRadians, endRadians], heights = [run.z.min, run.z.max];
      for (const other of samePlace) {
        heights.push(clamp(other.floor, run.z.min, run.z.max), clamp(other.ceiling, run.z.min, run.z.max));
        const contour = roomFootprintContour(other, 16);
        for (let index = 0; index < contour.length; index++) {
          const a = contour[index], b = contour[(index + 1) % contour.length];
          const dx = b.x - a.x, dy = b.y - a.y;
          const ox = a.x - center.x, oy = a.y - center.y;
          const qa = dx * dx + dy * dy, qb = 2 * (ox * dx + oy * dy);
          const qc = ox * ox + oy * oy - radiusFt * radiusFt;
          const discriminant = qb * qb - 4 * qa * qc;
          if (qa <= EPS || discriminant < 0) continue;
          for (const t of [(-qb - Math.sqrt(discriminant)) / (2 * qa),
            (-qb + Math.sqrt(discriminant)) / (2 * qa)]) {
            if (t < -EPS || t > 1 + EPS) continue;
            const angle = Math.atan2(oy + t * dy, ox + t * dx);
            for (const shifted of [angle - Math.PI * 2, angle, angle + Math.PI * 2]) {
              if (shifted > startRadians + EPS && shifted < endRadians - EPS) angles.push(shifted);
            }
          }
        }
      }
      const arcs = uniqueCuts(angles), bands = uniqueCuts(heights), kept = [];
      for (let ai = 1; ai < arcs.length; ai++) for (let zi = 1; zi < bands.length; zi++) {
        const start = arcs[ai - 1], end = arcs[ai];
        const z = { min: bands[zi - 1], max: bands[zi] };
        if (end <= start + EPS || z.max <= z.min + EPS
          || samePlace.some(other => inside(other, pointAt((start + end) / 2), (z.min + z.max) / 2))) continue;
        if (arcs.length === 2 && bands.length === 2) { kept.push(run); continue; }
        const canonicalKey = `${run.canonicalKey}:arc${numberKey(start)}-${numberKey(end)}:z${numberKey(z.min)}-${numberKey(z.max)}`;
        kept.push({ ...run, id: `wall-${hashString(canonicalKey).toString(36)}`,
          canonicalKey, curve: { ...run.curve, startRadians: start, endRadians: end }, z });
      }
      return kept;
    }
    const others = samePlace.filter(other =>
      other[run.axis].min < run.at - EPS && other[run.axis].max > run.at + EPS);
    if (!others.length) return [run];
    const spanAxis = run.axis === 'x' ? 'y' : 'x';
    const spanCuts = [run.span.min, run.span.max];
    const zCuts = [run.z.min, run.z.max];
    for (const other of others) {
      zCuts.push(clamp(other.floor, run.z.min, run.z.max), clamp(other.ceiling, run.z.min, run.z.max));
      const contour = roomFootprintContour(other, 16);
      for (let index = 0; index < contour.length; index++) {
        const a = contour[index], b = contour[(index + 1) % contour.length];
        const delta = b[run.axis] - a[run.axis];
        if (Math.abs(delta) < EPS) continue;
        const t = (run.at - a[run.axis]) / delta;
        if (t < -EPS || t > 1 + EPS) continue;
        spanCuts.push(clamp(a[spanAxis] + t * (b[spanAxis] - a[spanAxis]), run.span.min, run.span.max));
      }
    }
    const spans = uniqueCuts(spanCuts), heights = uniqueCuts(zCuts);
    const kept = [];
    for (let si = 1; si < spans.length; si++) for (let zi = 1; zi < heights.length; zi++) {
      const span = { min: spans[si - 1], max: spans[si] };
      const z = { min: heights[zi - 1], max: heights[zi] };
      if (span.max <= span.min + EPS || z.max <= z.min + EPS) continue;
      const coordinate = (span.min + span.max) / 2;
      const point = run.axis === 'x' ? { x: run.at, y: coordinate } : { x: coordinate, y: run.at };
      const height = (z.min + z.max) / 2;
      if (others.some(other => inside(other, point, height))) continue;
      if (spans.length === 2 && heights.length === 2) { kept.push(run); continue; }
      const canonicalKey = `${numberKey(run.floorFt)}:${run.axis}:${numberKey(run.at)}:${numberKey(span.min)}:${numberKey(span.max)}`
        + (heights.length > 2 ? `:z${numberKey(z.min)}-${numberKey(z.max)}` : '');
      kept.push({ ...run, id: `wall-${hashString(canonicalKey).toString(36)}`,
        canonicalKey, span, z });
    }
    return kept;
  });
}

function boundaryRecordsFromRuns(draft, shape, runs) {
  const openingsById = new Map(shape.openings.map(opening => [opening.id, opening]));
  const wallRuns = runs.filter(run => run.kind === 'wall');
  const connectionIntents = runs.filter(run => run.kind !== 'wall').map(run => ({
    id: run.openingId ?? run.id,
    kind: run.kind,
    theme: run.connectionIntent?.theme ?? 'manual',
    label: run.connectionIntent?.label ?? 'authored opening',
    doorItemId: run.connectionIntent?.doorItemId ?? null,
    // ⚠ THIS LIST IS A WHITELIST, NOT A PASSTHROUGH — a field missing here is silently DROPPED on
    // every authoring round-trip. doorInventoryId binds the opening to the door's real inventory
    // row (its lock, vitality and destruction); without this line, opening a backfilled scene in
    // the builder and saving would unbind all of its doors, and they would fail SILENTLY back to
    // the unbound behaviour with nobody told. (2026-08-30)
    doorInventoryId: run.connectionIntent?.doorInventoryId ?? null,
    structureState: openingsById.get(run.openingId)?.structureState,
    doorMeshId: run.connectionIntent?.doorMeshId ?? null,
    doorMeshAvailable: run.connectionIntent?.doorMeshAvailable === true,
    overrideMode: run.connectionIntent?.overrideMode ?? 'manual',
    queryOnly: true,
    floorFt: run.floorFt,
    axis: run.axis,
    at: run.at,
    span: { ...run.span },
    z: { ...run.z },
    roomIds: [...run.roomIds],
  }));
  const sharedGroups = new Map();
  for (const run of runs.filter(candidate => candidate.roomIds.length > 1)) {
    const key = `${numberKey(run.floorFt)}:${run.axis}:${numberKey(run.at)}:${run.roomIds.join('|')}`;
    if (!sharedGroups.has(key)) sharedGroups.set(key, []);
    sharedGroups.get(key).push(run);
  }
  const sharedBoundaries = Array.from(sharedGroups.entries()).map(([key, segments]) => ({
    id: `shared-${hashString(key).toString(36)}`,
    floorFt: segments[0].floorFt,
    axis: segments[0].axis,
    at: segments[0].at,
    span: {
      min: Math.min(...segments.map(segment => segment.span.min)),
      max: Math.max(...segments.map(segment => segment.span.max)),
    },
    roomIds: [...segments[0].roomIds],
    segments: segments.map(segment => ({
      id: segment.id,
      kind: segment.kind,
      span: { ...segment.span },
      z: { ...segment.z },
      openingId: segment.openingId,
    })),
  })).sort((left, right) => left.id.localeCompare(right.id));
  for (const boundary of sharedBoundaries) {
    const intent = connectionIntents.find(candidate => (
      same(candidate.floorFt, boundary.floorFt)
      && candidate.axis === boundary.axis
      && same(candidate.at, boundary.at)
      && candidate.roomIds.join('|') === boundary.roomIds.join('|')
    ));
    const override = draft.connectionOverrides.find(candidate => (
      candidate.roomIds.join('|') === boundary.roomIds.join('|')
    ));
    boundary.connectionIntentId = intent?.id ?? null;
    boundary.mode = override?.mode ?? 'auto';
    boundary.doorItemId = override?.doorItemId ?? null;
    boundary.doorMeshId = override?.doorMeshId ?? null;
    boundary.queryOnly = true;
    if (intent) intent.sharedBoundaryId = boundary.id;
  }
  // Room ownership can split one authored aperture at a rounded neighbour's face endpoint.
  // Keep those boundary segments, but give the opening one assembly per floor and plane.
  // Preserve the physical span union, height overlap and room ownership of those pieces.
  const doorsByOpening = new Map();
  const logicalIntents = connectionIntents.filter(intent => {
    if (intent.kind !== 'door-intent') return true;
    const key = `${intent.id}:${numberKey(intent.floorFt)}:${intent.axis}:${numberKey(intent.at)}`;
    const prior = doorsByOpening.get(key);
    if (!prior) {
      doorsByOpening.set(key, intent);
      return true;
    }
    prior.span.min = Math.min(prior.span.min, intent.span.min);
    prior.span.max = Math.max(prior.span.max, intent.span.max);
    prior.z.min = Math.max(prior.z.min, intent.z.min);
    prior.z.max = Math.min(prior.z.max, intent.z.max);
    prior.roomIds = [...new Set([...prior.roomIds, ...intent.roomIds])].sort();
    prior.sharedBoundaryId ??= intent.sharedBoundaryId;
    return false;
  });
  return { wallRuns, connectionIntents: logicalIntents, sharedBoundaries };
}

function floorNeedsAuthoredBoundary(draft, shape, floorFt, cellFt) {
  // A shared cut can take an off-grid endpoint from a room on ANOTHER floor
  // (or its rounded straight side). Both floors must honor that exact endpoint.
  const offGrid = value => !same(value / cellFt, Math.round(value / cellFt));
  if (shape.openings.some(opening => (opening.creation?.mergedPlace === true || opening.creation?.crossFloorShared === true)
    && [opening.span.min, opening.span.max].some(offGrid)
    && opening.creation?.rooms?.some(id => draft.rooms.some(room => room.id === id && same(room.floorFt, floorFt))))) return true;
  return draft.rooms.some(room => same(room.floorFt, floorFt)
    && room.footprint.kind === 'rect' && room.enclosure !== 'none'
    && ['x', 'y'].some(axis => ['min', 'max'].some(side => (
      !same(room[axis][side] / cellFt, Math.round(room[axis][side] / cellFt))
    ))));
}

/** Compile only selected rooms' wall faces from held authored geometry and cells. */
export function compileRoomBoundaryPatch(draft, shape, roomIds, solution = null) {
  const selected = new Set(roomIds);
  const floors = [...new Set(draft.rooms.filter(room => selected.has(room.id)).map(room => room.floorFt))];
  const exactFloors = solution ? floors.filter(floor => floorNeedsAuthoredBoundary(draft, shape, floor, solution.cellFt)) : floors;
  let segments = [
    ...(solution ? cellBoundarySegments(draft, shape, solution, selected).filter(run =>
      !exactFloors.some(floor => same(run.floorFt, floor))) : []),
    ...exactFloors.flatMap(floor => authoredBoundarySegments(draft, shape, floor, selected)),
  ];
  // A selected room may own only part (or none) of a door's face. Rebuild all
  // pieces of each touched door on this floor, so its physical span/height match
  // a full compile. Neighbour walls and unrelated doors stay outside the patch.
  for (const floor of floors) {
    const floorSelected = new Set(draft.rooms.filter(room => selected.has(room.id)
      && same(room.floorFt, floor)).map(room => room.id));
    // Older manual openings have no creation.rooms. An unenclosed neighbour
    // still owns the opposite room's face, even though it emits no wall itself.
    const touchesFace = (opening, room) => {
      const face = room[opening.plane.axis];
      const span = room[opening.plane.axis === 'x' ? 'y' : 'x'];
      return (same(face.min, opening.plane.at) || same(face.max, opening.plane.at))
        && span.min < opening.span.max - EPS && span.max > opening.span.min + EPS;
    };
    const touchedDoors = shape.openings.filter(opening => (
      opening.creation?.connectionIntent?.kind === 'door-intent'
      && (opening.creation.rooms?.some(id => floorSelected.has(id))
        || segments.some(run => run.openingId === opening.id && same(run.floorFt, floor))
        || draft.rooms.some(room => floorSelected.has(room.id) && touchesFace(opening, room)))
    ));
    if (!touchedDoors.length) continue;
    const doorIds = new Set(touchedDoors.map(opening => opening.id));
    const pieceRooms = new Set(segments.filter(run => doorIds.has(run.openingId)
      && same(run.floorFt, floor)).flatMap(run => run.roomIds));
    const doorRooms = new Set(draft.rooms.filter(room => same(room.floorFt, floor)
      && (pieceRooms.has(room.id)
        || touchedDoors.some(opening => opening.creation.rooms?.includes(room.id)
          || touchesFace(opening, room)))).map(room => room.id));
    if (!doorRooms.size) continue;
    const doorSegments = exactFloors.some(candidate => same(candidate, floor))
      ? authoredBoundarySegments(draft, shape, floor, doorRooms)
      : cellBoundarySegments(draft, shape, solution, doorRooms);
    segments = segments.filter(run => !(run.kind === 'door-intent'
      && same(run.floorFt, floor) && doorIds.has(run.openingId)));
    const ownedDoorIds = new Set(touchedDoors.filter(opening => (
      doorSegments.some(run => run.openingId === opening.id && run.roomIds.some(id => floorSelected.has(id)))
    )).map(opening => opening.id));
    segments.push(...doorSegments.filter(run => run.kind === 'door-intent' && ownedDoorIds.has(run.openingId)));
  }
  const runs = removeMergedInteriorWalls(draft, applyRoundedBoundaryAuthority(draft,
    splitMergedRiseRuns(mergeBoundarySegments(segments), shape), selected));
  return boundaryRecordsFromRuns(draft, shape, runs);
}

function cellBoundarySegments(draft, shape, solution, selectedRoomIds = null) {
  const roomById = new Map(draft.rooms.map(room => [room.id, room]));
  const cellsByKey = selectedRoomIds ? null : new Map(solution.cells.map(cell => [cell.key, cell]));
  const rawByCanonicalKey = new Map();
  const directions = [
    { dx: 0, dy: 1 },
    { dx: 1, dy: 0 },
    { dx: 0, dy: -1 },
    { dx: -1, dy: 0 },
  ];
  for (const cell of solution.cells) {
    if (selectedRoomIds && !selectedRoomIds.has(cell.footprintRoomId)) continue;
    const owner = roomById.get(cell.footprintRoomId);
    if (!owner || owner.enclosure === 'none') continue;
    for (let direction = 0; direction < directions.length; direction++) {
      if (cell.edges[direction] === 'open' && !selectedRoomIds) continue;
      const segment = segmentForCellEdge(cell, direction, solution.cellFt);
      const offset = directions[direction];
      const adjacent = cellsByKey?.get(floorCellKey(cell.floorFt,
        `${cell.ix + offset.dx},${cell.iy + offset.dy}`));
      const otherOwner = selectedRoomIds
        ? roomForPoint(draft.rooms, cell.cx + offset.dx * solution.cellFt,
          cell.cy + offset.dy * solution.cellFt, cell.floorFt)
        : (adjacent ? roomById.get(adjacent.footprintRoomId) : null);
      if (otherOwner?.id === owner.id) continue;
      const roomIds = [owner.id, otherOwner?.id].filter(Boolean).sort();
      const participatingRooms = draft.rooms.filter(room => roomIds.includes(room.id) && room.enclosure !== 'none');
      if (participatingRooms.length === 0) continue;
      const opening = openingForSegment(shape, segment, cell.floorFt);
      // On a scoped delta the held edge is old state, so a new opening can claim it.
      if (cell.edges[direction] === 'open' && !opening) continue;
      const connectionIntent = opening?.creation?.connectionIntent ?? (opening ? {
        kind: 'opening', theme: 'manual', label: 'authored opening', queryOnly: true,
      } : null);
      const physicalCeilings = participatingRooms
        .map(room => room.physicalCeilingFt)
        .filter(value => Number.isFinite(value));
      const z = {
        min: Math.max(...participatingRooms.map(room => room.floor)),
        max: physicalCeilings.length
          ? Math.min(...physicalCeilings)
          : Math.max(...participatingRooms.map(room => room.floor)),
      };
      if (!(z.max > z.min + EPS)) continue;
      const canonicalKey = `${numberKey(cell.floorFt)}:${segment.axis}:${numberKey(segment.at)}:${numberKey(segment.span.min)}:${numberKey(segment.span.max)}`;
      const candidate = {
        kind: connectionIntent?.kind === 'door-intent' ? 'door-intent' : (opening ? 'opening' : 'wall'),
        floorFt: cell.floorFt,
        axis: segment.axis,
        at: segment.at,
        span: { ...segment.span },
        z,
        roomIds,
        openingId: opening?.id ?? null,
        connectionIntent,
      };
      const existing = rawByCanonicalKey.get(canonicalKey);
      if (!existing) {
        rawByCanonicalKey.set(canonicalKey, candidate);
      } else {
        existing.roomIds = Array.from(new Set([...existing.roomIds, ...roomIds])).sort();
        existing.z.min = Math.min(existing.z.min, z.min);
        existing.z.max = Math.max(existing.z.max, z.max);
        if (candidate.kind !== 'wall') {
          existing.kind = candidate.kind;
          existing.openingId = candidate.openingId;
          existing.connectionIntent = candidate.connectionIntent;
        }
      }
    }
  }
  return [...rawByCanonicalKey.values()];
}

function compileRoomGroup(draft, shape, solution) {
  const rawSegments = cellBoundarySegments(draft, shape, solution);
  const exactFloors = [...new Set(draft.rooms.map(room => room.floorFt))]
    .filter(floor => floorNeedsAuthoredBoundary(draft, shape, floor, solution.cellFt));
  const boundarySegments = [...rawSegments.filter(run => !exactFloors.some(floor => same(run.floorFt, floor))),
    ...exactFloors.flatMap(floor => authoredBoundarySegments(draft, shape, floor))];
  const runs = removeMergedInteriorWalls(draft, applyRoundedBoundaryAuthority(
    draft,
    splitMergedRiseRuns(mergeBoundarySegments(boundarySegments), shape),
  ));
  const { wallRuns, connectionIntents, sharedBoundaries } = boundaryRecordsFromRuns(draft, shape, runs);
  const floors = draft.rooms.map(room => ({
    id: `floor-${room.id}`,
    roomId: room.id,
    floorFt: room.floorFt,
    presetId: room.presetId,
    environment: room.environment,
    kind: room.environment === 'exterior-open' ? 'open-ground' : 'room-floor',
    surfaceId: room.surfaceOverrides.floor ?? roomPreset(room.presetId).surfaces.floor,
    x: { ...room.x },
    y: { ...room.y },
    footprint: { ...room.footprint },
    z: room.floor,
  }));
  const structuralSpans = draft.rooms.map(room => ({
    id: `structure-${room.id}`,
    roomId: room.id,
    floorFt: room.floorFt,
    minZ: room.floor,
    maxZ: room.physicalCeilingFt ?? room.floor,
    physicalCeiling: room.physicalCeilingFt !== null,
  }));
  const roofs = compileDerivedRoofs(draft);
  const terrainCutaway = compileTerrainCutaway(draft);
  const verticalConnections = compileVerticalConnections(draft);
  return {
    queryOnly: true,
    canvas: JSON.parse(JSON.stringify(draft.canvas)),
    floors,
    wallRuns,
    sharedBoundaries,
    connectionIntents,
    verticalConnections,
    structuralSpans,
    roofs,
    terrainCutaway,
    policies: {
      interior: 'preset-owned-floor-walls-and-physical-ceiling',
      exteriorOpen: 'terrain-intent-without-enclosure-or-hard-sky-ceiling',
      gameplayAuthority: 'none-presentation-only',
      verticalConnections: 'authored-geometry-query-only-no-gameplay-traversal-or-persistence',
      roofs: 'derived-canonical-coverage-query-only-no-persistence',
      terrainCutaway: 'active-basement-canonical-footprint-difference-query-only',
    },
  };
}

export function resolveDynamicVerticalEnvelope(input = {}) {
  const structuralSpans = Array.isArray(input.structuralSpans) ? input.structuralSpans : [];
  const entityExtents = Array.isArray(input.entityExtents) ? input.entityExtents : [];
  const previewExtents = Array.isArray(input.previewExtents) ? input.previewExtents : [];
  const finiteExtents = values => values.filter(extent => (
    extent && Number.isFinite(extent.minZ) && Number.isFinite(extent.maxZ) && extent.maxZ >= extent.minZ
  ));
  const structures = finiteExtents(structuralSpans);
  const relevant = finiteExtents([...entityExtents, ...previewExtents]);
  const structuralMinZ = structures.length ? Math.min(...structures.map(extent => extent.minZ)) : 0;
  const structuralMaxZ = structures.length ? Math.max(...structures.map(extent => extent.maxZ)) : 0;
  const contentMinZ = relevant.length ? Math.min(structuralMinZ, ...relevant.map(extent => extent.minZ)) : structuralMinZ;
  const contentMaxZ = relevant.length ? Math.max(structuralMaxZ, ...relevant.map(extent => extent.maxZ)) : structuralMaxZ;
  const paddingFt = clamp(finiteNumber(input.paddingFt, 5), 0, 100);
  let minZ = contentMinZ - paddingFt;
  let maxZ = contentMaxZ + paddingFt;
  if (input.activeGesture === true && input.previousEnvelope
    && Number.isFinite(input.previousEnvelope.minZ) && Number.isFinite(input.previousEnvelope.maxZ)) {
    minZ = Math.min(minZ, input.previousEnvelope.minZ);
    maxZ = Math.max(maxZ, input.previousEnvelope.maxZ);
  }
  return {
    minZ,
    maxZ,
    structuralMinZ,
    structuralMaxZ,
    expandedDownward: contentMinZ < structuralMinZ - EPS,
    expandedUpward: contentMaxZ > structuralMaxZ + EPS,
    paddingFt,
    sourceCount: {
      structures: structures.length,
      entities: finiteExtents(entityExtents).length,
      previews: finiteExtents(previewExtents).length,
    },
    queryOnly: true,
    writes: 0,
    clampsGameplayState: false,
  };
}

function compileFloorBandSolution(draft, shape, tileSet) {
  const floorSolutions = [];
  const floors = Array.from(new Set(draft.rooms.map(room => room.floorFt))).sort((a, b) => a - b);
  for (const floorFt of floors) {
    const floorKey = numberKey(floorFt);
    const roomIds = new Set(draft.rooms
      .filter(room => same(room.floorFt, floorFt))
      .map(room => room.id));
    const floorRooms = draft.rooms.filter(room => roomIds.has(room.id));
    const floorShape = {
      ...shape,
      boxes: shape.boxes.filter(box => roomIds.has(box.id)),
      openings: (shape.openings ?? []).filter(opening => {
        if ((opening.creation?.mergedPlace === true || opening.creation?.crossFloorShared === true)
          && Array.isArray(opening.creation.rooms)) {
          return opening.creation.rooms.some(roomId => roomIds.has(roomId));
        }
        if (Number.isFinite(opening.creation?.floorFt)) return same(opening.creation.floorFt, floorFt);
        if (Array.isArray(opening.creation?.rooms)) {
          return opening.creation.rooms.every(roomId => roomIds.has(roomId));
        }
        return floorRooms.some(room => (
          opening.z.max > room.floor + EPS
          && opening.z.min < room.ceiling - EPS
        ));
      }),
    };
    validateShape(floorShape);
    const solved = solveShapeSkin(floorShape, {
      cellFt: draft.gridFt,
      seed: `${draft.seed}|${floorKey}`,
      placements: {},
      tileSet,
    });
    const validation = validateSolution(solved);
    if (!validation.ok) {
      throw new Error(`creation compile failed for floor ${floorKey} ft: ${validation.errors.join('; ')}`);
    }
    const clipped = clipSolutionToRoomFootprints(solved, draft, floorShape, floorFt);
    floorSolutions.push({
      floorFt,
      origin: { ...clipped.origin },
      cellCount: clipped.cells.length,
      clippedCellCount: clipped.footprintAuthority.clippedCellCount,
      deterministicSeed: `${draft.seed}|${floorKey}`,
      solution: clipped,
    });
  }
  return {
    shape,
    cellFt: draft.gridFt,
    origin: { x: draft.canvas.x.min, y: draft.canvas.y.min },
    seed: draft.seed,
    placements: {},
    tileSet,
    cells: floorSolutions.flatMap(receipt => receipt.solution.cells),
    grid: Object.assign({}, ...floorSolutions.map(receipt => receipt.solution.grid)),
    floorSolutions: floorSolutions.map(({ solution, ...receipt }) => receipt),
    footprintAuthority: {
      schema: 'ft.xz3d.room-footprint-authority.v1',
      containment: 'cell-center-inside-canonical-footprint',
      clippedCellCount: floorSolutions.reduce((sum, receipt) => sum + receipt.clippedCellCount, 0),
      queryOnly: true,
    },
  };
}

/** Compile an opening edit against resident geometry, without solving floors or copying the draft.
 * Only a suppression flag may change on an existing room; all geometry is held input. */
export function compileCreationOpeningDelta(previous, candidate, held = previous.draft) {
  if (candidate.rooms.length !== held.rooms.length) throw new Error('opening-delta:room-set-changed');
  const affected = new Set();
  const rooms = candidate.rooms.map((room, index) => {
    const before = held.rooms[index];
    if (room === before) return room;
    if (Object.keys(room).some(key => key !== 'windowSuppressedFaces' && room[key] !== before[key])
      || Object.keys(before).some(key => key !== 'windowSuppressedFaces' && room[key] !== before[key])) {
      throw new Error('opening-delta:room-geometry-changed');
    }
    affected.add(room.id);
    return room;
  });
  const priorManual = new Map(held.manualOpenings.map(opening => [opening.id, opening]));
  const nextIds = new Set(candidate.manualOpenings.map(opening => opening.id));
  const changed = new Set(held.manualOpenings.filter(opening => !nextIds.has(opening.id)).map(opening => opening.id));
  const manualOpenings = candidate.manualOpenings.map(opening => {
    if (priorManual.get(opening.id) === opening) return opening;
    changed.add(opening.id);
    // Binding receipts already carry normalized geometry. Keep their opaque authored fields too.
    if (priorManual.has(opening.id)) return opening;
    return stampWindowFacing({ ...opening, ...normalizeCreationOpening(opening, rooms) }, rooms);
  });
  const authored = previous.authoringShape ?? previous.shape;
  const oldShapeOpenings = new Map(authored.openings.map(opening => [opening.id, opening]));
  for (const opening of [...held.manualOpenings, ...manualOpenings]) if (changed.has(opening.id)) {
    for (const id of opening.creation?.rooms ?? []) affected.add(id);
    // A hand opening can cut both sides of a shared wall, including older openings without room ids.
    const along = opening.plane.axis === 'x' ? 'y' : 'x';
    for (const room of rooms) if ((same(room[opening.plane.axis].min, opening.plane.at)
      || same(room[opening.plane.axis].max, opening.plane.at))
      && room[along].min < opening.span.max - EPS && room[along].max > opening.span.min + EPS
      && room.floor < opening.z.max - EPS && room.ceiling > opening.z.min + EPS) affected.add(room.id);
  }
  if (!changed.size && !affected.size) return { next: previous, affectedRoomIds: [], changedOpeningIds: [] };
  const draft = { ...candidate, rooms: candidate.rooms === held.rooms ? held.rooms : rooms, manualOpenings };
  const nextManual = new Map(manualOpenings.map(opening => [opening.id, opening]));
  let openings = authored.openings.flatMap(opening => !changed.has(opening.id) ? [opening]
    : nextManual.has(opening.id) ? [nextManual.get(opening.id)] : []);
  openings.push(...manualOpenings.filter(opening => !oldShapeOpenings.has(opening.id)));
  if (draft.generateOpenings !== false) {
    const generatedHere = opening => opening.creation?.automatic === true
      && opening.creation?.connectionIntent?.kind === 'window-intent'
      && opening.creation.rooms?.some(id => affected.has(id));
    openings = openings.filter(opening => !generatedHere(opening));
    const generated = automaticWindowOpenings(rooms, draft.gridFt, openings, draft.seed,
      { underground: draft.underground, roomIds: [...affected] });
    openings.push(...generated.map(opening => oldShapeOpenings.get(opening.id) ?? stampWindowFacing(opening, rooms)));
  }
  const patchShape = source => ({ ...source,
    boxes: source.boxes.map(box => {
      const room = rooms.find(room => room.id === box.id);
      if (!room || room === held.rooms.find(before => before.id === box.id)) return box;
      const creation = { ...box.creation };
      if (room.windowSuppressedFaces?.length) creation.windowSuppressedFaces = room.windowSuppressedFaces;
      else delete creation.windowSuppressedFaces;
      return { ...box, creation };
    }),
    openings: source === authored ? openings : [
      ...source.openings.flatMap(opening => {
        const next = openings.find(next => next.id === opening.id);
        return next ? [changed.has(opening.id) ? next : opening] : [];
      }),
      ...openings.filter(opening => !source.openings.some(before => before.id === opening.id)),
    ],
  });
  const authoringShape = patchShape(authored);
  const shape = authored === previous.shape ? authoringShape : patchShape(previous.shape);
  // Decide feasibility before changing the controller, renderer or save queue.
  validateShape(authoringShape);
  const patch = compileRoomBoundaryPatch(draft, shape, [...affected]);
  const retain = (before, after) => {
    const unchanged = [], matches = new Map();
    for (const row of before) {
      if (row.roomIds.some(id => affected.has(id))) matches.set(JSON.stringify(row), row);
      else unchanged.push(row);
    }
    return [...unchanged, ...after.map(row => matches.get(JSON.stringify(row)) ?? row)];
  };
  const roomGroup = { ...previous.roomGroup,
    wallRuns: retain(previous.roomGroup.wallRuns, patch.wallRuns),
    connectionIntents: retain(previous.roomGroup.connectionIntents, patch.connectionIntents),
    sharedBoundaries: retain(previous.roomGroup.sharedBoundaries, patch.sharedBoundaries),
  };
  const next = { ...previous, draft, shape, authoringShape, roomGroup,
    summary: { ...previous.summary, openingCount: shape.openings.length,
      automaticOpeningCount: shape.openings.filter(opening => opening.creation?.automatic === true).length,
      wallRunCount: roomGroup.wallRuns.length, sharedBoundaryCount: roomGroup.sharedBoundaries.length,
      connectionIntentCount: roomGroup.connectionIntents.length } };
  return { next, affectedRoomIds: [...affected], changedOpeningIds: [...changed] };
}

export function compileCreationDraft(input, options = {}) {
  const draft = normalizeCreationDraft(input);
  const shape = shapeFromCreationDraft(draft, options);
  const tileSet = makeTileSet({
    floorVariants: 5,
    wallVariants: 3,
    clutter: 0.55,
    waterTee: false,
    waterWeight: 0.65,
    environmentFlavor: false,
    environmentDressing: false,
  });
  const solution = compileFloorBandSolution(draft, shape, tileSet);
  const roomGroup = compileRoomGroup(draft, shape, solution);
  const dressingPlacements = compileDressing(draft, solution);
  const roomCellCounts = draft.rooms.map(room => ({
    roomId: room.id,
    floorFt: room.floorFt,
    cellCount: solution.cells.filter(cell => (
      roomForPoint(draft.rooms, cell.cx, cell.cy, cell.floorFt)?.id === room.id
    )).length,
  }));
  return {
    schema: CREATION_SCHEMA,
    draft,
    shape,
    authoringShape: shape,
    spatialAuthority: {
      schema: 'ft.xz3d.room-footprint-authority.v1',
      queryOnly: true,
      persistence: 'none',
      encounterShapeV1Projection: 'axis-aligned-bounds-compatibility-only',
      rooms: draft.rooms.map(room => ({
        roomId: room.id,
        floorFt: room.floorFt,
        kind: room.footprint.kind,
        cornerRadiusFt: room.footprint.cornerRadiusFt,
        cornerRadiiFt: { ...room.footprint.cornerRadiiFt },
        bounds: { x: { ...room.x }, y: { ...room.y } },
        contour: roomFootprintContour(room, 12),
      })),
    },
    solution,
    roomGroup,
    dynamicVerticalEnvelope: resolveDynamicVerticalEnvelope({
      structuralSpans: roomGroup.structuralSpans,
      paddingFt: 5,
    }),
    architecturalDressing: draft.architecturalDressing.map(row => JSON.parse(JSON.stringify(row))),
    dressingPlacements,
    summary: {
      roomCount: draft.rooms.length,
      roundedRoomCount: draft.rooms.filter(room => room.footprint.kind === 'rounded-rect').length,
      floorCount: new Set(draft.rooms.map(room => numberKey(room.floorFt))).size,
      roofCount: roomGroup.roofs.length,
      basementRoomCount: draft.rooms.filter(room => room.floorFt < -EPS).length,
      openingCount: shape.openings.length,
      automaticOpeningCount: shape.openings.filter(opening => opening.creation?.automatic === true).length,
      cellCount: solution.cells.length,
      dressingCount: dressingPlacements.length,
      wallRunCount: roomGroup.wallRuns.length,
      sharedBoundaryCount: roomGroup.sharedBoundaries.length,
      connectionIntentCount: roomGroup.connectionIntents.length,
      verticalConnectionCount: roomGroup.verticalConnections.length,
      roomCellCounts,
      deterministicKey: hashString(JSON.stringify({
        seed: draft.seed,
        rooms: draft.rooms,
        openings: shape.openings,
        roomGroup,
        grid: solution.grid,
        architecturalDressing: draft.architecturalDressing,
      buildingRoofs: draft.buildingRoofs,
        dressingPlacements,
      })).toString(16).padStart(8, '0'),
    },
  };
}

export function shapeContainsAllCells(compiled) {
  return compiled.solution.cells.every(cell => (
    roomForPoint(compiled.draft.rooms, cell.cx, cell.cy, cell.floorFt)
  ));
}

const METERS_TO_FEET = 3.280839895013123;
/** Tallest 'surface' support whose bounds top is still its tabletop (the kit's tables/podiums top out at 1.39 m). */
const SURFACE_SEAT_MAX_HEIGHT_M = 1.5;

function objectValue(value, label) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`${label} must be an object`);
  }
  return value;
}

function boundsVector(value, label) {
  const source = objectValue(value, label);
  const result = {
    x: finiteNumber(source.x, Number.NaN),
    y: finiteNumber(source.y, Number.NaN),
    z: finiteNumber(source.z, Number.NaN),
  };
  if (!Object.values(result).every(Number.isFinite)) throw new Error(`${label} must contain finite x/y/z`);
  return result;
}

function normalizeZoneCatalog(input) {
  const catalog = objectValue(input, 'zone generation catalog');
  if (catalog.schema !== 'ft.xz3d.zone-generation-catalog.v2') {
    throw new Error('zone generation catalog schema mismatch');
  }
  if (catalog.generationProfilesEnabled !== false) {
    throw new Error('query-only zone generation requires disabled production profiles');
  }
  const revision = String(catalog.revision ?? '').trim();
  if (!revision) throw new Error('zone generation catalog revision is required');
  // DAFT3D S5: the live registry ships hundreds of candidates; the injected pilot fixture still
  // ships a handful. One bounded contract for both.
  if (!Array.isArray(catalog.candidates) || catalog.candidates.length < 1 || catalog.candidates.length > 4096) {
    throw new Error('zone generation catalog requires 1-4096 candidates');
  }
  const seen = new Set();
  const candidates = catalog.candidates.map((value) => {
    const candidate = objectValue(value, 'zone generation candidate');
    const id = String(candidate.id ?? '').trim();
    if (!id || seen.has(id)) throw new Error(`zone generation candidate id is missing or duplicated: ${id}`);
    seen.add(id);
    const localMin = boundsVector(candidate.authoredBoundsM?.min, `${id} authoredBoundsM.min`);
    const localMax = boundsVector(candidate.authoredBoundsM?.max, `${id} authoredBoundsM.max`);
    if (!(localMax.x > localMin.x && localMax.y > localMin.y && localMax.z > localMin.z)) {
      throw new Error(`${id} authored bounds must have positive extents`);
    }
    // Two support seats (vocabulary §3): a reviewed contact plane measured from the position
    // buffer (pilot fixture), or the profile-authored bounds of a live registry mesh / standee
    // (bottom-centre origin, contact at the bounds floor).
    const seat = String(candidate.support?.seat ?? '');
    if (candidate.support?.kind !== 'floor' || !['authored-contact-plane', 'profile-authored-bounds'].includes(seat)) {
      throw new Error(`${id} must declare floor support seated from an authored contact plane or profile-authored bounds`);
    }
    const localContactY = finiteNumber(candidate.support.localContactY, Number.NaN);
    const supportEvidence = objectValue(candidate.support.evidence, `${id} support evidence`);
    const evidencePercentile = finiteNumber(supportEvidence.percentile, Number.NaN);
    if (seat === 'authored-contact-plane') {
      if (!Number.isFinite(localContactY) || localContactY <= localMin.y || localContactY >= localMax.y) {
        throw new Error(`${id} support.localContactY must be finite and strictly inside authored Y bounds`);
      }
      if (supportEvidence.method !== 'position-buffer-y-percentile'
        || !Number.isFinite(evidencePercentile)
        || evidencePercentile <= 0
        || evidencePercentile > 0.25) {
        throw new Error(`${id} support evidence must declare a bounded position-buffer Y percentile`);
      }
    } else {
      if (!Number.isFinite(localContactY) || Math.abs(localContactY - localMin.y) > 1e-6) {
        throw new Error(`${id} profile-authored support contact must sit exactly on the bounds floor`);
      }
      if (supportEvidence.method !== 'profile-authored-bounds') {
        throw new Error(`${id} profile-authored support must declare profile-authored-bounds evidence`);
      }
    }
    // A mesh-backed candidate carries a content-addressed runtime; a standee candidate carries the
    // catalog item's image instead (runtime null) and is drawn as an image standee.
    const standee = candidate.standee && typeof candidate.standee === 'object' ? candidate.standee : null;
    let runtime = null;
    if (candidate.runtime !== null && candidate.runtime !== undefined) {
      const rawRuntime = objectValue(candidate.runtime, `${id} runtime`);
      if (!/^https:\/\//.test(String(rawRuntime.url ?? '')) || !/^[a-f0-9]{64}$/.test(String(rawRuntime.sha256 ?? ''))) {
        throw new Error(`${id} requires an HTTPS runtime URL and SHA-256 identity`);
      }
      runtime = {
        kind: String(rawRuntime.kind ?? ''),
        url: String(rawRuntime.url),
        sha256: String(rawRuntime.sha256),
        bytes: Math.max(1, Math.round(finiteNumber(rawRuntime.bytes, 0))),
      };
    } else if (!standee || standee.kind !== 'image-standee') {
      throw new Error(`${id} needs a runtime asset or an image standee`);
    }
    const itemId = String(candidate.itemId ?? '').trim().toUpperCase() || null;
    if (!runtime && !itemId) throw new Error(`${id} standee candidates need a catalog item id`);
    return {
      id,
      label: String(candidate.label ?? id),
      outputLane: String(candidate.outputLane ?? ''),
      collisionClass: ['prop', 'inventory', 'none'].includes(String(candidate.collisionClass ?? ''))
        ? String(candidate.collisionClass)
        : String(candidate.outputLane ?? '') === 'placeable' ? 'prop'
          : String(candidate.outputLane ?? '') === 'inventory' ? 'inventory' : 'none',
      assetRole: String(candidate.assetRole ?? ''),
      tags: [...new Set((Array.isArray(candidate.tags) ? candidate.tags : []).map(String))].sort(),
      // Zones match the compiler's dressingZone() literals; catalogs may carry canonical spellings.
      zones: [...new Set((Array.isArray(candidate.zones) ? candidate.zones : []).map(compilerZoneToken))].sort(),
      navigationPolicy: String(candidate.navigationPolicy ?? ''),
      weight: clamp(finiteNumber(candidate.weight, 1), 0.01, 100),
      // DAFT3D S5 identity + eligibility fields (vocabulary §2).
      archetype: String(candidate.archetype ?? '').trim(),
      roomFunctions: [...new Set((Array.isArray(candidate.roomFunctions) ? candidate.roomFunctions : []).map(String))].sort(),
      environmentTypeIds: [...new Set((Array.isArray(candidate.environmentTypeIds) ? candidate.environmentTypeIds : []).map(String))].sort(),
      orientation: candidate.orientation && typeof candidate.orientation === 'object'
        ? { grounding: String(candidate.orientation.grounding ?? 'floor'), yawMode: String(candidate.orientation.yawMode ?? 'free') }
        : { grounding: 'floor', yawMode: 'free' },
      itemId,
      meshId: String(candidate.meshId ?? '').trim().toUpperCase() || null,
      runtime,
      standee: standee ? {
        kind: 'image-standee',
        imageUrl: standee.imageUrl ? String(standee.imageUrl) : null,
        initials: String(standee.initials ?? '?').slice(0, 3) || '?',
        source: String(standee.source ?? 'catalog-item-standee'),
      } : null,
      authoredBoundsM: { min: localMin, max: localMax },
      support: {
        kind: 'floor',
        seat,
        localContactY,
        groundOffsetM: finiteNumber(candidate.support.groundOffsetM, 0),
        evidence: seat === 'authored-contact-plane'
          ? { method: 'position-buffer-y-percentile', percentile: evidencePercentile }
          : { method: 'profile-authored-bounds', percentile: 0 },
      },
    };
  }).sort((a, b) => a.id.localeCompare(b.id));
  return {
    schema: catalog.schema,
    revision,
    source: String(catalog.source ?? 'injected'),
    generationProfilesEnabled: false,
    candidates,
  };
}

function propsCatalogToken(value, label) {
  const token = String(value ?? '').trim().toLowerCase();
  if (!/^[a-z0-9][a-z0-9_-]{0,63}$/.test(token)) {
    throw new Error(`${label} must be a stable lowercase token`);
  }
  return token;
}

function normalizePropsCatalogCandidate(value, categoryMap) {
  const candidate = objectValue(value, 'Props catalog candidate');
  const id = String(candidate.id ?? '').trim();
  const label = String(candidate.label ?? '').trim();
  if (!id || id.length > 200 || !label || label.length > 120) {
    throw new Error('Props catalog candidates require bounded stable IDs and labels');
  }
  const categoryId = propsCatalogToken(candidate.categoryId, `${id} categoryId`);
  const subcategoryId = propsCatalogToken(candidate.subcategoryId, `${id} subcategoryId`);
  const category = categoryMap.get(categoryId);
  if (!category || !category.subcategories.has(subcategoryId)) {
    throw new Error(`${id} must reference a declared category and subcategory`);
  }
  if (candidate.manualAuthoringEligible !== true
    || candidate.runtimeStatus?.approved !== true
    || candidate.runtimeStatus?.deleted !== false
    || candidate.runtimeStatus?.source !== 'read-only-mesh-registry'
    || !String(candidate.runtimeStatus?.meshId ?? '').trim()) {
    throw new Error(`${id} lacks approved read-only manual-authoring eligibility`);
  }
  // DAFT3D S10: a card is either a mesh (content-addressed GLB/glTF runtime) or an image standee
  // (runtime null): the mesh / standee / all filter needs both; a standee pick queues a wishlist line.
  const standee = candidate.standee && typeof candidate.standee === 'object' ? candidate.standee : null;
  let runtime = null;
  let runtimeKind = '';
  let runtimeUrl = '';
  let runtimeSha256 = '';
  let runtimeBytes = 0;
  if (candidate.runtime !== null && candidate.runtime !== undefined) {
    runtime = objectValue(candidate.runtime, `${id} runtime`);
    runtimeKind = String(runtime.kind ?? '');
    runtimeUrl = String(runtime.url ?? '');
    runtimeSha256 = String(runtime.sha256 ?? '').toLowerCase();
    runtimeBytes = finiteNumber(runtime.bytes, Number.NaN);
    if (!['glb', 'gltf'].includes(runtimeKind)
      || !/^https:\/\/cdn\.fatetide\.com\//.test(runtimeUrl)
      || !/^[a-f0-9]{64}$/.test(runtimeSha256)
      || !Number.isInteger(runtimeBytes) || runtimeBytes <= 0) {
      throw new Error(`${id} requires an approved content-addressed GLB/glTF runtime`);
    }
  } else if (!standee || standee.kind !== 'image-standee'
    || (standee.imageUrl !== null && standee.imageUrl !== undefined && !/^https:\/\/cdn\.fatetide\.com\//.test(String(standee.imageUrl)))
    || (standee.initials !== undefined && (typeof standee.initials !== 'string' || standee.initials.length > 4))) {
    throw new Error(`${id} needs a runtime asset or a well-formed CDN image standee`);
  }
  const approvedAt = typeof candidate.approvedAt === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(candidate.approvedAt) ? candidate.approvedAt : null;
  const archetype = typeof candidate.archetype === 'string' ? candidate.archetype.trim() : '';
  const localMin = boundsVector(candidate.authoredBoundsM?.min, `${id} authoredBoundsM.min`);
  const localMax = boundsVector(candidate.authoredBoundsM?.max, `${id} authoredBoundsM.max`);
  if (!(localMax.x > localMin.x && localMax.y > localMin.y && localMax.z > localMin.z)) {
    throw new Error(`${id} authored bounds must have positive extents`);
  }
  // Two support seats (vocabulary §3): the pilot's measured contact plane, or the profile-authored
  // bounds of a live registry mesh (bottom-centre origin, contact on the bounds floor).
  const propsSeat = String(candidate.support?.seat ?? '');
  if (candidate.support?.kind !== 'floor' || !['authored-contact-plane', 'profile-authored-bounds'].includes(propsSeat)) {
    throw new Error(`${id} must declare reviewed floor/contact support`);
  }
  const localContactY = finiteNumber(candidate.support.localContactY, Number.NaN);
  const groundOffsetM = finiteNumber(candidate.support.groundOffsetM, Number.NaN);
  const evidence = objectValue(candidate.support.evidence, `${id} support evidence`);
  const percentile = finiteNumber(evidence.percentile, Number.NaN);
  if (propsSeat === 'authored-contact-plane') {
    if (!Number.isFinite(localContactY) || localContactY <= localMin.y || localContactY >= localMax.y
      || !Number.isFinite(groundOffsetM)
      || evidence.method !== 'position-buffer-y-percentile'
      || !Number.isFinite(percentile) || percentile <= 0 || percentile > 0.25) {
      throw new Error(`${id} requires bounded authored contact/support evidence`);
    }
  } else if (!Number.isFinite(localContactY) || Math.abs(localContactY - localMin.y) > 1e-6
    || !Number.isFinite(groundOffsetM) || evidence.method !== 'profile-authored-bounds') {
    throw new Error(`${id} requires profile-authored support exactly on the bounds floor`);
  }
  const preview = objectValue(candidate.preview, `${id} preview`);
  let normalizedPreview;
  if (preview.kind === 'placeholder' && preview.reason === 'no-verified-preview') {
    normalizedPreview = { kind: 'placeholder', reason: 'no-verified-preview' };
  } else if (preview.kind === 'standee-image' || preview.kind === 'thumbnail-image') {
    // A catalog picture: the item's own image (standee) or the mesh's baked thumbnail — presentation
    // only, never geometry; https on the CDN, no content hash (pictures are not content-addressed).
    const previewUrl = String(preview.url ?? '');
    if (!/^https:\/\/cdn\.fatetide\.com\//.test(previewUrl)) {
      throw new Error(`${id} ${preview.kind} preview must come from the CDN`);
    }
    normalizedPreview = { kind: preview.kind, url: previewUrl };
  } else if (preview.kind === 'verified-image') {
    const previewUrl = String(preview.url ?? '');
    const previewSha256 = String(preview.sha256 ?? '').toLowerCase();
    const previewBytes = finiteNumber(preview.bytes, Number.NaN);
    if (!/^https:\/\/cdn\.fatetide\.com\//.test(previewUrl) || !/^[a-f0-9]{64}$/.test(previewSha256)
      || !Number.isInteger(previewBytes) || previewBytes <= 0) {
      throw new Error(`${id} verified preview identity is incomplete`);
    }
    normalizedPreview = {
      kind: 'verified-image',
      url: previewUrl,
      sha256: previewSha256,
      bytes: previewBytes,
    };
  } else {
    throw new Error(`${id} must use a verified preview or an honest placeholder`);
  }
  const collisionClass = String(candidate.collisionClass ?? '');
  if (!['prop', 'inventory'].includes(collisionClass)) {
    throw new Error(`${id} must declare an authored object collision class`);
  }
  const itemId = String(candidate.itemId ?? '').trim().toUpperCase();
  if (collisionClass === 'inventory'
    && !/^[A-F0-9]{8}-[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{12}$/.test(itemId)) {
    throw new Error(`${id} inventory candidate requires a catalog item UUID`);
  }
  const forestPart = String(candidate.forestPart ?? '').trim();
  if (forestPart && !['tree', 'bush', 'hedge', 'herb'].includes(forestPart)) {
    throw new Error(`${id} forestPart is not an interactive forest inventory class`);
  }
  const searchTerms = [...new Set((Array.isArray(candidate.searchTerms) ? candidate.searchTerms : [])
    .map(term => String(term).trim().toLowerCase())
    .filter(term => term.length > 0 && term.length <= 80))].sort();
  return {
    id,
    label,
    categoryId,
    subcategoryId,
    searchTerms,
    manualAuthoringEligible: true,
    runtimeStatus: {
      approved: true,
      deleted: false,
      source: 'read-only-mesh-registry',
      meshId: String(candidate.runtimeStatus.meshId).trim(),
    },
    itemId: itemId || null,
    itemType: String(candidate.itemType ?? '').trim().toLowerCase() || null,
    forestPart: forestPart || null,
    collisionClass,
    assetRole: String(candidate.assetRole ?? '').trim(),
    navigationPolicy: String(candidate.navigationPolicy ?? '').trim(),
    runtime: runtime ? {
      kind: runtimeKind,
      url: runtimeUrl,
      sha256: runtimeSha256,
      bytes: runtimeBytes,
    } : null,
    standee: standee ? cloneJson(standee) : null,
    approvedAt,
    archetype,
    authoredBoundsM: { min: localMin, max: localMax },
    support: {
      kind: 'floor',
      seat: propsSeat,
      localContactY,
      groundOffsetM,
      evidence: propsSeat === 'authored-contact-plane'
        ? { method: 'position-buffer-y-percentile', percentile }
        : { method: 'profile-authored-bounds', percentile: 0 },
    },
    preview: normalizedPreview,
  };
}

export function normalizePropsCatalog(input) {
  const catalog = objectValue(input, 'Props catalog');
  if (catalog.schema !== 'ft.xz3d.props-authoring-catalog.v1'
    || catalog.queryOnly !== true || catalog.writes !== 0
    || catalog.generationProfilesEnabled !== false) {
    throw new Error('Props catalog must remain a versioned query-only zero-write envelope');
  }
  const revision = String(catalog.revision ?? '').trim();
  // DAFT3D S5: the live registry ships the whole tagged kit (hundreds of cards).
  if (!revision || !Array.isArray(catalog.categories) || !Array.isArray(catalog.candidates)
    || catalog.candidates.length < 1 || catalog.candidates.length > 4096) {
    throw new Error('Props catalog requires a revision and 1-4096 bounded candidates');
  }
  const categoryMap = new Map();
  const categories = catalog.categories.map((value) => {
    const category = objectValue(value, 'Props catalog category');
    const id = propsCatalogToken(category.id, 'Props category id');
    if (categoryMap.has(id) || !Array.isArray(category.subcategories) || !category.subcategories.length) {
      throw new Error(`Props category ${id} is duplicated or has no subcategories`);
    }
    const subcategoryIds = new Set();
    const subcategories = category.subcategories.map((subcategoryValue) => {
      const subcategory = objectValue(subcategoryValue, `${id} subcategory`);
      const subcategoryId = propsCatalogToken(subcategory.id, `${id} subcategory id`);
      if (subcategoryIds.has(subcategoryId)) throw new Error(`${id} subcategory ${subcategoryId} is duplicated`);
      subcategoryIds.add(subcategoryId);
      return { id: subcategoryId, label: String(subcategory.label ?? '').trim() || subcategoryId };
    }).sort((left, right) => left.id.localeCompare(right.id));
    const normalized = {
      id,
      label: String(category.label ?? '').trim() || id,
      subcategories,
    };
    categoryMap.set(id, { ...normalized, subcategories: subcategoryIds });
    return normalized;
  }).sort((left, right) => left.id.localeCompare(right.id));
  const seen = new Set();
  const candidates = catalog.candidates.map(value => normalizePropsCatalogCandidate(value, categoryMap))
    .sort((left, right) => left.id.localeCompare(right.id));
  for (const candidate of candidates) {
    if (seen.has(candidate.id)) throw new Error(`Props catalog candidate is duplicated: ${candidate.id}`);
    seen.add(candidate.id);
  }
  const computedCounts = {
    candidates: candidates.length,
    categories: categories.length,
    subcategories: categories.reduce((sum, category) => sum + category.subcategories.length, 0),
  };
  if (!catalog.counts || Object.entries(computedCounts).some(([key, value]) => catalog.counts[key] !== value)) {
    throw new Error('Props catalog counts must exactly describe its bounded eligible contents');
  }
  return {
    schema: 'ft.xz3d.props-authoring-catalog.v1',
    revision,
    source: String(catalog.source ?? 'read-only-injected'),
    queryOnly: true,
    writes: 0,
    generationProfilesEnabled: false,
    counts: computedCounts,
    categories,
    candidates,
  };
}

export function authoringLocalPlacementId({
  catalogRevision,
  candidateId,
  roomId = null,
  supportScope = 'room-floor',
  supportSurfaceId = '',
  ordinal,
} = {}) {
  const sequence = Math.max(0, Math.floor(finiteNumber(ordinal, 0)));
  const ownerIdentity = supportScope === 'scene-ground'
    ? `scene-ground:${String(supportSurfaceId ?? '')}`
    : `room-floor:${String(roomId ?? '')}`;
  const identity = `${String(catalogRevision ?? '')}|${String(candidateId ?? '')}|${ownerIdentity}|${sequence}`;
  return `authoring-local-${hashString(identity).toString(16).padStart(8, '0')}`;
}

export function evaluateAuthoringLocalPlacement({
  compiledCreation,
  zone,
  candidate,
  positionFt,
  surfaceContact = null,
  ordinal = 0,
  catalogRevision,
  objectCollisionsEnabled = true,
  bypassObjectCollisions = false,
  existingPlacements,
} = {}) {
  const base = {
    placement: null,
    blockerId: null,
    queryOnly: true,
    effects: { persistence: 'none', databaseWrites: 0, inventoryWrites: 0, encounterWrites: 0 },
  };
  const reject = (reason, blockerId = null) => ({ ...base, accepted: false, reason, blockerId });
  if (!compiledCreation || compiledCreation.schema !== CREATION_SCHEMA || !zone
    || zone.schema !== ZONE_GENERATION_SCHEMA || zone.queryOnly !== true
    || !candidate || candidate.manualAuthoringEligible !== true
    || !(
      (candidate.runtimeStatus?.approved === true && candidate.runtimeStatus?.deleted === false)
      || (candidate.standeeStatus?.approved === true && candidate.standee?.kind === 'image-standee')
    )) {
    return reject('candidate-ineligible');
  }
  const room = compiledCreation.draft.rooms.find(value => value.id === zone.roomId);
  if (!room) return reject('room-not-found');
  const x = finiteNumber(positionFt?.x, Number.NaN);
  const y = finiteNumber(positionFt?.y, Number.NaN);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return reject('outside-room');
  const rotationDegrees = finiteNumber(surfaceContact?.rotationDegrees, 0);
  const angle = rotationDegrees * Math.PI / 180;
  const authoredWidthFt = (candidate.authoredBoundsM.max.x - candidate.authoredBoundsM.min.x) * METERS_TO_FEET;
  const authoredDepthFt = (candidate.authoredBoundsM.max.z - candidate.authoredBoundsM.min.z) * METERS_TO_FEET;
  const widthFt = Math.abs(Math.cos(angle)) * authoredWidthFt + Math.abs(Math.sin(angle)) * authoredDepthFt;
  const depthFt = Math.abs(Math.sin(angle)) * authoredWidthFt + Math.abs(Math.cos(angle)) * authoredDepthFt;
  const footprint = {
    minX: x - widthFt / 2,
    maxX: x + widthFt / 2,
    minY: y - depthFt / 2,
    maxY: y + depthFt / 2,
    widthFt,
    depthFt,
    insideRoom: false,
    clearOfReservedSpace: false,
  };
  if (!roomContainsRectangle(room, footprint)) return reject('outside-room');
  const cellByKey = new Map(compiledCreation.solution.cells.map(cell => [cell.key, cell]));
  const reservedRectangles = zone.reservations.reservedCellKeys
    .map(key => cellByKey.get(key))
    .filter(Boolean)
    .map(cell => rectangleForCell(cell, compiledCreation.solution.cellFt));
  if (reservedRectangles.some(rectangle => rectanglesOverlap(footprint, rectangle))) {
    return reject('reserved-space');
  }
  const support = compiledCreation.roomGroup.floors.filter((floor) => {
    if (!same(floor.floorFt, room.floorFt) || floor.z > room.ceiling + EPS) return false;
    const supportRoom = compiledCreation.draft.rooms.find(value => value.id === floor.roomId);
    if (!supportRoom || !roomContainsRectangle(supportRoom, footprint)) return false;
    const cutouts = compiledCreation.roomGroup.verticalConnections.flatMap(connection => (
      connection.floorCutouts.filter(cutout => cutout.roomId === floor.roomId)
    ));
    return !cutouts.some((cutout) => {
      if (!cutout.contour.length) return false;
      const xs = cutout.contour.map(point => point.x);
      const ys = cutout.contour.map(point => point.y);
      return rectanglesOverlap(footprint, {
        minX: Math.min(...xs), maxX: Math.max(...xs),
        minY: Math.min(...ys), maxY: Math.max(...ys),
      });
    });
  }).sort((left, right) => right.z - left.z || left.id.localeCompare(right.id))[0] ?? null;
  if (!support) return reject('unsupported-surface');
  const contactZ = finiteNumber(surfaceContact?.z, support.z);
  if (contactZ < support.z - EPS) return reject('unsupported-surface');
  const rootOffsetM = {
    x: -(candidate.authoredBoundsM.min.x + candidate.authoredBoundsM.max.x) / 2,
    y: -candidate.support.localContactY + candidate.support.groundOffsetM,
    z: -(candidate.authoredBoundsM.min.z + candidate.authoredBoundsM.max.z) / 2,
  };
  const placement = {
    id: authoringLocalPlacementId({
      catalogRevision,
      candidateId: candidate.id,
      roomId: room.id,
      ordinal,
    }),
    roomId: room.id,
    supportScope: 'room-floor',
    category: candidate.categoryId,
    outputLane: 'placeable',
    collisionClass: candidate.collisionClass,
    authority: 'authoring-local',
    candidateId: candidate.id,
    candidateLabel: candidate.label,
    assetRole: candidate.assetRole,
    navigationPolicy: candidate.navigationPolicy,
    ...(candidate.runtime ? { runtime: cloneJson(candidate.runtime) } : {}),
    ...(candidate.standee ? { standee: cloneJson(candidate.standee) } : {}),
    itemId: candidate.itemId ?? null,
    itemType: candidate.itemType ?? null,
    meshId: candidate.runtimeStatus?.meshId ?? null,
    forestPart: candidate.forestPart ?? null,
    transform: {
      positionFt: { x, y, z: contactZ },
      rotationQuarterTurns: rotationDegrees / 90,
      rotationDegrees,
      scale: 1,
      rootOffsetM,
    },
    authoredBoundsM: cloneJson(candidate.authoredBoundsM),
    groundingMode: 'automatic-support',
    groundingReceipt: {
      mode: 'automatic-support',
      supportKind: 'floor',
      seat: 'authored-contact-plane',
      supportSurfaceId: support.id,
      supportSurfaceKind: support.kind,
      supportPlaneFt: contactZ,
      localContactY: candidate.support.localContactY,
      groundOffsetM: candidate.support.groundOffsetM,
      embedDepthM: candidate.support.localContactY - candidate.authoredBoundsM.min.y,
      groundedLocalContactFt: contactZ + candidate.support.groundOffsetM * METERS_TO_FEET,
      groundedLocalMinFt: contactZ + (
        candidate.authoredBoundsM.min.y - candidate.support.localContactY + candidate.support.groundOffsetM
      ) * METERS_TO_FEET,
      manualElevationFt: 0,
      resolution: 'highest-legal-authored-surface-beneath-footprint',
      evidence: cloneJson(candidate.support.evidence),
      source: 'explicit-reviewed-contact-plane',
    },
    footprintReceipt: {
      ...footprint,
      insideRoom: true,
      clearOfReservedSpace: true,
    },
    surfaceVariantId: null,
    catalogRevision: String(catalogRevision ?? ''),
  };
  const verticalSpan = zonePlacementVerticalSpan(placement);
  if (objectCollisionsEnabled && (!verticalSpan || verticalSpan.maxZ > room.ceiling + EPS)) {
    return reject('ceiling-clearance');
  }
  const occupied = Array.isArray(existingPlacements)
    ? existingPlacements
    : [...zone.resolvedPlacements, ...zone.preservedPlacements];
  const blocker = occupied.filter(value => value?.id !== placement.id && value?.footprintReceipt)
    .sort((left, right) => left.id.localeCompare(right.id))
    .find(value => zonePlacementCollisionReceipt(placement, value).collides) ?? null;
  if (objectCollisionsEnabled && !bypassObjectCollisions && blocker) return reject('object-collision', blocker.id);
  return { ...base, accepted: true, reason: 'accepted', placement, blockerId: blocker?.id ?? null };
}

function sceneGroundSupportIsValid(support) {
  return support?.schema === 'ft.xz3d.scene-ground-authoring-support.v1'
    && support.authority === 'scene-ground'
    && support.queryOnly === true
    && support.writes === 0
    && support.persistence === 'none'
    && Number.isFinite(support.floorFt)
    && typeof support.supportSurfaceId === 'string'
    && support.supportSurfaceId.length > 0
    && Number.isFinite(support.supportPlaneFt)
    && support.canvas?.x?.max > support.canvas?.x?.min
    && support.canvas?.y?.max > support.canvas?.y?.min
    && support.evidence?.source === 'explicit-scene-ground-presentation'
    && support.evidence?.renderedMeshRequiredForAuthority === false;
}

export function createSceneGroundAuthoringSupportReceipt({
  compiledCreation,
  authoringFloorFt = 0,
  surfaceId = null,
  surfaceSource = 'deterministic-fallback',
  fallbackKey = null,
  environmentName = '',
  variant = '',
} = {}) {
  if (!compiledCreation || compiledCreation.schema !== CREATION_SCHEMA) {
    throw new Error('Scene-ground authoring support requires a compiled creation');
  }
  const supportPlaneFt = finiteNumber(authoringFloorFt, compiledCreation.roomGroup?.terrainCutaway?.z);
  if (!Number.isFinite(supportPlaneFt)) throw new Error('Scene-ground support plane must be explicit and finite');
  const surfaceIdentity = String(surfaceId ?? '').trim()
    ? `surface:${String(surfaceId).trim()}`
    : `fallback:${String(fallbackKey ?? '').trim()}`;
  if (surfaceIdentity === 'fallback:') throw new Error('Scene-ground support requires a surface or fallback identity');
  return {
    schema: 'ft.xz3d.scene-ground-authoring-support.v1',
    authority: 'scene-ground',
    queryOnly: true,
    writes: 0,
    persistence: 'none',
    floorFt: supportPlaneFt,
    supportSurfaceId: `scene-ground:${numberKey(supportPlaneFt)}:${surfaceIdentity}`,
    supportPlaneFt,
    canvas: cloneJson(compiledCreation.draft.canvas),
    surfaceId: String(surfaceId ?? '').trim() || null,
    surfaceSource,
    fallbackKey: String(fallbackKey ?? '').trim() || null,
    environmentName: String(environmentName ?? '').trim().toLowerCase(),
    variant: String(variant ?? '').trim().toLowerCase(),
    evidence: {
      source: 'explicit-scene-ground-presentation',
      presentationSchema: 'ft.xz3d.scene-ground-presentation.v1',
      renderedMeshRequiredForAuthority: false,
    },
  };
}

function sceneGroundFootprintReason(compiledCreation, support, footprint) {
  if (!sceneGroundSupportIsValid(support)
    || JSON.stringify(support.canvas) !== JSON.stringify(compiledCreation?.draft?.canvas)) {
    return 'unsupported-surface';
  }
  const canvas = support.canvas;
  if (footprint.minX < canvas.x.min - EPS || footprint.maxX > canvas.x.max + EPS
    || footprint.minY < canvas.y.min - EPS || footprint.maxY > canvas.y.max + EPS) {
    return 'outside-canvas';
  }
  const footprintRoom = {
    x: { min: footprint.minX, max: footprint.maxX },
    y: { min: footprint.minY, max: footprint.maxY },
    footprint: { kind: 'rect', cornerRadiusFt: 0 },
  };
  const crossesStructure = compiledCreation.draft.rooms.some(room => (
    same(room.floorFt, support.floorFt) && roomFootprintsOverlap(room, footprintRoom)
  ));
  return crossesStructure ? 'authored-structure' : null;
}

export function resolveAuthoringLocalSupportScope({ compiledCreation, support, footprint } = {}) {
  const base = {
    supportScope: null,
    roomId: null,
    queryOnly: true,
    effects: { persistence: 'none', databaseWrites: 0, inventoryWrites: 0, encounterWrites: 0 },
  };
  if (!compiledCreation || compiledCreation.schema !== CREATION_SCHEMA || !sceneGroundSupportIsValid(support)) {
    return { ...base, accepted: false, reason: 'unsupported-surface' };
  }
  const containingRooms = compiledCreation.draft.rooms.filter(room => (
    same(room.floorFt, support.floorFt) && roomContainsRectangle(room, footprint)
  )).sort((left, right) => {
    const leftArea = (left.x.max - left.x.min) * (left.y.max - left.y.min);
    const rightArea = (right.x.max - right.x.min) * (right.y.max - right.y.min);
    return leftArea - rightArea || left.id.localeCompare(right.id);
  });
  if (containingRooms.length) {
    return {
      ...base,
      accepted: true,
      reason: 'room-floor',
      supportScope: 'room-floor',
      roomId: containingRooms[0].id,
    };
  }
  const footprintRoom = {
    x: { min: footprint.minX, max: footprint.maxX },
    y: { min: footprint.minY, max: footprint.maxY },
    footprint: { kind: 'rect', cornerRadiusFt: 0 },
  };
  if (compiledCreation.draft.rooms.some(room => (
    same(room.floorFt, support.floorFt) && roomFootprintsOverlap(room, footprintRoom)
  ))) {
    return { ...base, accepted: false, reason: 'boundary-straddle' };
  }
  const canvas = support.canvas;
  if (footprint.minX < canvas.x.min - EPS || footprint.maxX > canvas.x.max + EPS
    || footprint.minY < canvas.y.min - EPS || footprint.maxY > canvas.y.max + EPS) {
    return { ...base, accepted: false, reason: 'outside-canvas' };
  }
  return {
    ...base,
    accepted: true,
    reason: 'scene-ground',
    supportScope: 'scene-ground',
    roomId: null,
  };
}

export function evaluateSceneGroundAuthoringLocalPlacement({
  compiledCreation,
  support,
  candidate,
  positionFt,
  surfaceContact = null,
  ordinal = 0,
  catalogRevision,
  objectCollisionsEnabled = true,
  bypassObjectCollisions = false,
  existingPlacements = [],
} = {}) {
  const base = {
    placement: null,
    blockerId: null,
    queryOnly: true,
    effects: { persistence: 'none', databaseWrites: 0, inventoryWrites: 0, encounterWrites: 0 },
  };
  const reject = (reason, blockerId = null) => ({ ...base, accepted: false, reason, blockerId });
  if (!compiledCreation || compiledCreation.schema !== CREATION_SCHEMA
    || !sceneGroundSupportIsValid(support)
    || !candidate || candidate.manualAuthoringEligible !== true
    || !(
      (candidate.runtimeStatus?.approved === true && candidate.runtimeStatus?.deleted === false)
      || (candidate.standeeStatus?.approved === true && candidate.standee?.kind === 'image-standee')
    )) {
    return reject('candidate-ineligible');
  }
  const x = finiteNumber(positionFt?.x, Number.NaN);
  const y = finiteNumber(positionFt?.y, Number.NaN);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return reject('outside-canvas');
  const rotationDegrees = finiteNumber(surfaceContact?.rotationDegrees, 0);
  const angle = rotationDegrees * Math.PI / 180;
  const authoredWidthFt = (candidate.authoredBoundsM.max.x - candidate.authoredBoundsM.min.x) * METERS_TO_FEET;
  const authoredDepthFt = (candidate.authoredBoundsM.max.z - candidate.authoredBoundsM.min.z) * METERS_TO_FEET;
  const widthFt = Math.abs(Math.cos(angle)) * authoredWidthFt + Math.abs(Math.sin(angle)) * authoredDepthFt;
  const depthFt = Math.abs(Math.sin(angle)) * authoredWidthFt + Math.abs(Math.cos(angle)) * authoredDepthFt;
  const footprint = {
    minX: x - widthFt / 2,
    maxX: x + widthFt / 2,
    minY: y - depthFt / 2,
    maxY: y + depthFt / 2,
    widthFt,
    depthFt,
    insideRoom: false,
    clearOfReservedSpace: true,
    insideCanvas: false,
    clearOfAuthoredStructures: false,
  };
  const footprintReason = sceneGroundFootprintReason(compiledCreation, support, footprint);
  if (footprintReason) return reject(footprintReason);
  const contactZ = finiteNumber(surfaceContact?.z, support.supportPlaneFt);
  if (contactZ < support.supportPlaneFt - EPS) return reject('unsupported-surface');
  const rootOffsetM = {
    x: -(candidate.authoredBoundsM.min.x + candidate.authoredBoundsM.max.x) / 2,
    y: -candidate.support.localContactY + candidate.support.groundOffsetM,
    z: -(candidate.authoredBoundsM.min.z + candidate.authoredBoundsM.max.z) / 2,
  };
  const placement = {
    id: authoringLocalPlacementId({
      catalogRevision,
      candidateId: candidate.id,
      supportScope: 'scene-ground',
      supportSurfaceId: support.supportSurfaceId,
      ordinal,
    }),
    roomId: null,
    supportScope: 'scene-ground',
    category: candidate.categoryId,
    outputLane: 'placeable',
    collisionClass: candidate.collisionClass,
    authority: 'authoring-local',
    candidateId: candidate.id,
    candidateLabel: candidate.label,
    assetRole: candidate.assetRole,
    navigationPolicy: candidate.navigationPolicy,
    ...(candidate.runtime ? { runtime: cloneJson(candidate.runtime) } : {}),
    ...(candidate.standee ? { standee: cloneJson(candidate.standee) } : {}),
    itemId: candidate.itemId ?? null,
    itemType: candidate.itemType ?? null,
    meshId: candidate.runtimeStatus?.meshId ?? null,
    forestPart: candidate.forestPart ?? null,
    transform: {
      positionFt: { x, y, z: contactZ },
      rotationQuarterTurns: rotationDegrees / 90,
      rotationDegrees,
      scale: 1,
      rootOffsetM,
    },
    authoredBoundsM: cloneJson(candidate.authoredBoundsM),
    groundingMode: 'automatic-support',
    groundingReceipt: {
      mode: 'automatic-support',
      supportKind: 'floor',
      seat: 'authored-contact-plane',
      supportSurfaceId: support.supportSurfaceId,
      supportSurfaceKind: 'scene-ground',
      supportAuthorityId: support.supportSurfaceId,
      supportPlaneFt: contactZ,
      localContactY: candidate.support.localContactY,
      groundOffsetM: candidate.support.groundOffsetM,
      embedDepthM: candidate.support.localContactY - candidate.authoredBoundsM.min.y,
      groundedLocalContactFt: contactZ + candidate.support.groundOffsetM * METERS_TO_FEET,
      groundedLocalMinFt: contactZ + (
        candidate.authoredBoundsM.min.y - candidate.support.localContactY + candidate.support.groundOffsetM
      ) * METERS_TO_FEET,
      manualElevationFt: 0,
      resolution: 'highest-legal-authored-surface-beneath-footprint',
      evidence: cloneJson(candidate.support.evidence),
      source: 'explicit-reviewed-contact-plane',
    },
    footprintReceipt: {
      ...footprint,
      insideCanvas: true,
      clearOfAuthoredStructures: true,
    },
    surfaceVariantId: null,
    catalogRevision: String(catalogRevision ?? ''),
  };
  const blocker = (Array.isArray(existingPlacements) ? existingPlacements : [])
    .filter(value => value?.id !== placement.id && value?.footprintReceipt)
    .sort((left, right) => left.id.localeCompare(right.id))
    .find(value => zonePlacementCollisionReceipt(placement, value).collides) ?? null;
  if (objectCollisionsEnabled && !bypassObjectCollisions && blocker) {
    return reject('object-collision', blocker.id);
  }
  return { ...base, accepted: true, reason: 'accepted', placement, blockerId: blocker?.id ?? null };
}

export function createSceneAuthoringLocalCollection({ support, placements = [] } = {}) {
  if (!sceneGroundSupportIsValid(support)) throw new Error('Scene-local collection requires explicit scenery-ground support');
  if (!Array.isArray(placements) || placements.some(placement => (
    placement?.authority !== 'authoring-local'
    || placement.supportScope !== 'scene-ground'
    || placement.roomId !== null
    || placement.groundingReceipt?.supportAuthorityId !== support.supportSurfaceId
  ))) {
    throw new Error('Scene-local collection contains a placement outside its scenery-ground authority');
  }
  return {
    schema: 'ft.xz3d.scene-authoring-local-collection.v1',
    queryOnly: true,
    writes: 0,
    persistence: 'none',
    support: cloneJson(support),
    placements: cloneJson(placements),
    effects: { databaseWrites: 0, inventoryWrites: 0, encounterWrites: 0 },
  };
}

export function refreshSceneAuthoringLocalCollection({ compiledCreation, collection, support } = {}) {
  const base = {
    queryOnly: true,
    effects: { persistence: 'none', databaseWrites: 0, inventoryWrites: 0, encounterWrites: 0 },
  };
  const prior = createSceneAuthoringLocalCollection(collection ?? {});
  if (!sceneGroundSupportIsValid(support)
    || support.supportSurfaceId !== prior.support.supportSurfaceId
    || Math.abs(support.supportPlaneFt - prior.support.supportPlaneFt) > EPS
    || JSON.stringify(support.canvas) !== JSON.stringify(prior.support.canvas)) {
    return { ...base, accepted: false, reason: 'support-changed', collection: prior };
  }
  const invalid = prior.placements.some(placement => (
    !placement.footprintReceipt
    || sceneGroundFootprintReason(compiledCreation, support, placement.footprintReceipt) !== null
  ));
  if (invalid) return { ...base, accepted: false, reason: 'placement-invalid', collection: prior };
  return {
    ...base,
    accepted: true,
    reason: 'preserved',
    collection: createSceneAuthoringLocalCollection({ support, placements: prior.placements }),
  };
}

function normalizeTracedWallAssetIdentity(input = DEFAULT_TRACED_WALL_ASSET_IDENTITY) {
  const source = String(input?.source ?? '');
  const assetId = String(input?.assetId ?? '').trim();
  if (!['native-structural-primitive', 'approved-native-mesh-registry'].includes(source) || !assetId) {
    throw new Error('Traced wall asset identity must use an explicit native authority');
  }
  return {
    source,
    assetId,
    meshId: String(input?.meshId ?? '').trim() || null,
    surfaceId: String(input?.surfaceId ?? '').trim() || null,
    registryRequired: source === 'approved-native-mesh-registry',
  };
}

function canonicalTracedWallGeometry(startFt, endFt, gridFt) {
  const raw = [startFt, endFt].map(point => ({
    x: finiteNumber(point?.x, Number.NaN),
    y: finiteNumber(point?.y, Number.NaN),
  }));
  if (raw.some(point => !Number.isFinite(point.x) || !Number.isFinite(point.y))) return null;
  const snapped = raw.map(point => ({ x: snap(point.x, gridFt), y: snap(point.y, gridFt) }));
  const dx = Math.abs(snapped[1].x - snapped[0].x);
  const dy = Math.abs(snapped[1].y - snapped[0].y);
  if (dx < EPS && dy < EPS) return null;
  if (dx >= dy) {
    const at = snap((snapped[0].y + snapped[1].y) / 2, gridFt);
    const span = { min: Math.min(snapped[0].x, snapped[1].x), max: Math.max(snapped[0].x, snapped[1].x) };
    if (!(span.max > span.min + EPS)) return null;
    return {
      axis: 'y', at, span,
      startFt: { x: span.min, y: at },
      endFt: { x: span.max, y: at },
    };
  }
  const at = snap((snapped[0].x + snapped[1].x) / 2, gridFt);
  const span = { min: Math.min(snapped[0].y, snapped[1].y), max: Math.max(snapped[0].y, snapped[1].y) };
  if (!(span.max > span.min + EPS)) return null;
  return {
    axis: 'x', at, span,
    startFt: { x: at, y: span.min },
    endFt: { x: at, y: span.max },
  };
}

function tracedWallFootprint(segment) {
  const half = segment.thicknessFt / 2;
  return segment.axis === 'x'
    ? { minX: segment.at - half, maxX: segment.at + half, minY: segment.span.min, maxY: segment.span.max }
    : { minX: segment.span.min, maxX: segment.span.max, minY: segment.at - half, maxY: segment.at + half };
}

function tracedWallGeometryKey(floorFt, geometry) {
  return `${numberKey(floorFt)}:${geometry.axis}:${numberKey(geometry.at)}:${numberKey(geometry.span.min)}:${numberKey(geometry.span.max)}`;
}

function tracedWallEffects() {
  return { persistence: 'none', databaseWrites: 0, inventoryWrites: 0, encounterWrites: 0 };
}

function normalizeTracedWallOpenings(openings, segment, gridFt) {
  const normalized = (Array.isArray(openings) ? openings : []).map((opening, index) => {
    const kind = opening?.kind === 'door-intent' ? 'door-intent' : 'opening';
    const min = snap(finiteNumber(opening?.span?.min, Number.NaN), gridFt);
    const max = snap(finiteNumber(opening?.span?.max, Number.NaN), gridFt);
    const zMin = finiteNumber(opening?.z?.min, segment.z.min);
    const zMax = finiteNumber(opening?.z?.max, segment.z.max);
    if (!Number.isFinite(min) || !Number.isFinite(max) || !(max > min + EPS)
      || min < segment.span.min - EPS || max > segment.span.max + EPS
      || !(zMax > zMin + EPS) || zMin < segment.z.min - EPS || zMax > segment.z.max + EPS) {
      throw new Error(`Traced wall opening ${index + 1} must be contained by its segment`);
    }
    return {
      id: '',
      kind,
      span: { min, max },
      z: { min: zMin, max: zMax },
      doorItemId: kind === 'door-intent' ? String(opening?.doorItemId ?? '').trim() || null : null,
      queryOnly: true,
    };
  }).sort((left, right) => left.span.min - right.span.min || left.span.max - right.span.max || left.kind.localeCompare(right.kind));
  for (let index = 1; index < normalized.length; index += 1) {
    if (normalized[index].span.min < normalized[index - 1].span.max - EPS) {
      throw new Error('Traced wall openings cannot overlap');
    }
  }
  for (const opening of normalized) {
    const key = `${segment.id}:${opening.kind}:${numberKey(opening.span.min)}:${numberKey(opening.span.max)}:${numberKey(opening.z.min)}:${numberKey(opening.z.max)}`;
    opening.id = `traced-opening-${hashString(key).toString(36)}`;
  }
  return normalized;
}

function tracedWallCollectionIsValid(collection) {
  return collection?.schema === SCENE_TRACED_WALL_SCHEMA
    && collection.authority === 'scene-level-independent-wall-traces'
    && collection.queryOnly === true
    && collection.writes === 0
    && collection.persistence === 'none'
    && sceneGroundSupportIsValid(collection.support)
    && Array.isArray(collection.segments);
}

export function createSceneTracedWallCollection({
  support,
  gridFt = DEFAULT_GRID_FT,
  defaultHeightFt = DEFAULT_CLEAR_HEIGHT_FT,
  defaultThicknessFt = 0.5,
  assetIdentity = DEFAULT_TRACED_WALL_ASSET_IDENTITY,
  segments = [],
} = {}) {
  if (!sceneGroundSupportIsValid(support)) throw new Error('Traced walls require explicit scene-ground support');
  const normalizedGridFt = finiteNumber(gridFt, DEFAULT_GRID_FT);
  const heightFt = finiteNumber(defaultHeightFt, DEFAULT_CLEAR_HEIGHT_FT);
  const thicknessFt = finiteNumber(defaultThicknessFt, 0.5);
  if (!(normalizedGridFt > 0) || !(heightFt > 0) || !(thicknessFt > 0)) {
    throw new Error('Traced wall grid, height, and thickness must be positive');
  }
  const collection = {
    schema: SCENE_TRACED_WALL_SCHEMA,
    authority: 'scene-level-independent-wall-traces',
    queryOnly: true,
    writes: 0,
    persistence: 'none',
    support: cloneJson(support),
    gridFt: normalizedGridFt,
    defaultHeightFt: heightFt,
    defaultThicknessFt: thicknessFt,
    assetIdentity: normalizeTracedWallAssetIdentity(assetIdentity),
    segments: cloneJson(segments),
    effects: tracedWallEffects(),
  };
  if (collection.segments.some(segment => (
    segment?.authority !== 'scene-traced-wall'
    || !same(segment.floorFt, support.floorFt)
    || segment.supportSurfaceId !== support.supportSurfaceId
  ))) throw new Error('Traced wall collection contains a segment outside its scene authority');
  return collection;
}

function lineRunsOverlap(left, right) {
  return same(left.floorFt, right.floorFt) && left.axis === right.axis && same(left.at, right.at)
    && Math.min(left.span.max, right.span.max) > Math.max(left.span.min, right.span.min) + EPS;
}

function tracedWallCanvasReason(collection, segment) {
  const footprint = tracedWallFootprint(segment);
  const canvas = collection.support.canvas;
  return footprint.minX < canvas.x.min - EPS || footprint.maxX > canvas.x.max + EPS
    || footprint.minY < canvas.y.min - EPS || footprint.maxY > canvas.y.max + EPS
    ? 'outside-canvas'
    : null;
}

function roomWallOverlap(compiledCreation, segment) {
  return compiledCreation.roomGroup.wallRuns.find(run => (
    run.geometry === 'line' && run.kind === 'wall' && lineRunsOverlap(run, segment)
  )) ?? null;
}

export function evaluateSceneTracedWallTrace({
  compiledCreation,
  collection,
  startFt,
  endFt,
  floorFt,
  heightFt,
  thicknessFt,
  openings = [],
  assetIdentity,
} = {}) {
  const base = { segment: null, collection, blockerId: null, queryOnly: true, effects: tracedWallEffects() };
  const reject = (reason, blockerId = null) => ({ ...base, accepted: false, reason, blockerId });
  if (!compiledCreation || compiledCreation.schema !== CREATION_SCHEMA || !tracedWallCollectionIsValid(collection)) {
    return reject('invalid-authority');
  }
  const resolvedFloorFt = finiteNumber(floorFt, collection.support.floorFt);
  if (!same(resolvedFloorFt, collection.support.floorFt)) return reject('floor-mismatch');
  const geometry = canonicalTracedWallGeometry(startFt, endFt, collection.gridFt);
  if (!geometry) return reject('zero-length');
  const resolvedHeightFt = finiteNumber(heightFt, collection.defaultHeightFt);
  const resolvedThicknessFt = finiteNumber(thicknessFt, collection.defaultThicknessFt);
  if (!(resolvedHeightFt > 0) || !(resolvedThicknessFt > 0)) return reject('invalid-dimensions');
  const canonicalKey = tracedWallGeometryKey(resolvedFloorFt, geometry);
  const segment = {
    id: `traced-wall-${hashString(canonicalKey).toString(36)}`,
    canonicalKey,
    authority: 'scene-traced-wall',
    source: 'independent-trace',
    floorFt: resolvedFloorFt,
    supportSurfaceId: collection.support.supportSurfaceId,
    supportPlaneFt: collection.support.supportPlaneFt,
    axis: geometry.axis,
    at: geometry.at,
    span: geometry.span,
    startFt: geometry.startFt,
    endFt: geometry.endFt,
    heightFt: resolvedHeightFt,
    thicknessFt: resolvedThicknessFt,
    z: { min: collection.support.supportPlaneFt, max: collection.support.supportPlaneFt + resolvedHeightFt },
    assetIdentity: normalizeTracedWallAssetIdentity(assetIdentity ?? collection.assetIdentity),
    openings: [],
    footprint: null,
    queryOnly: true,
    persistence: 'none',
    writes: 0,
  };
  try {
    segment.openings = normalizeTracedWallOpenings(openings, segment, collection.gridFt);
  } catch {
    return reject('invalid-opening');
  }
  segment.footprint = tracedWallFootprint(segment);
  if (tracedWallCanvasReason(collection, segment)) return reject('outside-canvas');
  const coincident = collection.segments.find(existing => lineRunsOverlap(existing, segment));
  if (coincident) {
    return reject(existingGeometryEquals(coincident, segment) ? 'duplicate' : 'overlap', coincident.id);
  }
  const roomBlocker = roomWallOverlap(compiledCreation, segment);
  if (roomBlocker) return reject('room-wall-overlap', roomBlocker.id);
  const next = createSceneTracedWallCollection({
    support: collection.support,
    gridFt: collection.gridFt,
    defaultHeightFt: collection.defaultHeightFt,
    defaultThicknessFt: collection.defaultThicknessFt,
    assetIdentity: collection.assetIdentity,
    segments: [...collection.segments, segment].sort((left, right) => left.canonicalKey.localeCompare(right.canonicalKey)),
  });
  return { ...base, accepted: true, reason: 'accepted', segment: cloneJson(segment), collection: next };
}

function existingGeometryEquals(left, right) {
  return same(left.floorFt, right.floorFt) && left.axis === right.axis && same(left.at, right.at)
    && same(left.span.min, right.span.min) && same(left.span.max, right.span.max);
}

function lineIntersection(left, right) {
  if (!same(left.floorFt, right.floorFt)) return null;
  if (left.axis === right.axis) {
    if (!same(left.at, right.at)) return null;
    for (const value of [left.span.min, left.span.max]) {
      if (value >= right.span.min - EPS && value <= right.span.max + EPS) {
        return left.axis === 'x' ? { x: left.at, y: value } : { x: value, y: left.at };
      }
    }
    return null;
  }
  const x = left.axis === 'x' ? left.at : right.at;
  const y = left.axis === 'y' ? left.at : right.at;
  const leftAlong = left.axis === 'x' ? y : x;
  const rightAlong = right.axis === 'x' ? y : x;
  return leftAlong >= left.span.min - EPS && leftAlong <= left.span.max + EPS
    && rightAlong >= right.span.min - EPS && rightAlong <= right.span.max + EPS
    ? { x, y }
    : null;
}

function pointAlongSegment(segment, point) {
  return segment.axis === 'x' ? point.y : point.x;
}

export function compileSceneTracedWalls({ compiledCreation, collection } = {}) {
  if (!compiledCreation || compiledCreation.schema !== CREATION_SCHEMA || !tracedWallCollectionIsValid(collection)) {
    throw new Error('Traced wall compilation requires compiled scene and explicit wall authority');
  }
  const joinMap = new Map();
  const addJoin = (point, kind, segmentId, roomWallRunId = null) => {
    const key = `${numberKey(collection.support.floorFt)}:${numberKey(point.x)}:${numberKey(point.y)}`;
    if (!joinMap.has(key)) joinMap.set(key, {
      id: `traced-wall-join-${hashString(key).toString(36)}`,
      canonicalKey: key,
      floorFt: collection.support.floorFt,
      pointFt: { x: point.x, y: point.y, z: collection.support.supportPlaneFt },
      kinds: [], segmentIds: [], roomWallRunIds: [], queryOnly: true,
    });
    const join = joinMap.get(key);
    if (!join.kinds.includes(kind)) join.kinds.push(kind);
    if (segmentId && !join.segmentIds.includes(segmentId)) join.segmentIds.push(segmentId);
    if (roomWallRunId && !join.roomWallRunIds.includes(roomWallRunId)) join.roomWallRunIds.push(roomWallRunId);
  };
  for (const segment of collection.segments) {
    addJoin(segment.startFt, 'endpoint', segment.id);
    addJoin(segment.endFt, 'endpoint', segment.id);
  }
  for (let leftIndex = 0; leftIndex < collection.segments.length; leftIndex += 1) {
    for (let rightIndex = leftIndex + 1; rightIndex < collection.segments.length; rightIndex += 1) {
      const point = lineIntersection(collection.segments[leftIndex], collection.segments[rightIndex]);
      if (!point) continue;
      addJoin(point, 'traced-intersection', collection.segments[leftIndex].id);
      addJoin(point, 'traced-intersection', collection.segments[rightIndex].id);
    }
  }
  for (const segment of collection.segments) {
    for (const roomRun of compiledCreation.roomGroup.wallRuns.filter(run => run.geometry === 'line' && run.kind === 'wall')) {
      const point = lineIntersection(segment, roomRun);
      if (point) addJoin(point, 'room-wall-contact', segment.id, roomRun.id);
    }
  }
  const joins = Array.from(joinMap.values()).map(join => ({
    ...join,
    kinds: join.kinds.sort(),
    segmentIds: join.segmentIds.sort(),
    roomWallRunIds: join.roomWallRunIds.sort(),
  })).sort((left, right) => left.canonicalKey.localeCompare(right.canonicalKey));
  const runs = [];
  for (const segment of collection.segments) {
    const breakpoints = new Set([segment.span.min, segment.span.max]);
    for (const join of joins.filter(value => value.segmentIds.includes(segment.id))) {
      breakpoints.add(pointAlongSegment(segment, join.pointFt));
    }
    for (const opening of segment.openings) {
      breakpoints.add(opening.span.min);
      breakpoints.add(opening.span.max);
    }
    const ordered = Array.from(breakpoints).sort((a, b) => a - b);
    for (let index = 0; index < ordered.length - 1; index += 1) {
      const span = { min: ordered[index], max: ordered[index + 1] };
      if (!(span.max > span.min + EPS)) continue;
      const midpoint = (span.min + span.max) / 2;
      const opening = segment.openings.find(value => midpoint > value.span.min - EPS && midpoint < value.span.max + EPS) ?? null;
      const kind = opening?.kind ?? 'wall';
      const key = `${segment.canonicalKey}:${kind}:${numberKey(span.min)}:${numberKey(span.max)}:${opening?.id ?? ''}`;
      runs.push({
        id: `traced-${kind}-${hashString(key).toString(36)}`,
        canonicalKey: key,
        sourceAuthority: 'scene-traced-wall',
        sourceSegmentId: segment.id,
        floorFt: segment.floorFt,
        geometry: 'line',
        axis: segment.axis,
        at: segment.at,
        span,
        z: opening?.z ?? segment.z,
        kind,
        roomIds: [],
        openingId: opening?.id ?? null,
        connectionIntent: opening ? { kind, doorItemId: opening.doorItemId, queryOnly: true } : null,
        heightFt: segment.heightFt,
        thicknessFt: segment.thicknessFt,
        assetIdentity: cloneJson(segment.assetIdentity),
        queryOnly: true,
      });
    }
  }
  runs.sort((left, right) => left.canonicalKey.localeCompare(right.canonicalKey));
  return {
    schema: 'ft.xz3d.compiled-scene-traced-walls.v1',
    authority: 'scene-level-independent-wall-traces',
    sourceCollectionSchema: SCENE_TRACED_WALL_SCHEMA,
    queryOnly: true,
    writes: 0,
    persistence: 'none',
    floorFt: collection.support.floorFt,
    segments: cloneJson(collection.segments),
    runs,
    joins,
    openings: collection.segments.flatMap(segment => cloneJson(segment.openings)),
    protectedFootprints: collection.segments.map(segment => ({
      id: `traced-wall-footprint:${segment.id}`,
      sourceSegmentId: segment.id,
      floorFt: segment.floorFt,
      ...tracedWallFootprint(segment),
      queryOnly: true,
    })),
    effects: tracedWallEffects(),
  };
}

export function refreshSceneTracedWallCollection({ compiledCreation, collection, support } = {}) {
  const base = { collection, compiled: null, queryOnly: true, effects: tracedWallEffects() };
  if (!tracedWallCollectionIsValid(collection) || !sceneGroundSupportIsValid(support)
    || support.supportSurfaceId !== collection.support.supportSurfaceId
    || !same(support.floorFt, collection.support.floorFt)
    || Math.abs(support.supportPlaneFt - collection.support.supportPlaneFt) > EPS
    || JSON.stringify(support.canvas) !== JSON.stringify(collection.support.canvas)) {
    return { ...base, accepted: false, reason: 'support-changed' };
  }
  if (collection.segments.some(segment => tracedWallCanvasReason(collection, segment)
    || roomWallOverlap(compiledCreation, segment))) {
    return { ...base, accepted: false, reason: 'segment-invalid' };
  }
  const preserved = createSceneTracedWallCollection({
    support,
    gridFt: collection.gridFt,
    defaultHeightFt: collection.defaultHeightFt,
    defaultThicknessFt: collection.defaultThicknessFt,
    assetIdentity: collection.assetIdentity,
    segments: collection.segments,
  });
  return {
    ...base,
    accepted: true,
    reason: 'preserved',
    collection: preserved,
    compiled: compileSceneTracedWalls({ compiledCreation, collection: preserved }),
  };
}

export function footprintOverlapsSceneTracedWalls(footprint, compiledWalls) {
  if (!footprint || compiledWalls?.schema !== 'ft.xz3d.compiled-scene-traced-walls.v1') return false;
  return compiledWalls.protectedFootprints.some(wall => rectanglesOverlap(footprint, wall, 0));
}

function normalizeZoneRecipe(input) {
  const recipe = objectValue(input, 'zone generation recipe');
  if (recipe.queryOnly !== true) throw new Error('zone generation recipe must remain query-only');
  const id = String(recipe.id ?? '').trim();
  const version = String(recipe.version ?? '').trim();
  if (!id || !version) throw new Error('zone generation recipe id and version are required');
  const roomFunction = String(recipe.roomFunction ?? '');
  if (!ROOM_FUNCTIONS.includes(roomFunction)) throw new Error('zone generation recipe roomFunction is invalid');
  const requestRules = objectValue(recipe.requestRules, 'zone generation recipe requestRules');
  const rules = {};
  for (const [category, rawRule] of Object.entries(requestRules)) {
    const rule = objectValue(rawRule, `zone generation rule ${category}`);
    rules[category] = {
      outputLane: String(rule.outputLane ?? ''),
      candidateTags: [...new Set((Array.isArray(rule.candidateTags) ? rule.candidateTags : []).map(String))].sort(),
    };
  }
  const supplementalRequests = (Array.isArray(recipe.supplementalRequests) ? recipe.supplementalRequests : [])
    .map((value, index) => {
      const request = objectValue(value, `supplemental request ${index + 1}`);
      return {
        id: String(request.id ?? `supplemental-${index + 1}`),
        category: String(request.category ?? ''),
        outputLane: String(request.outputLane ?? ''),
        candidateTags: [...new Set((Array.isArray(request.candidateTags) ? request.candidateTags : []).map(String))].sort(),
        preferredZone: compilerZoneToken(request.preferredZone ?? 'center'),
        // DAFT3D S2: placer intent carried for S6 (facing, anchor relation, required, capability).
        section: request.section ? String(request.section) : null,
        capability: request.capability ? String(request.capability) : null,
        facing: request.facing ? String(request.facing) : null,
        anchorRef: request.anchorRef ? String(request.anchorRef) : null,
        required: request.required === true,
      };
    });
  // DAFT3D S2: optional per-category ceilings shared by supplemental and dressing requests.
  const categoryLimits = {};
  const rawLimits = recipe.categoryLimits && typeof recipe.categoryLimits === 'object' ? recipe.categoryLimits : {};
  for (const [category, value] of Object.entries(rawLimits)) {
    const limit = Number(value);
    if (Number.isInteger(limit) && limit >= 0) categoryLimits[category] = limit;
  }
  return {
    id,
    version,
    label: String(recipe.label ?? id),
    theme: String(recipe.theme ?? ''),
    roomFunction,
    requestRules: rules,
    supplementalRequests,
    categoryLimits,
    surfaceVariants: [...new Set((Array.isArray(recipe.surfaceVariants) ? recipe.surfaceVariants : []).map(String))].sort(),
    queryOnly: true,
  };
}

function zoneCellMap(cells) {
  return new Map(cells.map(cell => [cell.key, cell]));
}

function zoneRoomCells(compiled, room) {
  return compiled.solution.cells.filter(cell => (
    roomForPoint(compiled.draft.rooms, cell.cx, cell.cy, cell.floorFt)?.id === room.id
  ));
}

function closestCell(cells, x, y) {
  return cells.slice().sort((a, b) => (
    Math.hypot(a.cx - x, a.cy - y) - Math.hypot(b.cx - x, b.cy - y)
    || a.key.localeCompare(b.key)
  ))[0] ?? null;
}

function roomReservations(compiled, room) {
  const cells = zoneRoomCells(compiled, room);
  const byKey = zoneCellMap(cells);
  const doorApproach = new Set();
  const doorCells = cells.filter(cell => cell.edges.includes('door'));
  for (const cell of doorCells) {
    doorApproach.add(cell.key);
    cell.neighbors.filter(key => key && byKey.has(key)).forEach(key => doorApproach.add(key));
  }
  const center = closestCell(cells, (room.x.min + room.x.max) / 2, (room.y.min + room.y.max) / 2);
  // The center is the destination of an actual door-to-room route, not a
  // standalone keep-clear zone. A sealed room has no walk lane to reserve.
  const walkLane = new Set(center && doorCells.length ? [center.key] : []);
  for (const start of doorCells) {
    let current = start;
    const visited = new Set();
    while (center && current.key !== center.key && !visited.has(current.key)) {
      visited.add(current.key);
      walkLane.add(current.key);
      const currentDistance = Math.abs(current.ix - center.ix) + Math.abs(current.iy - center.iy);
      const next = current.neighbors
        .filter(key => key && byKey.has(key))
        .map(key => byKey.get(key))
        .filter(Boolean)
        .sort((a, b) => {
          const distanceA = Math.abs(a.ix - center.ix) + Math.abs(a.iy - center.iy);
          const distanceB = Math.abs(b.ix - center.ix) + Math.abs(b.iy - center.iy);
          return distanceA - distanceB || a.key.localeCompare(b.key);
        })[0];
      if (!next || Math.abs(next.ix - center.ix) + Math.abs(next.iy - center.iy) >= currentDistance) break;
      current = next;
    }
  }
  const reservedCellKeys = [...new Set([...doorApproach, ...walkLane])].sort();
  return {
    doorApproachCellKeys: [...doorApproach].sort(),
    walkLaneCellKeys: [...walkLane].sort(),
    reservedCellKeys,
  };
}

function rectangleForCell(cell, cellFt) {
  return {
    minX: cell.cx - cellFt / 2,
    maxX: cell.cx + cellFt / 2,
    minY: cell.cy - cellFt / 2,
    maxY: cell.cy + cellFt / 2,
  };
}

function rectanglesOverlap(a, b, padding = 0) {
  return a.minX < b.maxX + padding - EPS
    && a.maxX > b.minX - padding + EPS
    && a.minY < b.maxY + padding - EPS
    && a.maxY > b.minY - padding + EPS;
}

/** DAFT3D S14: the default thickness of a generated wall run, in feet. */
export const WALL_RUN_THICKNESS_FT = 0.5;

/**
 * DAFT3D S14 — the INVENTORY DESCRIPTOR of one compiled wall run (GUI §0, master principle 2).
 *
 * A run is already what the creation core cuts: one straight stretch between corners or openings
 * (`roomGroup.wallRuns`), with openings and rounded-corner chains handled upstream. This turns that
 * geometry into the row-facing record the server authority writes and the shape projection reads
 * back: a stable `canonicalKey` (floor feet · axis · plane · span) that binds the row to the run, the
 * length that becomes `inventory_size`, and the placement the row carries in `x_pos/y_pos/z_pos`
 * plus `transform_3d`.
 *
 * ⚠ The KEY is the binding, not the id: `wall-<hash>` is derived from the same canonical string, so
 * both survive a recompile of an unchanged room, but the key is what a PHP reader can rebuild from
 * a run without importing this module.
 */
/**
 * DAFT3D S14 — a CURVED run becomes a CHAIN of five-foot cell-edge runs (master principle 2: "a
 * curved wall is a chain of short runs, each snapped to Janus's five-foot cell edges for collision,
 * cover and vitality while the mesh renders the curve").
 *
 * The creation core emits one `arc` run per rounded corner, which is right for the MESH and wrong
 * for the rules: an arc has no plane, no span and no cell to stand in, so it can neither carry
 * vitality nor be breached. This rasterises the arc onto the grid — sample it densely, snap each
 * sample to a grid intersection, and emit the axis-aligned edge between consecutive distinct
 * points, inserting the corner edge for a diagonal step. Deterministic: same arc, same chain.
 */
function arcRunChain(run, gridFt = DEFAULT_GRID_FT) {
  const curve = run?.curve;
  if (!curve || !Number.isFinite(curve.radiusFt) || curve.radiusFt <= 0) return [];
  const snap = value => Math.round(value / gridFt) * gridFt;
  const sweep = Math.abs(curve.endRadians - curve.startRadians);
  const samples = Math.max(8, Math.ceil((sweep * curve.radiusFt) / (gridFt / 4)));
  const points = [];
  for (let index = 0; index <= samples; index++) {
    const angle = curve.startRadians + ((curve.endRadians - curve.startRadians) * index) / samples;
    const point = {
      x: snap(curve.center.x + Math.cos(angle) * curve.radiusFt),
      y: snap(curve.center.y + Math.sin(angle) * curve.radiusFt),
    };
    const last = points[points.length - 1];
    if (!last || last.x !== point.x || last.y !== point.y) points.push(point);
  }
  const segments = [];
  const edge = (from, to) => {
    if (from.x === to.x && from.y !== to.y) {
      segments.push({ axis: 'x', at: from.x, span: { min: Math.min(from.y, to.y), max: Math.max(from.y, to.y) } });
    } else if (from.y === to.y && from.x !== to.x) {
      segments.push({ axis: 'y', at: from.y, span: { min: Math.min(from.x, to.x), max: Math.max(from.x, to.x) } });
    }
  };
  for (let index = 1; index < points.length; index++) {
    const from = points[index - 1];
    const to = points[index];
    if (from.x !== to.x && from.y !== to.y) {
      // A diagonal step is two cell edges through the corner, never a hypotenuse.
      const corner = { x: to.x, y: from.y };
      edge(from, corner);
      edge(corner, to);
    } else {
      edge(from, to);
    }
  }
  return segments.map((segment, index) => ({
    ...run,
    geometry: 'chain',
    curve: null,
    axis: segment.axis,
    at: segment.at,
    span: segment.span,
    chainOf: run.id,
    id: `${run.id}-${index + 1}`,
    canonicalKey: `${numberKey(run.floorFt)}:${segment.axis}:${segment.at}:${segment.span.min}:${segment.span.max}`,
  }));
}

export function wallRunInventoryDescriptor(run, { compositionSurfaceId = null } = {}) {
  if (!run || run.kind !== 'wall') return null;
  // An arc carries no plane or span: it is expanded into a chain by wallRunsForRoom() first.
  if (!run.span || !run.z || run.geometry === 'arc') return null;
  const lengthFt = Math.max(0, run.span.max - run.span.min);
  const heightFt = Math.max(0, run.z.max - run.z.min);
  const midSpan = (run.span.min + run.span.max) / 2;
  // `axis` names the plane the run lies IN: an x-plane run stands at a fixed x and spans y.
  const positionFt = run.axis === 'x'
    ? { x: run.at, y: midSpan, z: run.z.min }
    : { x: midSpan, y: run.at, z: run.z.min };
  return {
    schema: 'ft.daft3d.wall-run.v1',
    runId: run.id,
    canonicalKey: run.canonicalKey,
    floorFt: run.floorFt,
    axis: run.axis,
    at: run.at,
    span: { min: run.span.min, max: run.span.max },
    z: { min: run.z.min, max: run.z.max },
    roomIds: [...run.roomIds],
    shared: run.roomIds.length > 1,
    geometry: run.geometry ?? 'line',
    lengthFt,
    heightFt,
    thicknessFt: WALL_RUN_THICKNESS_FT,
    positionFt,
    // A run standing in the x-plane faces +X; one in the y-plane faces +Y (quarter turns, the
    // same convention the placer uses: 0 faces +Y, 1 faces +X).
    rotationQuarterTurns: run.axis === 'x' ? 1 : 0,
    compositionSurfaceId,
  };
}

/**
 * DAFT3D S14: every wall run a room owns, as inventory descriptors. A SHARED run (two rooms) is
 * owned by the first room that can actually raise it (see `eligibleRoomIds`), so two adjacent rooms
 * generate it exactly once and a neighbour with its walls switched off never strands the boundary.
 */
export function wallRunsForRoom(compiledCreation, roomId, { compositionSurfaceId = null, eligibleRoomIds = null } = {}) {
  const runs = compiledCreation?.roomGroup?.wallRuns ?? [];
  const gridFt = compiledCreation?.solution?.cellFt ?? compiledCreation?.draft?.gridFt ?? DEFAULT_GRID_FT;
  // A SHARED run belongs to ONE of its rooms so it is written once. The owner is the first room id
  // that can actually raise it: `eligibleRoomIds` names the rooms generating walls, so a boundary
  // shared with a room whose walls are off (or that is not enclosed) still gets its row from the
  // other side instead of falling through the gap between them (Codex S14 review).
  const eligible = eligibleRoomIds ? new Set(eligibleRoomIds) : null;
  const ownerOf = run => (eligible
    ? run.roomIds.find(candidate => eligible.has(candidate)) ?? null
    : run.roomIds[0]);
  const owned = runs.filter(run => run.roomIds.includes(roomId) && ownerOf(run) === roomId);
  // An arc becomes its cell-edge chain first; every other run stands for itself.
  const expanded = owned.flatMap(run => (run.geometry === 'arc' ? arcRunChain(run, gridFt) : [run]));
  return expanded
    .map(run => wallRunInventoryDescriptor(run, { compositionSurfaceId }))
    .filter(Boolean);
}

/**
 * DAFT3D S11 — the INVENTORY DESCRIPTOR of one door-intent opening (owner ruling D-S11-1: doors are
 * on by default and they CONNECT rooms, the way Dungeon Alchemist joins them).
 *
 * The compiler already decides a shared boundary should be a door: `connectionIntentForPair()` reads
 * the room preset (`old-stone-dungeon-interior` and `ramshackle-timber-interior` say `door-intent`;
 * `wet-cave-interior` says a natural breach, which stays a hole). What was missing is the DOOR
 * itself — the intent named a doorway and no row was ever born, so a generated pair of rooms had an
 * opening rather than something a player can open, lock or break.
 *
 * ⭐ The inventory door is the object; the opening is its doorway (the rule
 * `ft_door_inventory_for_opening()` states). This is the row that makes that true for a generated
 * room: it binds to the opening by id, and the runtime keeps open/closed in `encounter_door_states`.
 */
export function doorRowDescriptorForOpening(opening, { floorFt = null } = {}) {
  const intent = opening?.creation?.connectionIntent ?? null;
  if (!intent || intent.kind !== 'door-intent') return null;
  const axis = opening.plane?.axis;
  if (axis !== 'x' && axis !== 'y') return null;
  const widthFt = Math.max(0, (opening.span?.max ?? 0) - (opening.span?.min ?? 0));
  const heightFt = Math.max(0, (opening.z?.max ?? 0) - (opening.z?.min ?? 0));
  if (!(widthFt > 0) || !(heightFt > 0)) return null;
  // The WIDTH of the doorway chooses the identity, and the identity chooses the mesh: a 10 ft
  // opening in a timber hall hangs "Door, Double, Wooden", a 5 ft one hangs "Door, Wooden"
  // (owner 2026-09-07). An explicitly overridden door item is respected as given.
  const seedItemId = intent.doorItemId ?? DOOR_INTENT_CATALOG[0].id;
  const chosen = intent.overrideMode === 'door-intent'
    ? doorIntent(seedItemId)
    : doorIntentForWidth(seedItemId, widthFt);
  const itemId = chosen.id;
  const midSpan = ((opening.span.min + opening.span.max) / 2);
  return {
    schema: 'ft.daft3d.door-row.v1',
    openingId: String(opening.id),
    itemId,
    meshId: intent.doorMeshId ?? null,
    // The identity, not the intent label, is what the mesh follows.
    itemLabel: chosen.label,
    leaves: chosen.leaves ?? 1,
    meshAvailable: chosen.meshAvailable === true,
    label: intent.label ?? 'door',
    floorFt: Number.isFinite(floorFt) ? floorFt : (opening.creation?.floorFt ?? null),
    rooms: [...(opening.creation?.rooms ?? [])],
    axis,
    at: opening.plane.at,
    span: { min: opening.span.min, max: opening.span.max },
    z: { min: opening.z.min, max: opening.z.max },
    widthFt,
    heightFt,
    // The door stands IN its doorway: on the plane, centred in the span, on the opening's sill.
    positionFt: axis === 'x'
      ? { x: opening.plane.at, y: midSpan, z: opening.z.min }
      : { x: midSpan, y: opening.plane.at, z: opening.z.min },
    // Same convention as a wall run: a door in the x-plane faces +X.
    rotationQuarterTurns: axis === 'x' ? 1 : 0,
  };
}

/**
 * DAFT3D S11: every door a room should own — one per door-intent opening it participates in. A
 * shared doorway is owned by the first room that can raise it, exactly like a shared wall run, so
 * two adjoining rooms produce ONE connecting door rather than two.
 */
export function doorRowsForRoom(compiledCreation, roomId, { eligibleRoomIds = null } = {}) {
  const openings = compiledCreation?.shape?.openings ?? [];
  const eligible = eligibleRoomIds ? new Set(eligibleRoomIds) : null;
  return openings
    .map(opening => doorRowDescriptorForOpening(opening))
    .filter(Boolean)
    .filter(door => {
      const rooms = door.rooms.length ? door.rooms : [roomId];
      if (!rooms.includes(roomId)) return false;
      const owner = eligible ? rooms.find(candidate => eligible.has(candidate)) ?? null : rooms[0];
      return owner === roomId;
    });
}

/**
 * DAFT3D S11 — the WINDOW row for a window-intent opening, the same way a door gets one.
 *
 * ⭐ The inventory window is the object; the opening is the hole it fills. Its vitality is what the
 * structure ladder reads, so breaking this row is what makes the opening crossable — which is the
 * whole reason a window has to be a real row and not a decoration painted on a wall.
 */
export function windowRowDescriptorForOpening(opening, { floorFt = null } = {}) {
  const intent = opening?.creation?.connectionIntent ?? null;
  if (!intent || intent.kind !== 'window-intent') return null;
  const axis = opening.plane?.axis;
  if (axis !== 'x' && axis !== 'y') return null;
  const widthFt = Math.max(0, (opening.span?.max ?? 0) - (opening.span?.min ?? 0));
  const heightFt = Math.max(0, (opening.z?.max ?? 0) - (opening.z?.min ?? 0));
  if (!(widthFt > 0) || !(heightFt > 0)) return null;
  const chosen = windowIntent(intent.windowItemId ?? '') ?? WINDOW_INTENT_CATALOG[0];
  const midSpan = (opening.span.min + opening.span.max) / 2;
  return {
    schema: 'ft.daft3d.window-row.v1',
    openingId: String(opening.id),
    itemId: chosen.id,
    itemLabel: chosen.label,
    closure: chosen.closure,
    // Authoring truth the runtime ladder reads: can you see through it when it is SHUT?
    transparentWhenShut: chosen.transparentWhenShut === true,
    // Carried onto the row so the commit can lock it once, at birth. Never re-read afterwards.
    startsLocked: intent.startsLocked === true,
    meshAvailable: (chosen.looks ?? 0) > 0,
    label: intent.label ?? 'window',
    floorFt: Number.isFinite(floorFt) ? floorFt : (opening.creation?.floorFt ?? null),
    rooms: [...(opening.creation?.rooms ?? [])],
    axis,
    at: opening.plane.at,
    span: { min: opening.span.min, max: opening.span.max },
    z: { min: opening.z.min, max: opening.z.max },
    widthFt,
    heightFt,
    // The window sits IN its hole — and unlike a door, that hole starts well above the floor.
    positionFt: axis === 'x'
      ? { x: opening.plane.at, y: midSpan, z: opening.z.min }
      : { x: midSpan, y: opening.plane.at, z: opening.z.min },
    rotationQuarterTurns: axis === 'x' ? 1 : 0,
  };
}

/**
 * Every window a room owns. Unlike a doorway — which is shared and must be claimed by exactly one
 * of the two rooms it joins — a window is on the EXTERIOR wall, so it belongs to the one room whose
 * wall it is cut through and there is nothing to arbitrate.
 */
export function windowRowsForRoom(compiledCreation, roomId, { eligibleRoomIds = null } = {}) {
  const openings = compiledCreation?.shape?.openings ?? [];
  const eligible = eligibleRoomIds ? new Set(eligibleRoomIds) : null;
  if (eligible && !eligible.has(roomId)) return [];
  return openings
    .map(opening => windowRowDescriptorForOpening(opening))
    .filter(Boolean)
    .filter(window => (window.rooms.length ? window.rooms : [roomId]).includes(roomId));
}

/**
 * DAFT3D S11 — PLAN one hand-placed opening, or refuse it with a reason.
 *
 * ⭐ This is the whole decision: where the opening sits, whether it is legal, and why not when it
 * is not. It lives HERE rather than in the panel so the thing that runs in production is the thing
 * the contract tests — the first cut put this arithmetic in a private DOM method and the test
 * copied the formula, which proves only that two copies agree (Codex review, 2026-09-08).
 *
 * Refusals, each of which was a real way to write a broken shape:
 *   `too-short`   the wall cannot hold an opening of this width;
 *   `no-headroom` the ceiling is at or below the sill, so z.max <= z.min — an INVALID range that
 *                 the shape validator rejects, taking the GM's whole write down with it;
 *   `overlaps`    something is already cut there. A window does NOT split its wall run, so the same
 *                 wall stays selectable and a second click would silently stack a second window in
 *                 exactly the same hole.
 *
 * @param {object} params
 * @returns {{opening: object}|{refused: string, detail?: string}}
 */
export function planHandPlacedOpening({
  room,
  run,
  kind,
  offsetPct = 50,
  itemId = '',
  existingOpenings = [],
  gridFt = DEFAULT_GRID_FT,
  id = null,
}) {
  if (!room || !run || (kind !== 'door' && kind !== 'window')) {
    return { refused: 'invalid-input' };
  }
  const widthFt = kind === 'door' ? gridFt : WINDOW_WIDTH_FT;
  const usable = (run.span.max - run.span.min) - widthFt;
  if (!(usable >= 0)) {
    return { refused: 'too-short', detail: `${(run.span.max - run.span.min).toFixed(0)} ft wall, ${widthFt} ft opening` };
  }
  const z = kind === 'door'
    ? { min: room.floor, max: Math.min(room.ceiling, room.floor + 8) }
    : { min: room.floor + WINDOW_SILL_FT, max: Math.min(room.ceiling, room.floor + WINDOW_HEAD_FT) };
  // ⛔ A range whose max does not EXCEED its min is not a small opening, it is an invalid shape.
  if (!(z.max > z.min + EPS)) {
    return { refused: 'no-headroom', detail: `ceiling ${room.ceiling} ft over floor ${room.floor} ft` };
  }
  const pct = Math.min(100, Math.max(0, finiteNumber(offsetPct, 50)));
  const centre = run.span.min + widthFt / 2 + usable * (pct / 100);
  const span = { min: centre - widthFt / 2, max: centre + widthFt / 2 };

  // Overlap is checked in BOTH axes: two openings may share a plane and a span as long as one is
  // above the other (a window over a doorway is a real thing), but not when they also share height.
  for (const other of existingOpenings) {
    if (!other?.plane || other.plane.axis !== run.axis || Math.abs(other.plane.at - run.at) > EPS) continue;
    const spanHits = span.min < other.span.max - EPS && span.max > other.span.min + EPS;
    const zHits = z.min < other.z.max - EPS && z.max > other.z.min + EPS;
    if (spanHits && zHits) {
      return { refused: 'overlaps', detail: String(other.id ?? 'an existing opening') };
    }
  }

  const used = new Set(existingOpenings.map(other => String(other?.id ?? '')));
  let openingId = String(id ?? '').trim();
  if (!openingId || used.has(openingId)) {
    // Derived from the placement itself, so the same click is the same id — and suffixed only if
    // that id is somehow taken, rather than trusting a random string to be unique.
    const base = `${kind === 'door' ? 'hd' : 'hw'}-${hashString(
      `${room.id}|${run.axis}|${run.at}|${span.min.toFixed(3)}`,
    ).toString(36)}`.slice(0, 30);
    openingId = base;
    for (let attempt = 1; used.has(openingId) && attempt < 100; attempt++) {
      openingId = `${base}${attempt.toString(36)}`.slice(0, 32);
    }
    if (used.has(openingId)) return { refused: 'id-exhausted' };
  }

  const seesThrough = kind === 'window' ? (windowIntent(itemId)?.transparentWhenShut !== false) : true;
  return {
    opening: {
      id: openingId,
      plane: { axis: run.axis, at: run.at },
      span,
      z,
      passable: kind === 'door',
      transparent: seesThrough,
      ...(kind === 'window' && windowIntent(itemId)?.passesLight !== undefined ? { passesLight: windowIntent(itemId).passesLight } : {}),
      creation: {
        automatic: false,
        floorFt: room.floorFt,
        rooms: [room.id],
        connectionIntent: kind === 'door'
          ? {
            kind: 'door-intent',
            label: 'hand-placed door',
            doorItemId: itemId || DOOR_INTENT_LIVE[0]?.id,
            overrideMode: 'door-intent',
            queryOnly: true,
          }
          : {
            kind: 'window-intent',
            label: 'hand-placed window',
            windowItemId: itemId || WINDOW_INTENT_CATALOG[0]?.id,
            transparentWhenShut: seesThrough,
            queryOnly: true,
          },
      },
    },
  };
}

/** Why a placement was refused, in words a GM can act on. */
export function handPlacementRefusalText(refused, detail = '') {
  switch (refused) {
    case 'too-short': return `That wall is too short for this opening${detail ? ` (${detail})` : ''}.`;
    case 'no-headroom': return `That room has no headroom for a window${detail ? ` (${detail})` : ''}.`;
    case 'overlaps': return `Something is already cut there${detail ? ` (${detail})` : ''}.`;
    default: return 'That opening could not be placed.';
  }
}

export function zonePlacementCollisionClass(placement) {
  const explicit = String(placement?.collisionClass ?? '').trim();
  if (['prop', 'inventory', 'none'].includes(explicit)) return explicit;
  return placement?.outputLane === 'placeable' ? 'prop' : 'none';
}

export function zonePlacementVerticalSpan(placement, { positionFt, scale } = {}) {
  const transform = placement?.transform;
  const bounds = placement?.authoredBoundsM;
  const rootOffsetM = transform?.rootOffsetM;
  const resolvedPosition = positionFt ?? transform?.positionFt;
  const resolvedScale = scale ?? transform?.scale;
  if (!bounds || !rootOffsetM || !resolvedPosition
    || !Number.isFinite(resolvedPosition.z) || !Number.isFinite(resolvedScale)) return null;
  const minZ = resolvedPosition.z + (rootOffsetM.y + bounds.min.y) * resolvedScale * METERS_TO_FEET;
  const maxZ = resolvedPosition.z + (rootOffsetM.y + bounds.max.y) * resolvedScale * METERS_TO_FEET;
  if (!Number.isFinite(minZ) || !Number.isFinite(maxZ)) return null;
  return { minZ: Math.min(minZ, maxZ), maxZ: Math.max(minZ, maxZ) };
}

export function zonePlacementCollisionReceipt(left, right, {
  leftFootprint,
  leftPositionFt,
  leftScale,
  rightFootprint,
  rightPositionFt,
  rightScale,
  footprintPaddingFt = 0,
} = {}) {
  const leftClass = zonePlacementCollisionClass(left);
  const rightClass = zonePlacementCollisionClass(right);
  const resolvedLeftFootprint = leftFootprint ?? left?.footprintReceipt;
  const resolvedRightFootprint = rightFootprint ?? right?.footprintReceipt;
  const xyOverlaps = !!resolvedLeftFootprint && !!resolvedRightFootprint
    && rectanglesOverlap(resolvedLeftFootprint, resolvedRightFootprint, footprintPaddingFt);
  const leftSpan = zonePlacementVerticalSpan(left, { positionFt: leftPositionFt, scale: leftScale });
  const rightSpan = zonePlacementVerticalSpan(right, { positionFt: rightPositionFt, scale: rightScale });
  // Missing reviewed height metadata remains conservative for authored objects.
  const zOverlaps = !leftSpan || !rightSpan
    ? true
    : leftSpan.minZ < rightSpan.maxZ - EPS && leftSpan.maxZ > rightSpan.minZ + EPS;
  const policyAllowsCollision = leftClass !== 'none'
    && rightClass !== 'none';
  const overlaps3d = xyOverlaps && zOverlaps;
  return {
    collides: policyAllowsCollision && overlaps3d,
    policyAllowsCollision,
    xyOverlaps,
    zOverlaps,
    overlaps3d,
    leftClass,
    rightClass,
    leftSpan,
    rightSpan,
  };
}

function candidateFootprintFt(candidate, quarterTurns) {
  const widthM = candidate.authoredBoundsM.max.x - candidate.authoredBoundsM.min.x;
  const depthM = candidate.authoredBoundsM.max.z - candidate.authoredBoundsM.min.z;
  return quarterTurns % 2 === 0
    ? { width: widthM * METERS_TO_FEET, depth: depthM * METERS_TO_FEET }
    : { width: depthM * METERS_TO_FEET, depth: widthM * METERS_TO_FEET };
}

function weightedCandidateOrder(candidates, rng) {
  const remaining = candidates.slice();
  const result = [];
  while (remaining.length) {
    const total = remaining.reduce((sum, candidate) => sum + candidate.weight, 0);
    let target = rng() * total;
    let selected = 0;
    for (let index = 0; index < remaining.length; index++) {
      target -= remaining[index].weight;
      if (target <= 0) { selected = index; break; }
    }
    result.push(remaining.splice(selected, 1)[0]);
  }
  return result;
}

function makeZoneRequests(compiled, room, recipe) {
  // Mandatory (supplemental) requests own each family's budget; the seeded density pass fills
  // what is left under the family's ceiling. Emission order stays dressing-then-supplemental so
  // recipes without limits (the pilot fixture) produce exactly the requests they always did.
  const counts = Object.create(null);
  const limitFor = category => (Object.hasOwn(recipe.categoryLimits ?? {}, category) ? recipe.categoryLimits[category] : Infinity);
  for (const supplemental of recipe.supplementalRequests) {
    counts[supplemental.category] = (counts[supplemental.category] ?? 0) + 1;
  }
  const requests = [];
  for (const placement of compiled.dressingPlacements) {
    if (placement.roomId !== room.id) continue;
    const used = counts[placement.category] ?? 0;
    if (used >= limitFor(placement.category)) continue;
    counts[placement.category] = used + 1;
    const rule = Object.hasOwn(recipe.requestRules, placement.category) ? recipe.requestRules[placement.category] : null;
    requests.push({
      id: `recipe-${placement.id}`,
      category: placement.category,
      outputLane: rule?.outputLane ?? '',
      candidateTags: rule?.candidateTags ?? [],
      preferredZone: placement.zone,
      preferredCellKey: placement.cellKey,
      source: 'creation-dressing',
    });
  }
  for (const supplemental of recipe.supplementalRequests) {
    requests.push({ ...supplemental, preferredCellKey: null, source: 'recipe-supplemental' });
  }
  return requests;
}

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

/**
 * DAFT3D S5 — resolver order (vocabulary §2): room eligibility first (a candidate with no
 * roomFunctions is unrestricted; one with roomFunctions must name the recipe's room type or an
 * alias of it), then the family in precedence: candidates whose ARCHETYPE the request names, else
 * candidates whose ASSET ROLE it names, else the synonym standees. The pilot fixture's candidates
 * carry neither archetype nor roomFunctions and keep matching by tag, exactly as before.
 */
function zoneCandidatesForRequest(catalog, request, recipe) {
  const roomType = canonicalRoomTypeId(recipe.roomFunction);
  const eligible = catalog.candidates.filter(candidate => {
    if (candidate.outputLane !== request.outputLane) return false;
    if (!candidate.roomFunctions || candidate.roomFunctions.length === 0) return true;
    return candidate.roomFunctions.some(token => canonicalRoomTypeId(token) === roomType);
  });
  const wanted = new Set(request.candidateTags);
  // The pilot fixture's candidates carry no archetype anywhere in the CATALOG: they keep the flat
  // tag match (and the exact deterministic layouts it produced). Precedence applies to live-shaped
  // catalogs only, and eligibility filtering can never flip the mode (Codex S5 review).
  const structured = catalog.candidates.some(candidate => candidate.archetype);
  if (!structured) {
    const flat = eligible.filter(candidate => !candidate.standee && candidate.tags.some(tag => wanted.has(tag)));
    return flat.length ? [flat] : [];
  }
  // Tiers, tried in order until one yields a legal placement: an archetype match, then a role
  // match, then the synonym standee — so a family whose meshes cannot fit the room still lands
  // as a standee rather than a hole (owner ruling 2026-09-06).
  const byArchetype = eligible.filter(candidate => !candidate.standee && candidate.archetype && wanted.has(candidate.archetype));
  const seen = new Set(byArchetype.map(candidate => candidate.id));
  return [
    byArchetype,
    eligible.filter(candidate => !candidate.standee && candidate.assetRole && wanted.has(candidate.assetRole) && !seen.has(candidate.id)),
    eligible.filter(candidate => candidate.standee && (wanted.has(candidate.archetype) || candidate.tags.some(tag => wanted.has(tag)))),
  ].filter(tier => tier.length > 0);
}

export function compileZoneGeneration({
  compiledCreation,
  roomId,
  recipe: recipeInput,
  catalog: catalogInput,
  refreshNonce = 0,
  existingPlacements = [],
} = {}) {
  if (!compiledCreation || compiledCreation.schema !== CREATION_SCHEMA) {
    throw new Error('zone generation requires a compiled encounter creation draft');
  }
  const recipe = normalizeZoneRecipe(recipeInput);
  const catalog = normalizeZoneCatalog(catalogInput);
  const room = compiledCreation.draft.rooms.find(candidate => candidate.id === roomId);
  if (!room) throw new Error(`zone generation room not found: ${roomId}`);
  const nonce = Math.max(0, Math.floor(finiteNumber(refreshNonce, 0)));
  const requests = makeZoneRequests(compiledCreation, room, recipe);
  const reservations = roomReservations(compiledCreation, room);
  const roomCells = zoneRoomCells(compiledCreation, room);
  const byKey = zoneCellMap(roomCells);
  const reservedRectangles = reservations.reservedCellKeys
    .map(key => byKey.get(key))
    .filter(Boolean)
    .map(cell => rectangleForCell(cell, compiledCreation.solution.cellFt));
  const preservedPlacements = (Array.isArray(existingPlacements) ? existingPlacements : [])
    .filter(placement => placement?.roomId === room.id
      && (placement?.authority === 'manual'
        || placement?.authority === 'generated-pinned'
        || placement?.authority === 'authoring-local'))
    .map(cloneJson);
  const occupied = preservedPlacements
    .filter(placement => placement.footprintReceipt
      && ['minX', 'maxX', 'minY', 'maxY'].every(key => Number.isFinite(placement.footprintReceipt[key])));
  const resolvedPlacements = [];
  const rejections = [];
  const catalogKey = hashString(JSON.stringify({
    revision: catalog.revision,
    candidates: catalog.candidates.map(candidate => ({
      id: candidate.id,
      sha256: candidate.runtime?.sha256 ?? `standee:${candidate.itemId ?? candidate.id}`,
      collisionClass: candidate.collisionClass,
      authoredBoundsM: candidate.authoredBoundsM,
      support: candidate.support,
    })),
  })).toString(16).padStart(8, '0');
  const generationSeed = `${compiledCreation.draft.seed}|${room.id}|${recipe.id}|${recipe.version}|${catalog.revision}|${catalogKey}|${nonce}`;

  // DAFT3D S13: a request whose every eligible candidate seats on a SURFACE (clutter on tables,
  // jars on shelves) is placed after the floor pieces, so its supports exist when it looks for one.
  const placeRequest = (request, tiers) => {
    const rng = mulberry32(hashString(`${generationSeed}|${request.id}`));
    const candidates = tiers.flatMap(tier => weightedCandidateOrder(tier, rng));
    const preferred = request.preferredCellKey ? byKey.get(request.preferredCellKey) : null;
    const cells = shuffled(roomCells, rng).sort((a, b) => {
      const preferredA = preferred?.key === a.key ? -2 : 0;
      const preferredB = preferred?.key === b.key ? -2 : 0;
      const zoneA = dressingZone(a) === request.preferredZone ? -1 : 0;
      const zoneB = dressingZone(b) === request.preferredZone ? -1 : 0;
      return preferredA - preferredB || zoneA - zoneB;
    });
    // ── DAFT3D S13 composition rules (vocabulary §3; content plan §4.3 / §4.6) ──────────────
    // Yaw: quarter turns; 0 faces encounter +Y, 1 faces +X, 2 faces -Y, 3 faces -X (edge index order).
    const centreX = (room.x.min + room.x.max) / 2;
    const centreY = (room.y.min + room.y.max) / 2;
    const directionToward = (fromX, fromY, toX, toY) => {
      const dx = toX - fromX;
      const dy = toY - fromY;
      if (Math.abs(dx) < EPS && Math.abs(dy) < EPS) return null;
      return Math.abs(dx) >= Math.abs(dy) ? (dx >= 0 ? 1 : 3) : (dy >= 0 ? 0 : 2);
    };
    const wallEdgesOf = cell => cell.edges.map((edge, index) => (edge === 'wall' ? index : -1)).filter(index => index >= 0);
    // A grammar line's category is its whole alternative list ("table_round|table_small"); an
    // anchorRef names ONE alternative, so the match is against the alternatives, not the string
    // (Codex-visual S13: common-room chairs had lost their table and faced nowhere).
    const anchorsTo = (category, ref) => category === ref || String(category ?? '').split('|').includes(ref);
    const anchorTarget = request.anchorRef
      ? occupied.find(other => other.roomId === room.id && anchorsTo(other.category, request.anchorRef) && other.transform?.positionFt) ?? null
      : null;
    const isLight = request.section === 'lights';
    const lightSpacingFt = 10;
    // A ceiling-grounded fixture (chandelier) hangs with its top at the ceiling only when a Medium
    // creature still walks under it; otherwise it keeps the old floor placement (2026-09-10).
    const ceilingHangHeadroomFt = 6;
    const requestedYaw = request.facing === 'room' ? 'face_room'
      : request.facing === 'focus' ? 'face_focus'
        : request.facing === 'along_wall' ? 'along_wall'
          : request.facing === 'free' ? 'free' : null;
    let resolved = null;
    for (const candidate of candidates) {
      const grounding = candidate.orientation?.grounding ?? 'floor';
      const profileYaw = candidate.orientation?.yawMode ?? 'free';
      // Effective yaw mode: wall grounding fixes the yaw and `authored` keeps 0; otherwise the
      // request's facing, else the profile's own mode, else the zone default (centre → free,
      // wall band / corner → face the room).
      const yawMode = grounding === 'wall' ? 'wall'
        : profileYaw === 'authored' ? 'authored'
          : (requestedYaw ?? (profileYaw !== 'free' ? profileYaw : (request.preferredZone === 'center' ? 'free' : 'face_room')));
      const firstRotation = Math.floor(rng() * 4);
      const yawsFor = (cell) => {
        const walls = wallEdgesOf(cell);
        if (yawMode === 'authored') return [0];
        if (yawMode === 'wall') return walls.length === 1 ? [(walls[0] + 2) % 4] : walls.map(wall => (wall + 2) % 4);
        const preferred = [];
        if (yawMode === 'face_focus' && anchorTarget) {
          const towards = directionToward(cell.cx, cell.cy, anchorTarget.transform.positionFt.x, anchorTarget.transform.positionFt.y);
          if (towards !== null) preferred.push(towards);
        }
        if (preferred.length === 0 && yawMode !== 'free') {
          // face_room (also along_wall, and face_focus without an anchor): away from the one
          // adjacent wall; in a corner or in the open, toward the room centroid.
          if (walls.length === 1) preferred.push((walls[0] + 2) % 4);
          else {
            const towards = directionToward(cell.cx, cell.cy, centreX, centreY);
            if (towards !== null) preferred.push(towards);
          }
        }
        if (yawMode === 'free' || preferred.length === 0) return [0, 1, 2, 3].map((_, offset) => (firstRotation + offset) % 4);
        // Against exactly one wall the yaw is fixed (a sideways piece there is the gate's "sideways
        // furniture"); elsewhere the preferred yaw goes first and the rest follow so a corner or
        // centre piece can still seat.
        if (walls.length === 1 && yawMode !== 'face_focus') return preferred;
        return [...preferred, ...[0, 1, 2, 3].filter(turn => !preferred.includes(turn))];
      };
      let candidateCells = cells.filter(cell => candidate.zones.includes(dressingZone(cell)));
      if (yawMode === 'wall') candidateCells = cells.filter(cell => wallEdgesOf(cell).length >= 1);
      // Surface grounding seats on a resolved support (a surface / storage / shelving piece) in
      // this room; without one the piece falls back to the floor and says so.
      // The seat is the support's bounds TOP, so a support whose top is at or above the ceiling is no
      // seat at all: the gothic altar's 3.19 m reredos put a chapel candle at 10.47 ft in a 10 ft room —
      // through the ceiling, and onto Level 2 once one existed (2026-09-10).
      const seatTopFt = other => Number(other.transform?.positionFt?.z ?? room.floor)
        + (other.authoredBoundsM.max.y - Number(other.groundingReceipt?.localContactY ?? 0)
          + Number(other.groundingReceipt?.groundOffsetM ?? 0)) * METERS_TO_FEET;
      const supports = grounding === 'surface'
        ? occupied.filter(other => other.roomId === room.id
          && ['surface', 'storage', 'shelving'].includes(other.assetRole)
          && other.footprintReceipt && other.groundingReceipt?.supportKind !== 'surface'
          && Number.isFinite(other.authoredBoundsM?.max?.y)
          && seatTopFt(other) < room.ceiling - EPS
          // A 'surface' is a TABLETOP: past tabletop height its bounds top is a canopy or back,
          // not a seat (the resized 2.90 m gothic altar would still perch a candle at 9.5 ft).
          && (other.assetRole !== 'surface'
            || other.authoredBoundsM.max.y - Number(other.groundingReceipt?.localContactY ?? 0) <= SURFACE_SEAT_MAX_HEIGHT_M + EPS))
        : [];
      const candidateHeightFt = (candidate.authoredBoundsM.max.y - candidate.support.localContactY
        + candidate.support.groundOffsetM) * METERS_TO_FEET;
      for (const cell of candidateCells) {
        if (resolved) break;
        const walls = wallEdgesOf(cell);
        for (const rotationQuarterTurns of yawsFor(cell)) {
          if (resolved) break;
          const footprint = candidateFootprintFt(candidate, rotationQuarterTurns);
          if (footprint.width > room.x.max - room.x.min + EPS || footprint.depth > room.y.max - room.y.min + EPS) continue;
          // A surface-grounded piece tries EVERY eligible support in seeded order before this trial
          // gives up (Codex S13 review: one sampled support starved a request while a roomier table
          // stood free); a floor piece has the one trial.
          const seats = grounding === 'surface' && supports.length ? shuffled(supports, rng) : [null];
          for (const seat of seats) {
          let x = clamp(cell.cx, room.x.min + footprint.width / 2, room.x.max - footprint.width / 2);
          let y = clamp(cell.cy, room.y.min + footprint.depth / 2, room.y.max - footprint.depth / 2);
          let zFt = room.floor;
          let seatedOn = null;
          let wallSnapped = false;
          let hungFromCeiling = false;
          if (seat) {
            const rect = seat.footprintReceipt;
            if (rect.maxX - rect.minX + EPS < footprint.width || rect.maxY - rect.minY + EPS < footprint.depth) continue;
            x = rect.minX + footprint.width / 2 + rng() * ((rect.maxX - rect.minX) - footprint.width);
            y = rect.minY + footprint.depth / 2 + rng() * ((rect.maxY - rect.minY) - footprint.depth);
            const localContactY = Number(seat.groundingReceipt?.localContactY ?? 0);
            const groundOffsetM = Number(seat.groundingReceipt?.groundOffsetM ?? 0);
            zFt = Number(seat.transform?.positionFt?.z ?? room.floor)
              + (seat.authoredBoundsM.max.y - localContactY + groundOffsetM) * METERS_TO_FEET;
            // The seated piece itself must also clear the ceiling (a tall vase on a tall cabinet).
            if (!(zFt + candidateHeightFt <= room.ceiling + EPS)) continue; // NaN fails closed
            seatedOn = seat;
          } else if (grounding === 'ceiling' && Number.isFinite(room.physicalCeilingFt)
            && room.ceiling - candidateHeightFt >= room.floor + ceilingHangHeadroomFt - EPS) {
            // The profile said "ceiling" all along; the placer used to stand every chandelier on the floor.
            zFt = room.ceiling - candidateHeightFt;
            hungFromCeiling = true;
          } else if ((yawMode === 'face_room' || yawMode === 'along_wall' || yawMode === 'wall') && walls.length >= 1) {
            // Wall snap: the back edge sits against the wall face (a hair of clearance keeps it out
            // of the run); a corner piece snaps to both of its walls.
            const gapFt = 0.1;
            for (const wall of walls.slice(0, 2)) {
              if (wall === 0) y = room.y.max - gapFt - footprint.depth / 2;
              else if (wall === 2) y = room.y.min + gapFt + footprint.depth / 2;
              else if (wall === 1) x = room.x.max - gapFt - footprint.width / 2;
              else x = room.x.min + gapFt + footprint.width / 2;
            }
            wallSnapped = true;
          }
          const rectangle = {
            minX: x - footprint.width / 2,
            maxX: x + footprint.width / 2,
            minY: y - footprint.depth / 2,
            maxY: y + footprint.depth / 2,
          };
          if (!roomContainsRectangle(room, rectangle)) continue;
          // Facing the anchor is a RULE judged where the piece actually lands (after clamping or
          // seating), not a preference from the cell centre: a trial that cannot face it is skipped.
          if (yawMode === 'face_focus' && anchorTarget) {
            const towards = directionToward(x, y, anchorTarget.transform.positionFt.x, anchorTarget.transform.positionFt.y);
            if (towards !== null && towards !== rotationQuarterTurns) continue;
          }
          if (!seatedOn && reservedRectangles.some(reserved => rectanglesOverlap(rectangle, reserved, 0.25))) continue;
          if (isLight && occupied.some(other => other.emitter && other.transform?.positionFt
            && Math.hypot(other.transform.positionFt.x - x, other.transform.positionFt.y - y) < lightSpacingFt)) continue;
          const rootOffsetM = {
            x: -(candidate.authoredBoundsM.min.x + candidate.authoredBoundsM.max.x) / 2,
            y: -candidate.support.localContactY + candidate.support.groundOffsetM,
            z: -(candidate.authoredBoundsM.min.z + candidate.authoredBoundsM.max.z) / 2,
          };
          const proposedPlacement = {
            outputLane: candidate.outputLane,
            collisionClass: candidate.collisionClass,
            transform: {
              positionFt: { x, y, z: zFt },
              rotationQuarterTurns,
              scale: 1,
              rootOffsetM,
            },
            authoredBoundsM: candidate.authoredBoundsM,
            footprintReceipt: rectangle,
          };
          // The collision receipt is 3D-aware: clutter seated on a table does not collide with the
          // table (it sits above its top), while two pieces on the same top do.
          if (occupied.some(other => other !== seatedOn && zonePlacementCollisionReceipt(proposedPlacement, other, {
            footprintPaddingFt: seatedOn ? 0 : 0.5,
          }).collides)) continue;
          const surfaceVariantId = recipe.surfaceVariants.length
            ? recipe.surfaceVariants[Math.floor(rng() * recipe.surfaceVariants.length)]
            : null;
          const transform = {
            positionFt: { x, y, z: zFt },
            rotationQuarterTurns,
            scale: 1,
            rootOffsetM,
          };
          const generatedKey = hashString(JSON.stringify({
            generationSeed,
            requestId: request.id,
            candidateId: candidate.id,
            transform,
            surfaceVariantId,
          })).toString(16).padStart(8, '0');
          const topFt = zFt + (candidate.authoredBoundsM.max.y - candidate.support.localContactY + candidate.support.groundOffsetM) * METERS_TO_FEET;
          resolved = {
            id: `generated-${room.id}-${generatedKey}`,
            generatedKey,
            requestId: request.id,
            roomId: room.id,
            category: request.category,
            outputLane: candidate.outputLane,
            collisionClass: candidate.collisionClass,
            authority: 'generated-free',
            candidateId: candidate.id,
            candidateLabel: candidate.label,
            assetRole: candidate.assetRole,
            archetype: candidate.archetype ?? '',
            itemId: candidate.itemId ?? null,
            meshId: candidate.meshId ?? null,
            navigationPolicy: candidate.navigationPolicy,
            runtime: candidate.runtime ? cloneJson(candidate.runtime) : null,
            standee: candidate.standee ? cloneJson(candidate.standee) : null,
            orientation: candidate.orientation ? cloneJson(candidate.orientation) : null,
            transform,
            authoredBoundsM: cloneJson(candidate.authoredBoundsM),
            // A hung fixture is a floor-referenced MANUAL elevation (the stored-row form), so no
            // re-grounding pass ever drops it back onto the floor.
            groundingMode: hungFromCeiling ? 'manual-z' : 'automatic-support',
            groundingReceipt: {
              mode: hungFromCeiling ? 'manual-z' : 'automatic-support',
              supportKind: seatedOn ? 'surface' : 'floor',
              seat: candidate.support.seat,
              supportSurfaceId: seatedOn ? seatedOn.id : `floor-${room.id}`,
              supportSurfaceKind: seatedOn ? 'placement-top' : (room.environment === 'exterior-open' ? 'open-ground' : 'room-floor'),
              supportPlaneFt: hungFromCeiling ? room.floor : zFt,
              localContactY: candidate.support.localContactY,
              groundOffsetM: candidate.support.groundOffsetM,
              embedDepthM: candidate.support.localContactY - candidate.authoredBoundsM.min.y,
              groundedLocalContactFt: zFt + candidate.support.groundOffsetM * METERS_TO_FEET,
              groundedLocalMinFt: zFt
                + (candidate.authoredBoundsM.min.y - candidate.support.localContactY
                  + candidate.support.groundOffsetM) * METERS_TO_FEET,
              manualElevationFt: hungFromCeiling ? zFt - room.floor : 0,
              resolution: seatedOn ? 'seated-on-resolved-support-top'
                : hungFromCeiling ? 'hung-from-room-ceiling' : 'highest-legal-authored-surface-beneath-footprint',
              evidence: cloneJson(candidate.support.evidence),
              source: candidate.support.seat === 'authored-contact-plane'
                ? 'explicit-reviewed-contact-plane'
                : 'profile-authored-bounds',
            },
            footprintReceipt: {
              ...rectangle,
              widthFt: footprint.width,
              depthFt: footprint.depth,
              insideRoom: roomContainsRectangle(room, rectangle),
              footprintAuthority: room.footprint.kind,
              clearOfReservedSpace: true,
            },
            // DAFT3D S13: how this piece was composed — the quality gate reads it.
            composition: {
              yawMode,
              facingRequested: request.facing ?? null,
              wallEdges: walls,
              wallSnapped,
              seatedOn: seatedOn ? seatedOn.id : null,
              surfaceFallback: grounding === 'surface' && !seatedOn,
              hungFromCeiling,
              anchorRef: request.anchorRef ?? null,
              anchorId: anchorTarget ? anchorTarget.id : null,
              section: request.section ?? null,
            },
            // The light PLAN: an emitter record per light request (the lamp mesh is the visual, the
            // emitter is the light); lights keep at least lightSpacingFt between them.
            emitter: isLight ? { kind: 'point', heightFt: topFt, radiusFt: 20, spacingFt: lightSpacingFt } : null,
            surfaceVariantId,
            recipe: { id: recipe.id, version: recipe.version },
            catalogRevision: catalog.revision,
          };
          occupied.push(resolved);
          break;
          }
        }
      }
      if (resolved) break;
    }
    if (resolved) resolvedPlacements.push(resolved);
    else rejections.push({ requestId: request.id, category: request.category, reason: 'no-legal-placement' });
  };
  const deferredRequests = [];
  for (const request of requests) {
    if (!request.outputLane || request.candidateTags.length < 1) {
      rejections.push({ requestId: request.id, category: request.category, reason: 'recipe-category-unmapped' });
      continue;
    }
    const tiers = zoneCandidatesForRequest(catalog, request, recipe);
    if (!tiers.length) {
      rejections.push({ requestId: request.id, category: request.category, reason: 'catalog-no-candidate' });
      continue;
    }
    if (tiers.every(tier => tier.every(candidate => (candidate.orientation?.grounding ?? 'floor') === 'surface'))) {
      deferredRequests.push([request, tiers]);
      continue;
    }
    placeRequest(request, tiers);
  }
  for (const [request, tiers] of deferredRequests) placeRequest(request, tiers);

  const result = {
    schema: ZONE_GENERATION_SCHEMA,
    status: 'query-only-preview',
    queryOnly: true,
    roomId: room.id,
    roomFunction: room.function,
    refreshNonce: nonce,
    recipe: {
      id: recipe.id,
      version: recipe.version,
      label: recipe.label,
      theme: recipe.theme,
      roomFunction: recipe.roomFunction,
      surfaceVariants: recipe.surfaceVariants,
    },
    catalog: { revision: catalog.revision, source: catalog.source, deterministicKey: catalogKey },
    requests,
    reservations,
    resolvedPlacements,
    preservedPlacements,
    rejections,
    effects: {
      persistence: 'none',
      databaseWrites: 0,
      inventoryWrites: 0,
      generationProfilesEnabled: false,
    },
    summary: {
      requestCount: requests.length,
      resolvedCount: resolvedPlacements.length,
      rejectedCount: rejections.length,
      preservedCount: preservedPlacements.length,
      deterministicKey: '',
    },
  };
  result.summary.deterministicKey = hashString(JSON.stringify({
    generationSeed,
    requests,
    reservations,
    resolvedPlacements,
    preservedPlacements,
    rejections,
  })).toString(16).padStart(8, '0');
  return result;
}
